import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  Image,
  Provider,
  defaultTheme,
  Flex,
  ToastQueue
} from '@adobe/react-spectrum';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import ChevronDownIcon from 'app/components/TimelineCard/ChevronDownIcon';
import ChevronDropdownIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import RiskLevelLowIcon from '../../assets/icons/risk-level-low.svg';
import RiskLevelModerateIcon from '../../assets/icons/risk-level-moderate.svg';
import RiskLevelHighIcon from '../../assets/icons/risk-level-high.svg';
import RiskLevelPlanningIcon from '../../assets/icons/risk-level-planning.svg';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import ChevronDownRiskIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import Checkmark from '@spectrum-icons/workflow/Checkmark';
import CheckIcon from 'app/assets/icons/dropdown-check.svg';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import styles from './SlideOutPanel.module.scss';
import { CommentPanel } from './CommentPanel';
import CommentIcon from 'app/assets/svgIcons/CommentIcon';
import { DeleteButton } from './DeleteButton';
import { InfoButton } from './InfoButton';
import { SideNavTooltip } from './SideNavTooltip';
import GeneralInfoCommentIcon from './CommentPanel/GeneralInfoCommentIcon/GeneralCommentIcon';
import ViewCardContent from 'app/assets/svgIcons/ViewCardContent';
import FeaturesIcon from 'app/assets/svgIcons/Features';
import AskAiIcon from 'app/assets/svgIcons/AskAiIcon';
import { Features, FeatureRow, FeaturesContainerRef } from './Features';
import { AiAssistantSection } from './AiAssistantSection';
import FeaturesSectionReadonlyWrapper from './FeaturesSectionWrapper/FeaturesSectionReadonlyWrapper';
import PrimaryContacts from './PrimaryContacts/PrimaryContacts';
import GeneralInfo from './GeneralInfo/GeneralInfo';
// Reused as-is from Review & Approval - see FeedbackSlideOut.tsx's own doc
// comment (it's fully self-contained given just `navigation`/`onClose`, no
// ReviewApproval slice dependency) - this is the SAME component/state/
// navigation shape that feature's own dashboard tables use, not a second
// Feedback slide-out for the Milestones matrix's own Feedback icon (see
// MilestoneMatrixFromAPI.tsx's `.feedbackIconButton`).
import { FeedbackSlideOut } from 'app/pages/ReviewApproval/components/FeedbackSlideOut';
import type { FeedbackSlideoutNavigation } from 'app/pages/ReviewApproval/types';
import {
  RISK_LEVELS,
  momentTypeSupportsGenAi,
  isEolMoment,
  MOMENT_TYPES,
  PAR_ID_MAX_LENGTH
} from '../../../utils/constants';
import {
  getRtmPhasedTitleParts,
  getPhasedOfferPhaseStripLabel
} from 'utils/rtmPhaseDisplayTitle';
import { getCategoryIcon } from '../../../utils/categoryIconUtils';
import type {
  SlcApprovalSubjectContext,
  SlcSubjectMomentDates
} from '../../../utils/slcApprovalSubject';
import UnsavedChangesWarning from '../UnsavedChangesWarning';
import MessageDialog from '../MessageDialog';

import Field from './Field/Field';
import type { OverviewRef } from './Overview/Overview';
import GeneralInfoIcon from './GeneralInfo/GeneralInfoIcon';
import { Decisions } from './Decisions/Decisions';
import { DACIContacts } from './DACIContacts/DACIContacts';
import DACIContactPopup from '../DACIContactPopup';
import { DACI_ROLES, DRIVER_ROLES } from '../../constants/daciRoles';
import AddPrimaryContact from '../AddPrimaryContact';
import EditPrimaryContact from './PrimaryContacts/EditPrimaryContact';
import AdditionalInfo, {
  AdditionalInfoRef
} from './AdditionalInfo/AdditionalInfo';
import { ChangeLogSection } from './ChangeLog/ChangeLog';
import { SLIDEOUT_STORAGE_KEYS } from './slideoutStorageKeys';
import { EditOnlyWrapper, RBACProvider } from './context/RBACContext';
import { useRBAC as useRBACUtils } from '../../utils/rbacHandler';
// EditDecisionPopup now handled within Decisions component
import {
  MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS,
  MOMENT_TYPE_PRODUCT_RELEASE_WITHOUT_MARKETING,
  usesMomentLevelMilestonesType,
  usesPcoStylePhasedDates
} from './configs/momentTypeConstants';
import { CalendarRange } from '../Fields/Calendar';
import { parseDate, CalendarDate } from '@internationalized/date';
import { shouldShowRoadmapToggle } from './configs/momentTypeDateFieldConfig';
// Removed mock data import - using only API data
import {
  adaptOverviewData,
  mapExistingToNewOverview
} from '../../../utils/overviewMapping';
import {
  SlideoutGeneralInfoSection,
  SlideoutOverviewSection,
  SlideoutLegacyFeaturesTab,
  SlideoutFeaturesContainerSection,
  SlideoutMilestoneMatrix,
  SlideoutInheritedMilestonesPlaceholder,
  SlideoutPhasedGeneralInfo,
  SlideoutHeaderShell,
  SlideoutNotificationBell,
  SlideoutNonPhasedLayoutSection,
  SlideoutCustomAccordionSection,
  SlideoutMainContentSection
} from './containers';
import { useFormApiConfig } from '../../hooks/useFormApiConfig';
import {
  fetchMomentDataCached,
  updateMomentPrimaryContacts
} from '../../services/momentsApi';
import { useUpdateMomentType } from './hooks/useUpdateMomentType';
import { useDeleteMilestones } from './hooks/useDeleteMilestones';
import { useSlideoutFieldDropdownPortals } from './hooks/useSlideoutFieldDropdownPortals';
import {
  useMilestoneSortPreference,
  type MilestoneSortPreferenceChoice
} from './hooks/useMilestoneSortPreference';
// Features fetching handled within FeaturesContainer
import {
  createPrimaryContactsManager,
  PrimaryContactsManager,
  Contact as PCContact
} from './utils/primaryContactsManager';
import { MomentProvider } from './context/MomentContext';
import type {
  MomentData,
  EventData,
  ParentMomentData
} from '../../services/momentsApi';
import {
  useGlobalCacheSlice,
  selectEventsData,
  selectParentMomentsData,
  selectIsEventsLoading,
  selectIsParentMomentsLoading,
  selectEventsError,
  selectParentMomentsError
} from '../../store/globalCacheSlice';
import { useApiDataUpdater } from './utils/apiDataUpdater';
import { resolveRiskLevel } from './utils/resolveRiskLevel';
import {
  updateMoment,
  addRtmPhase,
  updateRtmPhase,
  patchRtmPhase
} from '../RsdForms/api';
import {
  detectStrategicAlignmentError,
  getStrategicAlignmentErrorMessage
} from './utils/strategicAlignmentErrorHandler';
import { fetchDropdownOptions } from '../RsdForms/store/api';
import { useSlideoutUrl } from '../../hooks/useSlideoutUrl';
import { useDispatch } from 'react-redux';
import { commentActions } from './CommentPanel/CommentState';
import { useFieldHighlight } from '../AlertsAndNotification/hooks/useFieldHighlight';
import '../AlertsAndNotification/styles/alert-field-highlight.scss';
import { useAlertsAndNotificationsSlice } from '../AlertsAndNotification/store/slice';
import { mapFieldRefToSelector } from '../AlertsAndNotification/utils/fieldMapper';
import { highlightField } from '../AlertsAndNotification/utils/fieldHighlighter';
import type { PhaseOrderEntry } from '../AlertsAndNotification/utils/alertScrollPriority';
import { useSelector } from 'react-redux';
import { RootState } from 'types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  selectAudiences,
  selectCategories,
  selectEvents,
  selectFeatures,
  selectFilteredReleases,
  selectGenAiFilter,
  selectMomentTypeFilter,
  selectReleases,
  selectHierarchyFilters,
  selectLanesTiers,
  selectMustWinDropdownSelection,
  selectProductFilter,
  selectProductGroups,
  selectRevenueImpactFilter,
  selectSearchFilterActive,
  selectStatus
} from 'app/pages/Home/slice/selectors';
import {
  updatePhasedTierField,
  updatePhasedRiskLevelField,
  updatePhasedRevenueImpactField,
  updatePhasedPrimaryContacts
} from './utils/phasedFieldUpdateUtils';
import { updateDACIContactsWithIds } from './utils/phasedFieldUpdateUtils';
import {
  mergeContactEmailFields,
  resolveContactEmail,
  type ContactEmailSource
} from '../../services/contactEmailUtils';
import MaximizeIcon from 'app/assets/svgIcons/MaximizeIcon';
import MinimizeIcon from 'app/assets/svgIcons/MinimizeIcon';
import { resolveSharedRootMilestoneTierOrDefault } from './Milestones/resolveSharedRootMilestoneTier';
import { extractPermissionData } from '../../utils/rbacHandler';
import { useCommentSlice } from './CommentPanel/CommentState';
import { layoutActions } from 'app/pages/Layout/slice';
import {
  selectCardData,
  selectBadgeCount
} from './CommentPanel/CommentState/selectors';
import {
  selectAlertByIssueId,
  selectAlertCountByIssueId
} from '../AlertsAndNotification/store/slice/selectors';
import {
  selectUserProfile,
  selectCanAccessAiAssistant,
  selectShowAiAssistant
} from 'app/pages/Layout/slice/selectors';
import { categoryIcons } from '../../../utils/categoryIconUtils';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { DelayedTooltip } from './DelayedTooltip';
import { useSlideoutFullscreen } from './hooks/useSlideoutFullscreen';
import { useSlideoutUnsavedChanges } from './hooks/useSlideoutUnsavedChanges';
import { useSlideoutClose } from './hooks/useSlideoutClose';
import { useSlideoutNavigation } from './hooks/useSlideoutNavigation';
import { useSlideoutSeamlessRefresh } from './hooks/useSlideoutSeamlessRefresh';
import { useSlideoutStatusHandlers } from './hooks/useSlideoutStatusHandlers';
import { clearUnsavedFeaturesFromSessionStorage } from './utils/unsavedFeaturesStorage';
import { showOptimisticUpdateErrorToast } from './utils/showOptimisticUpdateErrorToast';
import type {
  SlideOutPanelCard,
  SlideOutPanelProps
} from './types/slideOutPanel.types';

export type { SlideOutPanelCard } from './types/slideOutPanel.types';

// Removed all mock data - using only API data

export function SlideOutPanel({
  card,
  onClose,
  isOpen,
  showNewItemBanner = false,
  onDismissNewItemBanner,
  releasePhaseId: propReleasePhaseId
}: SlideOutPanelProps) {
  const dispatch = useDispatch();
  const { actions } = useCommentSlice();
  const { actions: alertsActions } = useAlertsAndNotificationsSlice();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    overviewHasUnsavedChanges,
    overviewResetTrigger,
    setOverviewResetTrigger,
    hasUnsavedFeatures,
    hasUnsavedChanges
  } = useSlideoutUnsavedChanges();

  // Strategic alignment error dialog (replaces grey toast for better visibility)
  const [strategicAlignmentDialog, setStrategicAlignmentDialog] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: '' });

  // State for tier update error handling - strategic alignment required
  const [pendingTierUpdate, setPendingTierUpdate] = useState<{
    phaseId: string;
    tierValue: string;
    oldTier: string;
    isNonPhased: boolean;
  } | null>(null);

  // State for generic pending updates (overview, products, etc.) - strategic alignment required
  const [pendingUpdate, setPendingUpdate] = useState<{
    type: 'overview' | 'products' | 'generalInfo' | 'other';
    payload: Record<string, any>;
    oldValue?: any;
    revertFn?: () => void;
    intendedFieldValues?: Record<string, any>;
  } | null>(null);

  // Ref to Overview component for error communication
  const overviewErrorRef = useRef<OverviewRef | null>(null);

  const { isFullScreen, setIsFullScreen } = useSlideoutFullscreen();

  const cardData = useSelector(selectCardData);
  const loggedInUser = useSelector(selectUserProfile);
  const canAccessAiAssistant = useSelector(selectCanAccessAiAssistant);
  const showAiAssistant = useSelector(selectShowAiAssistant);
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  const {
    showUnsavedWarning,
    setShowUnsavedWarning,
    pendingCloseAction,
    showSlideAnimation,
    setShowSlideAnimation,
    isClosing,
    prevCardId,
    handleCloseWithRefresh,
    handleConfirmClose,
    handleCancelClose
  } = useSlideoutClose({
    onClose,
    hasUnsavedChanges,
    overviewHasUnsavedChanges,
    setOverviewResetTrigger,
    isCommentPanelOpen,
    setIsCommentPanelOpen,
    setIsFullScreen,
    location,
    dispatch,
    alertsActions,
    setIsCloneModalOpen
  });

  const discardUnsavedDrafts = useCallback(() => {
    clearUnsavedFeaturesFromSessionStorage();
    overviewHasUnsavedChanges.current = false;
    setOverviewResetTrigger(prev => prev + 1);
  }, [overviewHasUnsavedChanges, setOverviewResetTrigger]);

  const handleCloneClick = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      pendingCloseAction.current = () => {
        discardUnsavedDrafts();
        setIsCloneModalOpen(true);
      };
      return;
    }
    setIsCloneModalOpen(true);
  }, [
    discardUnsavedDrafts,
    hasUnsavedChanges,
    setShowUnsavedWarning,
    pendingCloseAction
  ]);

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const notificationPopoverRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  // Field highlighting for alerts â€” hook call moved just below `phasesForScrollPriority`
  // (it needs `normalizedPhases`, which isn't computed until later in this component).

  // Moment-scoped alert (GET /notifications/moment/{issueId}) â€” not overwritten by global list
  const alert = useSelector((state: RootState) =>
    card?.issueId ? selectAlertByIssueId(state, card.issueId) : undefined
  );

  // Get alert count for notification badge
  const alertCount = useSelector((state: RootState) =>
    selectAlertCountByIssueId(state, card?.issueId || '')
  );

  // Get issues from alert (filter out dismissed ones)
  const activeIssues =
    alert?.issues?.filter(
      issue => !alert.dismissedIssueKeys.includes(issue.issueKey)
    ) || [];

  const clearURLCommentID = () => {
    dispatch(commentActions.setCurrentCommentId(null));
  };

  const handleCommentPanelClose = useCallback(() => {
    setIsCommentPanelOpen(false);
    clearURLCommentID();
  }, [clearURLCommentID]);

  // Feature navigation state - read from URL params
  const [targetFeatureId, setTargetFeatureId] = useState<string | null>(null);
  const [targetProductId, setTargetProductId] = useState<string | null>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const featuresContainerRef = useRef<FeaturesContainerRef>(null); // Ref to trigger features refresh

  // âœ… Navigation context tracking - determines which priority to apply
  const [navigationContext, setNavigationContext] = useState<{
    type:
      | 'feature-click'
      | 'phase-click'
      | 'card-click'
      | 'chevron'
      | 'direct'
      | 'url';
    targetFeatureId?: string;
    targetPhaseId?: string;
    targetProductId?: string;
  }>({
    type: 'direct'
  });
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clearTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isChevronNavigationRef = useRef(false); // Track if navigation is via chevron
  const getCurrentTierRef = useRef<() => string>(() => '');
  const getCurrentTier = useCallback(() => getCurrentTierRef.current(), []);

  // Read featureId and productId from URL on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const featureId = searchParams.get('featureId');
    const productId = searchParams.get('productId');

    // âœ… Detect navigation type based on URL params and flags
    if (featureId && !isChevronNavigationRef.current) {
      setNavigationContext({
        type: 'feature-click',
        targetFeatureId: featureId,
        targetProductId: productId || undefined
      });
    } else if (propReleasePhaseId && !isChevronNavigationRef.current) {
      setNavigationContext({
        type: 'phase-click',
        targetPhaseId: propReleasePhaseId
      });
    } else if (isChevronNavigationRef.current) {
      setNavigationContext({ type: 'chevron' });
    } else if (location.state?.fromTimeline) {
      setNavigationContext({ type: 'card-click' });
    } else {
      setNavigationContext({ type: 'direct' });
    }

    // âœ… Clear any pending scroll/clear timeouts when URL changes
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }

    // âœ… Check sessionStorage for MVP navigation intent (from inherited milestone click)
    const shouldScrollToMvp = sessionStorage.getItem('scrollToMvpSection');
    if (shouldScrollToMvp === 'true') {
      // Clear the flag immediately to prevent repeated scrolling
      sessionStorage.removeItem('scrollToMvpSection');

      // âœ… Auto-expand Overview accordion if it's closed
      setIsOverviewExpanded(true);

      // Wait longer for accordion to expand and content to load, then scroll to MVP section
      setTimeout(() => {
        const mvpSection = document.getElementById('mvp-section');
        if (mvpSection) {
          mvpSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Trigger highlight animation via custom event
          window.dispatchEvent(new CustomEvent('highlightMvpSection'));
        }
      }, 800); // Increased from 500ms to 800ms to allow accordion expansion
    }
    // âœ… Handle MVP section hash navigation (legacy support)
    else if (location.hash === '#mvp-section') {
      // âœ… Auto-expand Overview accordion if it's closed
      setIsOverviewExpanded(true);

      setTimeout(() => {
        const mvpSection = document.getElementById('mvp-section');
        if (mvpSection) {
          mvpSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Trigger highlight animation via custom event
          window.dispatchEvent(new CustomEvent('highlightMvpSection'));

          // Clear the hash after scrolling
          window.history.replaceState(
            {},
            '',
            location.pathname + location.search
          );
        }
      }, 800); // Increased from 500ms to 800ms to allow accordion expansion
    }
    // âœ… Only scroll to features if featureId exists AND navigation is NOT from chevron
    else if (featureId && !isChevronNavigationRef.current) {
      setTargetFeatureId(featureId);
      setTargetProductId(productId);

      // Scroll to features section after a short delay to ensure rendering is complete
      scrollTimeoutRef.current = setTimeout(() => {
        if (featuresRef.current) {
          featuresRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }

        // âœ… FIX: Clear the URL params and target state after scrolling and highlighting
        // This allows users to collapse the accordion without it auto-expanding again
        clearTimeoutRef.current = setTimeout(() => {
          // Clear URL parameters
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete('featureId');
          newSearchParams.delete('productId');

          // Update URL without the feature params (without triggering navigation)
          const newUrl = `${location.pathname}${
            newSearchParams.toString() ? '?' + newSearchParams.toString() : ''
          }`;
          window.history.replaceState({}, '', newUrl);

          // Clear the target state to allow accordion control
          setTargetFeatureId(null);
          setTargetProductId(null);
        }, 1500); // Clear after 1.5 seconds (enough time for highlight animation)
      }, 300);
    } else {
      // Reset chevron navigation flag for next navigation
      isChevronNavigationRef.current = false;
    }

    // Cleanup function to clear timeouts if component unmounts or effect re-runs
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, [location.search, location.hash, location.pathname]);

  // ðŸŽ¯ Listen for MVP milestone status updates to trigger full data refresh
  useEffect(() => {
    const handleMvpStatusUpdate = (event: CustomEvent) => {
      // Trigger full seamless data refresh to update Overview section
      handleSeamlessDataRefresh();
    };

    window.addEventListener(
      'mvpStatusUpdated',
      handleMvpStatusUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        'mvpStatusUpdated',
        handleMvpStatusUpdate as EventListener
      );
    };
  }, [card.issueId]); // Re-attach listener when issueId changes

  // ðŸŽ¯ Listen for MVP link clicks to auto-expand Overview accordion
  useEffect(() => {
    const handleExpandOverviewForMvp = () => {
      // Expand Overview accordion so user can see the highlighted MVP section
      setIsOverviewExpanded(true);
    };

    window.addEventListener(
      'expandOverviewForMvp',
      handleExpandOverviewForMvp as EventListener
    );

    return () => {
      window.removeEventListener(
        'expandOverviewForMvp',
        handleExpandOverviewForMvp as EventListener
      );
    };
  }, []); // No dependencies needed - just listens for the event

  const deletingContactsRef = useRef<Set<string>>(new Set()); // Track contacts being deleted to avoid duplicate API work

  // Milestone sort: in-memory keys are `${phaseId}-${issueId}` (etc.); Date/Default
  // are meant to apply to every phase on the RSD â€” see useMilestoneSortPreference.
  const [milestoneSortBy, setMilestoneSortBy] = useState<
    Record<string, MilestoneSortPreferenceChoice>
  >({});
  const [sortDropdownOpen, setSortDropdownOpen] = useState<
    Record<string, boolean>
  >({});
  const [milestoneMenuOpen, setMilestoneMenuOpen] = useState<
    Record<string, boolean>
  >({});

  /** Explicit `undefined` when there is no card or no issue id (see useMilestoneSortPreference). */
  const milestoneSortIssueId: string | undefined =
    card == null ? undefined : card.issueId ?? undefined;

  const { getEffectiveSort, applyMilestoneSortAcrossIssue } =
    useMilestoneSortPreference(milestoneSortIssueId);

  const getEffectiveMilestoneSort = useCallback(
    (phaseKey: string) => getEffectiveSort(milestoneSortBy, phaseKey),
    [getEffectiveSort, milestoneSortBy]
  );

  const getMilestoneMatrixSortProps = useCallback(
    (phaseKey: string) => ({
      sortBy: getEffectiveMilestoneSort(phaseKey),
      onSortChange: (sortValue: MilestoneSortPreferenceChoice) => {
        applyMilestoneSortAcrossIssue(setMilestoneSortBy, sortValue, phaseKey);
      }
    }),
    [
      getEffectiveMilestoneSort,
      applyMilestoneSortAcrossIssue,
      setMilestoneSortBy
    ]
  );

  const focusInput = () => {
    const container = document.querySelector('.comment-form');
    const textarea = container?.querySelector('textarea');
    textarea?.focus();
  };

  // Get badge count directly from Redux state
  const badgeCount = useSelector(selectBadgeCount);

  const callFetchCommentsHandler = () => {
    if (cardData && cardData.issueId) {
      focusInput();
      dispatch(actions.fetchComments(cardData.issueId));
    }
  };

  const dispatchSectionChangeHandler = (key, sectionLabel, sectionCategory) => {
    dispatch(
      actions.sectionChanged({
        key: key,
        sectionLabel: sectionLabel,
        item: {
          id: key,
          label: sectionCategory,
          type: 'item'
        }
      })
    );
    dispatch(
      actions.filterChanged({
        key: key,
        filterLabel: sectionLabel,
        item: {
          id: key,
          label: sectionCategory,
          type: 'item'
        }
      })
    );
  };

  const handleMilestonesCommentIconClick = phase => {
    clearURLCommentID();
    callFetchCommentsHandler();
    setIsCommentPanelOpen(true);
    switch (phase.phaseName) {
      case 'Private Beta':
        dispatchSectionChangeHandler(
          'private-milestones',
          'Private Beta',
          'Milestones'
        );
        break;
      case 'Public Beta':
        dispatchSectionChangeHandler(
          'public-milestones',
          'Public Beta',
          'Milestones'
        );
        break;
      case 'GA':
        dispatchSectionChangeHandler('ga-milestones', 'GA', 'Milestones');
        break;
      case 'Internal Beta':
        dispatchSectionChangeHandler(
          'internal-milestones',
          'Internal Beta',
          'Milestones'
        );
        break;
    }
  };

  const handleGeneralInfoCommentIconClick = phase => {
    clearURLCommentID();
    callFetchCommentsHandler();
    setIsCommentPanelOpen(true);
    switch (phase.phaseName) {
      case 'Private Beta':
        dispatchSectionChangeHandler(
          'private-general-info',
          'Private Beta',
          'General Info'
        );
        break;
      case 'Public Beta':
        dispatchSectionChangeHandler(
          'public-general-info',
          'Public Beta',
          'General Info'
        );
        break;
      case 'GA':
        dispatchSectionChangeHandler('ga-general-info', 'GA', 'General Info');
        break;
      case 'Internal Beta':
        dispatchSectionChangeHandler(
          'internal-general-info',
          'Internal Beta',
          'General Info'
        );
        break;
      case 'General Info':
        dispatchSectionChangeHandler('general-info', '', 'General Info');
        break;
      case 'Milestones':
        dispatchSectionChangeHandler('milestones', '', 'Milestones');
        break;
      case 'Additional Info':
        dispatchSectionChangeHandler('additional-info', '', 'Additional Info');
        break;
      case 'DACI Contacts':
        dispatchSectionChangeHandler('daci-contacts', '', 'DACI Contacts');
        break;
      case 'Decisions':
        dispatchSectionChangeHandler('decisions', '', 'Decisions');
        break;
      case 'Overview':
        dispatchSectionChangeHandler('overview', '', 'Overview');
        break;
      case 'Features':
        dispatchSectionChangeHandler('features', '', 'Features');
        break;
      //For new moment type
      case 'VIP':
        dispatchSectionChangeHandler('vip-general-info', 'VIP', 'General Info');
        break;
      case 'Mobile':
        dispatchSectionChangeHandler(
          'mobile-general-info',
          'Mobile',
          'General Info'
        );
        break;
      case 'ETLA':
        dispatchSectionChangeHandler(
          'etla-general-info',
          'ETLA',
          'General Info'
        );
        break;
      case 'A.com Direct':
        dispatchSectionChangeHandler(
          'a.com-direct-general-info',
          'A.com Direct',
          'General Info'
        );
        break;
      case 'Initial Announcement':
        dispatchSectionChangeHandler(
          'initial-announcement-general-info',
          'Initial Announcement',
          'General Info'
        );
        break;
      case 'End of Sale':
        dispatchSectionChangeHandler(
          'end-of-sale-general-info',
          'End of Sale',
          'General Info'
        );
        break;
      case 'End of Support':
        dispatchSectionChangeHandler(
          'end-of-support-general-info',
          'End of Support',
          'General Info'
        );
        break;
      case 'Decommission':
      case 'Decommision':
        dispatchSectionChangeHandler(
          'decommission-general-info',
          'Decommission',
          'General Info'
        );
        break;
      default:
      //do nothing
    }
  };

  // Sort handlers for milestones - now phase-specific
  const handleSortToggle = (phaseKey: string) => {
    setSortDropdownOpen(prev => {
      // Close all other sort dropdowns and milestone menus
      const allClosed: Record<string, boolean> = {};
      return {
        ...allClosed,
        [phaseKey]: !prev[phaseKey]
      };
    });
    // Close all other dropdowns
    setMilestoneMenuOpen({});
    setRevenueImpactDropdownOpen(null);
    setStatusDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
    setTierDropdownOpen(null);
  };

  const handleMilestoneMenuToggle = (phaseKey: string) => {
    setMilestoneMenuOpen(prev => {
      // Close all other milestone menus
      const allClosed: Record<string, boolean> = {};
      return {
        ...allClosed,
        [phaseKey]: !prev[phaseKey]
      };
    });
    // Close all other dropdowns
    setSortDropdownOpen({});
    setRevenueImpactDropdownOpen(null);
    setStatusDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
    setTierDropdownOpen(null);
  };

  const handleRevenueImpactToggle = (phaseId: string) => {
    // Toggle revenue impact dropdown
    setRevenueImpactDropdownOpen(
      revenueImpactDropdownOpen === phaseId ? null : phaseId
    );
    // Close all other dropdowns
    setSortDropdownOpen({});
    setMilestoneMenuOpen({});
    setStatusDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
    setTierDropdownOpen(null);
  };

  const handleStatusToggle = (phaseId: string) => {
    // Toggle status dropdown
    setStatusDropdownOpen(statusDropdownOpen === phaseId ? null : phaseId);
    // Close all other dropdowns
    setSortDropdownOpen({});
    setMilestoneMenuOpen({});
    setRevenueImpactDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
    setTierDropdownOpen(null);
    setRtmDropdownOpen(null);
  };

  const handleRiskLevelToggle = (phaseId: string) => {
    // Toggle risk level dropdown
    setRiskLevelDropdownOpen(
      riskLevelDropdownOpen === phaseId ? null : phaseId
    );
    // Close all other dropdowns
    setSortDropdownOpen({});
    setMilestoneMenuOpen({});
    setRevenueImpactDropdownOpen(null);
    setStatusDropdownOpen(null);
    setTierDropdownOpen(null);
    setRtmDropdownOpen(null);
  };

  const handleTierToggle = (phaseId: string) => {
    // Toggle tier dropdown
    setTierDropdownOpen(tierDropdownOpen === phaseId ? null : phaseId);
    // Close all other dropdowns
    setSortDropdownOpen({});
    setMilestoneMenuOpen({});
    setRevenueImpactDropdownOpen(null);
    setStatusDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
    setRtmDropdownOpen(null);
  };

  const handleRtmToggle = (phaseId: string) => {
    setRtmDropdownOpen(rtmDropdownOpen === phaseId ? null : phaseId);
    setSortDropdownOpen({});
    setMilestoneMenuOpen({});
    setRevenueImpactDropdownOpen(null);
    setStatusDropdownOpen(null);
    setTierDropdownOpen(null);
    setRiskLevelDropdownOpen(null);
  };

  const handleSortSelect = (
    phaseKey: string,
    sortOption: MilestoneSortPreferenceChoice
  ) => {
    applyMilestoneSortAcrossIssue(setMilestoneSortBy, sortOption, phaseKey);
    setSortDropdownOpen(prev => ({
      ...prev,
      [phaseKey]: false
    }));
  };

  // Get descriptive text for current sort - now phase-specific
  const getSortDescriptiveText = (phaseKey: string) => {
    const sortValue = getEffectiveMilestoneSort(phaseKey);
    switch (sortValue) {
      case 'date':
        return 'Sort By';
      case 'name':
        return 'Sorted by Name';
      case 'none':
      default:
        return 'Sort By';
    }
  };

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside sort dropdown
      if (
        !target.closest(`.${styles.sortByDropdown}`) &&
        !target.closest(`.${styles.sortDropdown}`)
      ) {
        // Only update state if there are actually open dropdowns
        setSortDropdownOpen(prev => {
          const hasOpenDropdowns = Object.values(prev).some(isOpen => isOpen);
          if (hasOpenDropdowns) {
            return {}; // Close all open dropdowns
          }
          return prev; // No change if nothing is open
        });
      }
    };

    // Use capture phase so stopPropagation() on child buttons doesn't block this handler
    document.addEventListener('mousedown', handleClickOutside, true);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  // Trigger slide animation only on initial open, not when navigating between moments
  useEffect(() => {
    const currentCardId = card?.issueId;

    // If we have a card and it's different from the previous one
    if (currentCardId) {
      if (prevCardId.current === null) {
        // Initial open - trigger slide animation
        const timer = setTimeout(() => {
          setShowSlideAnimation(true);
        }, 10);
        prevCardId.current = currentCardId;
        return () => clearTimeout(timer);
      } else if (prevCardId.current !== currentCardId) {
        // Navigation between moments - no slide animation, just update content
        setShowSlideAnimation(true);
        prevCardId.current = currentCardId;
      }
    }
  }, [card?.issueId]);

  const openCommentsPanelHandler = () => {
    if (!isCommentPanelOpen) {
      focusInput();
      callFetchCommentsHandler();
      setIsCommentPanelOpen(true);
    }

    // Set filter to "All" to display all comments
    dispatch(
      actions.filterChanged({
        key: 'totalComments',
        item: {
          id: 'totalComments',
          label: 'All',
          name: 'All',
          type: 'item'
        },
        filterLabel: 'All'
      })
    );
  };

  // Get form API configuration
  const { config: formApiConfig, loading: formApiLoading } = useFormApiConfig();
  const [activeSection, setActiveSection] = useState('view');

  // Shell header "Ask AI" toggles Redux; keep slideout section in sync.
  useEffect(() => {
    if (showAiAssistant) {
      setActiveSection('ai');
    } else {
      setActiveSection(prev => (prev === 'ai' ? 'view' : prev));
    }
  }, [showAiAssistant]);

  const handleAskAiSectionClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (activeSection === 'ai') {
        setActiveSection('view');
        dispatch(layoutActions.closeAiAssistant());
        return;
      }
      setActiveSection('ai');
      dispatch(layoutActions.openAiAssistant());
    },
    [activeSection, dispatch]
  );

  const handleViewSectionClick = useCallback(() => {
    if (activeSection === 'ai') {
      dispatch(layoutActions.closeAiAssistant());
    }
    setActiveSection('view');
  }, [activeSection, dispatch]);

  // Banner state management
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Navigation logic
  const { openSlideout, currentCommentId } = useSlideoutUrl();
  const releases = useSelector(selectReleases);
  const filteredReleases = useSelector(selectFilteredReleases);
  const categoriesFilter = useSelector(selectCategories);
  const productGroupsFilter = useSelector(selectProductGroups);
  const audiencesFilter = useSelector(selectAudiences);
  const selectedEventsFilter = useSelector(selectEvents);
  const selectedFeaturesFilter = useSelector(selectFeatures);
  const selectedProductsFilter = useSelector(selectProductFilter);
  const lanesTiersFilter = useSelector(selectLanesTiers);
  const statusFilter = useSelector(selectStatus);
  const genAiFilter = useSelector(selectGenAiFilter);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const momentTypeFilterState = useSelector(selectMomentTypeFilter);
  const mustWinDropdownSelection = useSelector(selectMustWinDropdownSelection);
  const searchFilterActive = useSelector(selectSearchFilterActive);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(
    null
  ); // Track which phase's status dropdown is open
  const [riskLevelDropdownOpen, setRiskLevelDropdownOpen] = useState<
    string | null
  >(null); // Track which phase's risk dropdown is open
  const [tierDropdownOpen, setTierDropdownOpen] = useState<string | null>(null); // Track which phase's tier dropdown is open
  const [rtmDropdownOpen, setRtmDropdownOpen] = useState<string | null>(null); // Track which phase's RTM dropdown is open
  const [openDateChipPhaseId, setOpenDateChipPhaseId] = useState<string | null>(
    null
  ); // Track which phase's accordion date chip calendar is open
  const [nonPhasedDateChipOpen, setNonPhasedDateChipOpen] = useState(false); // Track non-phased accordion date chip calendar
  const [tierOptions, setTierOptions] = useState<string[]>([
    'L1',
    'L2',
    'L3',
    'Other'
  ]); // Tier options from API (default fallback)
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const navigationFilters = useMemo(
    () => ({
      searchFilterActive,
      categoriesFilter,
      productGroupsFilter,
      audiencesFilter,
      selectedEventsFilter,
      selectedFeaturesFilter,
      selectedProductsFilter,
      lanesTiersFilter,
      statusFilter,
      genAiFilter,
      revenueImpactFilter,
      momentTypeFilterState,
      mustWinDropdownSelection
    }),
    [
      searchFilterActive,
      categoriesFilter,
      productGroupsFilter,
      audiencesFilter,
      selectedEventsFilter,
      selectedFeaturesFilter,
      selectedProductsFilter,
      lanesTiersFilter,
      statusFilter,
      genAiFilter,
      revenueImpactFilter,
      momentTypeFilterState,
      mustWinDropdownSelection
    ]
  );

  const {
    handleNavigateNext,
    handleNavigatePrevious,
    getNextMomentId,
    getPreviousMomentId
  } = useSlideoutNavigation({
    card,
    hasUnsavedChanges,
    setShowUnsavedWarning,
    pendingCloseAction,
    overviewHasUnsavedChanges,
    setOverviewResetTrigger,
    setIsInitialLoading,
    isChevronNavigationRef,
    setTargetFeatureId,
    setTargetProductId,
    location,
    navigate,
    isCommentPanelOpen,
    dispatch,
    fetchComments: actions.fetchComments,
    releases,
    filteredReleases,
    navigationFilters,
    tierOptions,
    getCurrentTier
  });

  const [revenueImpactDropdownOpen, setRevenueImpactDropdownOpen] = useState<
    string | null
  >(null); // Track which phase's revenue driver dropdown is open
  const [revenueImpactOptions] = useState<string[]>(['Yes', 'No']); // $ (Revenue Impact) options
  const statusButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {}
  );
  const riskButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {}
  );
  const tierButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {}
  );
  const revenueImpactButtonRefs = useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const rtmButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Reset dropdown states when slideout opens/closes and move focus into the panel
  useEffect(() => {
    if (isOpen) {
      // Ensure dropdowns are closed when slideout opens
      setStatusDropdownOpen(null);
      setRiskLevelDropdownOpen(null);
      setTierDropdownOpen(null);
      setRevenueImpactDropdownOpen(null);

      // Blur any currently-focused element so no blue focus ring appears
      // when the slideout opens. The tab-trap handler will redirect focus
      // into the panel on the first Tab press.
      const timer = setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 200); // Small delay to allow slide animation to start
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const hasBlockingModalOpen = () => {
    if (typeof document === 'undefined') return false;

    const modalSelectors = [
      '[data-testid="additional-info-popup-modal-overlay"]',
      '[data-testid="decision-screen-modal-overlay"]',
      '[data-testid="edit-decision-popup-modal-overlay"]',
      '[data-testid="add-primary-contact-overlay"]',
      '[data-testid="edit-primary-contact-overlay"]',
      '[data-testid="daci-contact-popup-modal-overlay"]',
      '[data-testid="clone-moment-modal-overlay"]',
      '[data-testid="unsaved-changes-modal-overlay"]',
      '[data-testid="strategic-alignment-dialog-overlay"]',
      '.figma-modal-overlay'
    ];

    return Boolean(document.querySelector(modalSelectors.join(', ')));
  };

  // âœ… Keyboard navigation: Left/Right arrow keys to navigate between moments
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // If a popup/modal is open, don't run slideout shortcuts
      if (hasBlockingModalOpen()) {
        return;
      }

      // Only handle shortcuts if not typing in an input/textarea
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Don't interfere with typing in input fields (except for Escape)
      if (isInputField && event.key !== 'Escape') return;

      // Handle Escape key - close slideout (with unsaved changes check)
      if (event.key === 'Escape') {
        event.preventDefault();
        // Check for unsaved changes before closing
        if (hasUnsavedChanges()) {
          setShowUnsavedWarning(true);
          pendingCloseAction.current = () => onClose();
          return; // Don't close yet, wait for user confirmation
        }
        onClose();
        return;
      }

      // Handle Ctrl/Cmd + F - Open find/search (browser default, don't prevent)
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Let browser handle find - don't prevent default
        return;
      }

      // Handle Ctrl/Cmd + S - Save (prevent browser save dialog)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        // Show unsaved changes modal if there are unsaved changes
        if (hasUnsavedChanges()) {
          setShowUnsavedWarning(true);
          // No pending action - user wants to save, not close/navigate
          pendingCloseAction.current = null;
        }
        return;
      }

      // Handle Left Arrow (previous moment)
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleNavigatePrevious();
      }
      // Handle Right Arrow (next moment)
      else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNavigateNext();
      }
      // Handle Arrow Up/Down - scroll the slideout content container
      // (prevents the timeline/grid behind the panel from scrolling)
      else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        // Don't intercept arrow keys when user is inside a dropdown/listbox/menu
        const targetEl = event.target as HTMLElement;
        const isInsideDropdown = targetEl.closest(
          '[role="listbox"], [role="menu"], [role="combobox"], [role="option"], [class*="dropdown"], [class*="Dropdown"], [class*="Picker"], [class*="picker"]'
        );
        if (isInsideDropdown) return;

        event.preventDefault();
        const contentContainer = document.querySelector(
          '[data-testid="slideout-content-container"]'
        ) as HTMLElement;
        if (contentContainer) {
          const scrollAmount = 80; // px per arrow key press
          contentContainer.scrollTop +=
            event.key === 'ArrowDown' ? scrollAmount : -scrollAmount;
        }
      }
      // Tab is handled by the focus trap below
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, card?.issueId, hasUnsavedChanges, onClose]); // Re-attach when dependencies change

  // âœ… Enter key: activate focused elements (dropdowns, clickable divs, etc.)
  useEffect(() => {
    if (!isOpen) return;

    const handleEnterKey = (event: KeyboardEvent) => {
      if (hasBlockingModalOpen()) return;
      if (event.key !== 'Enter') return;

      const panel = document.querySelector(
        '[data-testid="slideout-panel"]'
      ) as HTMLElement;
      if (!panel) return;

      const target = event.target as HTMLElement;

      // Only handle if focus is inside the slideout panel
      if (!panel.contains(target)) return;

      // Native elements (button, a, input, select) already handle Enter natively â€” skip
      const nativeTag = target.tagName;
      if (
        nativeTag === 'BUTTON' ||
        nativeTag === 'A' ||
        nativeTag === 'SELECT'
      ) {
        return; // Let browser handle natively
      }

      // For INPUT and TEXTAREA, don't simulate click (user is typing)
      if (nativeTag === 'INPUT' || nativeTag === 'TEXTAREA') {
        return;
      }

      // For non-native interactive elements (divs with onClick, role="button", etc.)
      // simulate a click so Enter activates them like a mouse click
      if (
        target.hasAttribute('role') ||
        target.hasAttribute('tabindex') ||
        target.hasAttribute('data-testid') ||
        target.style.cursor === 'pointer' ||
        target.onclick
      ) {
        event.preventDefault();
        target.click();
      }
    };

    document.addEventListener('keydown', handleEnterKey);

    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [isOpen]);

  // âœ… Focus trap: keep Tab key navigation within the slideout panel when open
  useEffect(() => {
    if (!isOpen) return;

    const handleTabTrap = (event: KeyboardEvent) => {
      if (hasBlockingModalOpen()) return;
      if (event.key !== 'Tab') return;

      const panel = document.querySelector(
        '[data-testid="slideout-panel"]'
      ) as HTMLElement;
      if (!panel) return;

      // Get all focusable elements inside the slideout panel
      const focusableSelectors =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
      const focusableElements = Array.from(
        panel.querySelectorAll(focusableSelectors)
      ).filter(
        el =>
          !!(el as HTMLElement).offsetParent || // visible
          (el as HTMLElement).offsetWidth > 0 ||
          (el as HTMLElement).offsetHeight > 0
      ) as HTMLElement[];

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      // If focus is not inside the panel at all, move it in
      if (!panel.contains(activeElement)) {
        event.preventDefault();
        firstFocusable.focus();
        return;
      }

      // Shift+Tab on first element â†’ wrap to last
      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
        return;
      }

      // Tab on last element â†’ wrap to first
      if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
        return;
      }
    };

    // Use capture phase so this runs before any other keydown handlers
    document.addEventListener('keydown', handleTabTrap, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleTabTrap, { capture: true });
    };
  }, [isOpen]);

  // Fetch tier options from form template API on mount
  useEffect(() => {
    const loadTierOptions = async () => {
      try {
        // console.log('ðŸ“¡ Fetching tier options from form template...');
        const options = await fetchDropdownOptions('tier');
        if (options && options.length > 0) {
          const tierValues = options.map((opt: any) => opt.name || opt.id);
          // console.log('âœ… Tier options loaded:', tierValues);
          setTierOptions(tierValues);
        } else {
        }
      } catch (error) {
        // Keep default tier options on error
      }
    };
    loadTierOptions();
  }, []); // Run once on mount

  // Auto-open comment panel when commentId is present in URL
  useEffect(() => {
    const hasCommentIdInUrl = window.location.pathname.includes('/comments/');
    if (!hasCommentIdInUrl) return;

    if (currentCommentId && !isCommentPanelOpen) {
      setIsCommentPanelOpen(true);
      callFetchCommentsHandler();
    }
  }, [currentCommentId, isCommentPanelOpen]);

  // Add window resize and scroll listeners to reposition open dropdowns
  useEffect(() => {
    const handleResize = () => {
      // Close any open dropdowns on resize to prevent positioning issues
      if (statusDropdownOpen || riskLevelDropdownOpen) {
        setStatusDropdownOpen(null);
        setRiskLevelDropdownOpen(null);
      }
    };

    const handleScroll = (event: Event) => {
      // Only close dropdowns if scrolling is happening outside the slideout panel
      const target = event.target as HTMLElement;
      const slideoutPanel = document.querySelector(
        '[data-testid="sliding-panel-modal"]'
      );

      // If scrolling within the slideout panel, keep dropdowns open - they will recalculate position
      if (
        slideoutPanel &&
        (slideoutPanel.contains(target) || target === slideoutPanel)
      ) {
        // Dropdowns will handle position recalculation in their own scroll listeners
      }
      // If scrolling outside the panel (window scroll), also close dropdowns
      else if (
        target === document.body ||
        target === document.documentElement
      ) {
        if (statusDropdownOpen || riskLevelDropdownOpen) {
          setStatusDropdownOpen(null);
          setRiskLevelDropdownOpen(null);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Capture all scroll events
    document.addEventListener('scroll', handleScroll, true); // Also listen on document

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [statusDropdownOpen, riskLevelDropdownOpen]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      let shouldCloseStatus = false;
      let shouldCloseRisk = false;
      let shouldCloseTier = false;
      let shouldCloseRevenueImpact = false;

      // Check if click is outside status dropdown
      if (statusDropdownOpen) {
        const statusButton = statusButtonRefs.current[statusDropdownOpen];
        // Look for dropdown portal in document.body since it's rendered via createPortal
        const dropdownPortal = document.querySelector('.dropdownPortal');

        // Check if click is on the dropdown menu itself
        const targetElement = target as HTMLElement;
        const isClickOnDropdownMenu =
          targetElement.classList?.contains('statusDropdownMenu') ||
          targetElement.closest('.statusDropdownMenu') ||
          targetElement.classList?.contains('statusMenuItem') ||
          targetElement.closest('.statusMenuItem') ||
          targetElement.className?.includes('statusMenuItem') ||
          targetElement.closest('[class*="statusMenuItem"]');

        if (
          statusButton &&
          !statusButton.contains(target) &&
          !dropdownPortal?.contains(target) &&
          !isClickOnDropdownMenu
        ) {
          shouldCloseStatus = true;
        }
      }

      // Check if click is outside risk dropdown
      if (riskLevelDropdownOpen) {
        const riskButton = riskButtonRefs.current[riskLevelDropdownOpen];
        const targetElement = target as HTMLElement;

        const isClickOnRiskDropdown =
          targetElement.closest(`.${styles.riskLevelDropdownContainer}`) ||
          targetElement.closest(`.${styles.riskLevelDropdownMenu}`) ||
          targetElement.closest(`.${styles.riskLevelMenuItem}`);

        if (
          riskButton &&
          !riskButton.contains(target) &&
          !isClickOnRiskDropdown
        ) {
          shouldCloseRisk = true;
        }
      }

      // Check if click is outside tier dropdown
      if (tierDropdownOpen) {
        const tierButton = tierButtonRefs.current[tierDropdownOpen];
        const targetElement = target as HTMLElement;

        // Check if click is on tier dropdown menu or items
        const isClickOnTierDropdown =
          targetElement.closest(`.${styles.tierDropdownContainer}`) ||
          targetElement.closest(`.${styles.tierDropdownMenu}`) ||
          targetElement.closest(`.${styles.tierMenuItem}`);

        if (
          tierButton &&
          !tierButton.contains(target) &&
          !isClickOnTierDropdown
        ) {
          shouldCloseTier = true;
        }
      }

      // Check if click is outside revenue impact dropdown
      if (revenueImpactDropdownOpen) {
        const revenueButton =
          revenueImpactButtonRefs.current[revenueImpactDropdownOpen];
        const targetElement = target as HTMLElement;

        // Check if click is on revenue impact dropdown menu or items
        const isClickOnRevenueDropdown =
          targetElement.closest(`.${styles.tierDropdownContainer}`) ||
          targetElement.closest(`.${styles.tierDropdownMenu}`) ||
          targetElement.closest(`.${styles.tierMenuItem}`);

        if (
          revenueButton &&
          !revenueButton.contains(target) &&
          !isClickOnRevenueDropdown
        ) {
          shouldCloseRevenueImpact = true;
        }
      }

      // Check if click is outside RTM dropdown
      let shouldCloseRtm = false;
      if (rtmDropdownOpen) {
        const rtmButton = rtmButtonRefs.current[rtmDropdownOpen];
        const targetElement = target as HTMLElement;
        const isClickOnRtmDropdown =
          targetElement.closest(`.${styles.rtmDropdownContainer}`) ||
          targetElement.closest(`.${styles.rtmDropdownMenu}`) ||
          targetElement.closest(`.${styles.rtmMenuItem}`);
        if (rtmButton && !rtmButton.contains(target) && !isClickOnRtmDropdown) {
          shouldCloseRtm = true;
        }
      }

      // Check if click is outside Add Phase dropdown
      if (addPhaseDropdownOpen) {
        const targetElement = target as HTMLElement;
        const isClickOnAddPhase =
          addPhaseButtonRef.current?.contains(target as Node) ||
          targetElement.closest('[data-add-phase-dropdown]');
        if (!isClickOnAddPhase) {
          setAddPhaseDropdownOpen(false);
        }
      }

      if (shouldCloseStatus) setStatusDropdownOpen(null);
      if (shouldCloseRisk) setRiskLevelDropdownOpen(null);
      if (shouldCloseTier) setTierDropdownOpen(null);
      if (shouldCloseRevenueImpact) setRevenueImpactDropdownOpen(null);
      if (shouldCloseRtm) setRtmDropdownOpen(null);
    };

    if (
      statusDropdownOpen ||
      riskLevelDropdownOpen ||
      tierDropdownOpen ||
      revenueImpactDropdownOpen ||
      rtmDropdownOpen ||
      addPhaseDropdownOpen
    ) {
      // Use capture phase so stopPropagation() on child buttons doesn't block this handler
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [
    statusDropdownOpen,
    riskLevelDropdownOpen,
    tierDropdownOpen,
    revenueImpactDropdownOpen,
    rtmDropdownOpen
  ]);

  // Click outside handler to close accordion date chip calendars
  useEffect(() => {
    if (!openDateChipPhaseId && !nonPhasedDateChipOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target.closest('[data-testid^="slideout-phase-date-chip-wrapper"]') &&
        !target.closest('[data-testid^="slideout-moment-date-chip-wrapper"]') &&
        !target.closest('.calendar-range-container')
      ) {
        setOpenDateChipPhaseId(null);
        setNonPhasedDateChipOpen(false);
      }
    };
    // Use capture phase so stopPropagation() on child buttons doesn't block this handler
    document.addEventListener('mousedown', handleClickOutside, true);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside, true);
  }, [openDateChipPhaseId, nonPhasedDateChipOpen]);

  // API data state
  const [apiData, setApiData] = useState<MomentData | null>(null);
  const [isLoadingApiData, setIsLoadingApiData] = useState(false);

  // Keep a stable reference for empty overview data to avoid re-creating objects on re-render
  const EMPTY_OVERVIEW: Record<string, any> = useMemo(() => ({}), []);

  // Memoize adapted overview values so unrelated SlideOutPanel re-renders (e.g. tier dropdown)
  // don't change the `values` prop identity and cause Overview to reset its local state.
  const rawOverviewData = useMemo(() => {
    if (apiData?.overview) return apiData.overview;
    if (card?._apiData?.overview) return card._apiData.overview;
    return EMPTY_OVERVIEW;
  }, [apiData?.overview, card?._apiData?.overview, EMPTY_OVERVIEW]);

  const adaptedOverviewData = useMemo(() => {
    return adaptOverviewData(rawOverviewData);
  }, [rawOverviewData]);

  // Smooth loading state management
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Use global cache for events and parent moments instead of local state
  const { actions: globalCacheActions } = useGlobalCacheSlice();
  const eventsData = useSelector(selectEventsData);
  const parentMomentsData = useSelector(selectParentMomentsData);
  const isLoadingEvents = useSelector(selectIsEventsLoading);
  const isLoadingParentMoments = useSelector(selectIsParentMomentsLoading);
  const eventsError = useSelector(selectEventsError);
  const parentMomentsError = useSelector(selectParentMomentsError);

  // Get hierarchy filters data from Redux store
  const hierarchyFilters = useSelector(selectHierarchyFilters);

  // Add state for contact information
  const [updatedProdMarketingManager, setUpdatedProdMarketingManager] =
    useState<string | null>(null);
  const [updatedProductManager, setUpdatedProductManager] = useState<
    string | null
  >(null);
  const [updatedProgramManager, setUpdatedProgramManager] = useState<
    string | null
  >(null);

  // Add state for contacts list
  const [primaryContacts, setPrimaryContacts] = useState<any[]>([]);

  // Add state for DACI contacts - use API data if available, fallback to mock data
  const [daciContacts, setDaciContacts] = useState<any>(null);

  const {
    title,
    categories,
    status,
    dates,
    milestones = [],
    description,
    businessOrg,
    priority,
    riskLevel,
    labels = [],
    projectType,
    part_of_an_event,
    product_marketing_manager,
    product_manager,
    program_manager,
    issueId
  } = card;

  // State for display title (with product groups)
  const [displayTitle, setDisplayTitle] = useState(title ?? '');

  // State for product dropdown functionality
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [enableProductDropdown, setEnableProductDropdown] = useState(true);
  // Ref to track if a product update is in progress
  const isProductUpdateInProgress = useRef(false);
  // Ref to track the last user-initiated product selection timestamp
  const lastUserProductUpdateTimestamp = useRef(0);
  // Ref to track the last user-selected products
  const lastUserSelectedProducts = useRef<string[]>([]);
  // Hierarchy products are now loaded from Redux store at app startup
  // No need for local state anymore

  // Effect to load API data when component mounts with smooth loading
  useEffect(() => {
    if (card.issueId && isOpen) {
      setIsInitialLoading(true);
      setLoadingProgress(10); // Start loading

      // âœ… Safety timeout: Force loading to complete after 10 seconds to prevent freeze
      const loadingTimeout = setTimeout(() => {
        setIsInitialLoading(false);
        setIsLoadingApiData(false);
        setLoadingProgress(100);
      }, 10000); // 10 second timeout

      // Check if API data is already provided via card._apiData
      if (card._apiData) {
        setApiData(card._apiData);
        setDaciContacts(card._apiData.daciContacts || null);
        setLoadingProgress(50); // Data loaded from cache

        // Check if global cache data is ready and complete loading faster
        if (eventsData.length > 0 && parentMomentsData.length > 0) {
          setLoadingProgress(100);
          setTimeout(() => {
            setIsInitialLoading(false);
            clearTimeout(loadingTimeout); // Clear timeout on success
          }, 100);
        } else {
          // Smooth transition to complete even if cache is still loading
          setTimeout(() => {
            setLoadingProgress(100);
            setTimeout(() => {
              setIsInitialLoading(false);
              clearTimeout(loadingTimeout); // Clear timeout on success
            }, 150);
          }, 100);
        }
      } else {
        // Fetch from API with force refresh to always get latest data
        setIsLoadingApiData(true);
        setLoadingProgress(20);

        // Fetch moment data and notifications in parallel
        Promise.all([
          fetchMomentDataCached(card.issueId, true), // Always force refresh for slideouts
          // Fetch moment-specific notifications
          Promise.resolve(
            dispatch(
              alertsActions.loadMomentNotifications({ issueId: card.issueId })
            )
          )
        ])
          .then(([response]) => {
            setLoadingProgress(70);
            if (response.success && response.data) {
              setApiData(response.data);
              setDaciContacts(response.data.daciContacts || null);
            } else {
              setDaciContacts(null);
            }
            setLoadingProgress(90);
          })
          .catch(err => {
            setDaciContacts(null);
            setLoadingProgress(90);
          })
          .finally(() => {
            setIsLoadingApiData(false);
            setLoadingProgress(100);
            // Check if global cache data is ready and complete loading faster
            if (eventsData.length > 0 && parentMomentsData.length > 0) {
              setTimeout(() => {
                setIsInitialLoading(false);
                clearTimeout(loadingTimeout); // Clear timeout on success
              }, 100);
            } else {
              // Smooth transition to complete even if cache is still loading
              setTimeout(() => {
                setIsInitialLoading(false);
                clearTimeout(loadingTimeout); // Clear timeout on success
              }, 200);
            }
          });
      }

      // Cleanup function to clear timeout
      return () => {
        clearTimeout(loadingTimeout);
      };
    }
  }, [card.issueId, card._apiData, isOpen]);

  // Reset loading state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialLoading(true);
      setLoadingProgress(0);
    }
  }, [isOpen]);

  // Events and parent moments are loaded globally at app startup.
  //
  // loadGlobalCacheData already refreshes parent moments when their cache has expired
  // (selectIsParentMomentsCacheValid / cacheExpiry), so the previous unconditional
  // forceRefreshParentMoments() here re-fetched the full ACTIVE marketing-moment list on *every*
  // open, bypassing that TTL for data that changes rarely. Rely on the TTL instead; a stale-parent
  // edit is still corrected on the next expiry, and the picker is not the source of truth for saves.
  useEffect(() => {
    if (isOpen) {
      // Refresh cached global data whose TTL has expired (saga checks validity per entry)
      dispatch(globalCacheActions.loadGlobalCacheData());
    }
  }, [isOpen, dispatch, globalCacheActions]);

  // Extract permission data from API response and get permissions
  // This must be after apiData state declaration but before usage in JSX
  const permissionData = extractPermissionData(apiData);
  const permissions = useRBACUtils(permissionData);

  // Safely get permission values with defaults
  const canEditGeneralInfo = permissions?.canEditGeneralInfo ?? false;
  const canEditMilestones = permissions?.canEditMilestones ?? false;

  // Helper function to get API data only
  const getDataWithFallback = () => {
    return apiData || null;
  };

  // Feedback slide-out for a milestone's own Feedback icon (see
  // MilestoneMatrixFromAPI.tsx) - owned here (the RSD slide-out's own
  // root), the same "own the navigation state, render the slide-out
  // alongside everything else" pattern ReviewApproval/index.tsx already
  // uses for its own three tables. Independent of any of this component's
  // other slide-out/modal state - only one can be meaningfully open at a
  // time from a user's perspective, but nothing here needs to coordinate
  // that; FeedbackSlideOut portals itself above this whole panel regardless
  // (see FeedbackSlideOut.module.scss's own comment on why its z-index was
  // bumped for this exact call site).
  const [feedbackNavigation, setFeedbackNavigation] =
    useState<FeedbackSlideoutNavigation | null>(null);

  /**
   * Takes an already-resolved `{issueId, milestoneId, milestoneName}` (built
   * by MilestoneMatrixFromAPI.tsx's own Feedback icon click handler, using
   * the exact same `(milestone as any).id || milestoneId` fallback its
   * sibling `fetchMilestoneApprovalStatus` call already relies on - a raw
   * milestone entry doesn't reliably carry its own `id`, so this must NOT
   * require it unconditionally the way an earlier version of this callback
   * did, which is why clicking the icon appeared to do nothing) - not a raw
   * `Milestone` object, since deriving `issueId` from `card` here would be
   * wrong for a phase whose own resolved `issueId` differs from `card`'s
   * (see that matrix's own `issueId={milestones?.issueId || card?.issueId!}`
   * prop).
   *
   * Builds the exact same `FeedbackSlideoutContext.releaseMoment` shape
   * (categories/products/productGroups/momentType/surfaces/genai/
   * consumesCredit) as the real Header's own `headerProps` below
   * (`SlideoutHeaderShell`) - same real Moment data already loaded for this
   * slide-out, just read again here since those are inline expressions
   * there rather than named variables this callback could import/reuse
   * directly. No second Moment/Milestone GET.
   */
  const openMilestoneFeedback = useCallback(
    (feedbackContext: {
      issueId: string;
      milestoneId: string;
      milestoneName: string;
    }) => {
      const mt = card?.momentType || card?.projectType || (card as any)?.type;
      const pco = (card as any)?.pco;
      const supportsGenAi = momentTypeSupportsGenAi(mt, { pco });
      const data = getDataWithFallback() as any;
      setFeedbackNavigation({
        context: {
          milestoneId: feedbackContext.milestoneId,
          momentName: displayTitle,
          milestoneName: feedbackContext.milestoneName,
          releaseMoment: {
            issueId: feedbackContext.issueId,
            categories,
            products: (card as any).products || [],
            productGroups: Array.isArray(data?.productGroups)
              ? data.productGroups
              : [],
            momentType: mt,
            surfaces: Array.isArray(data?.surfaces) ? data.surfaces : [],
            genai: supportsGenAi
              ? data?.genai == null
                ? undefined
                : data.genai
              : undefined,
            consumesCredit: supportsGenAi
              ? data?.consumesCredit == null
                ? undefined
                : data.consumesCredit
              : undefined
          }
        }
      });
    },
    [card, categories, displayTitle]
  );

  // Effect to initialize product data for dropdown
  useEffect(() => {
    // Skip updating products if a product update is in progress
    // This prevents the effect from overwriting optimistic updates
    if (isProductUpdateInProgress.current) {
      // console.log('â¸ï¸ Skipping product initialization - update in progress');
      return;
    }

    const data = getDataWithFallback() as any;
    const cardProducts = (card as any).products || [];
    const dataProducts = data?.products || [];

    // âœ… FIX: Prioritize apiData.products over card.products
    // apiData is kept fresh with soft refreshes, while card prop may be stale
    const initialProducts = Array.isArray(dataProducts)
      ? dataProducts
      : Array.isArray(cardProducts)
      ? cardProducts
      : [];

    // CRITICAL FIX: Check if we have recent user selections that should take precedence
    const timeSinceLastUserUpdate =
      Date.now() - lastUserProductUpdateTimestamp.current;
    const hasRecentUserUpdate = timeSinceLastUserUpdate < 3000; // 3 second protection window (increased for stability)

    // If user recently made changes, preserve their exact selection and order
    if (hasRecentUserUpdate) {
      const userSelectedProducts = lastUserSelectedProducts.current;

      // Check if API data matches user's exact order (WITHOUT sorting)
      const productsMatchExactOrder =
        JSON.stringify(userSelectedProducts) ===
        JSON.stringify(initialProducts);

      // Always preserve user's selection order during the protection window
      // console.log(

      // Still update available products but keep user's selection
      const apiProducts = initialProducts || [];
      const reduxHierarchyProducts = hierarchyFilters.products || [];
      const allAvailableProducts = Array.from(
        new Set([
          ...apiProducts,
          ...reduxHierarchyProducts,
          ...userSelectedProducts
        ])
      );
      setAvailableProducts(allAvailableProducts);
      return; // Don't overwrite user's selection or change order
    }

    // console.log('Initializing selectedProducts:', {

    setSelectedProducts(initialProducts);

    // Set available products - use Redux hierarchy products combined with current API products
    const apiProducts = initialProducts || [];
    const reduxHierarchyProducts = hierarchyFilters.products || [];
    const allAvailableProducts = Array.from(
      new Set([...apiProducts, ...reduxHierarchyProducts])
    );

    // console.log('Setting available products:', {

    setAvailableProducts(allAvailableProducts);
  }, [card, apiData, hierarchyFilters.products]);

  // Hierarchy products are now loaded from Redux store at app startup
  // No need to fetch them here anymore

  // Debug effect to monitor selectedProducts changes
  useEffect(() => {
    // console.log('selectedProducts changed:', selectedProducts);
  }, [selectedProducts]);

  // Effect to update display title when API data changes
  useEffect(() => {
    const data = getDataWithFallback();
    const productGroups = (data as any)?.productGroups || [];

    // Use the title from API data if available, otherwise fall back to card.title
    const rawTitle = data?.name || (data as any)?.summary || title;
    const mt = data?.momentType || card?.projectType;
    const parts = getRtmPhasedTitleParts({
      momentType: mt,
      rawTitle,
      releasePhase: card?.releasePhase,
      releasePhaseId: card?.releasePhaseId,
      releasePhases: Array.isArray(data?.releasePhases)
        ? data!.releasePhases
        : null
    });

    if (!productGroups || productGroups.length === 0) {
      setDisplayTitle(parts.displayTitle);
      return;
    }

    const productGroupNames = productGroups.map((group: any) => {
      return typeof group === 'string'
        ? group
        : group.name || group.label || group;
    });
    const productGroupsPrefix = productGroupNames.join(', ') + ' - ';
    setDisplayTitle(`${productGroupsPrefix}${parts.displayTitle}`);
  }, [
    apiData,
    title,
    card?.releasePhase,
    card?.releasePhaseId,
    card?.projectType
  ]); // Re-run when API data, title, or card phase changes

  // --- PHASED/NON-PHASED LOGIC ---
  // Check if releasePhases exists and determine if it's array or object structure
  const currentData = getDataWithFallback();
  const releasePhases = currentData ? (currentData as any).releasePhases : null;
  const hasPhases = !!(
    releasePhases &&
    (Array.isArray(releasePhases)
      ? releasePhases.length > 0
      : Object.keys(releasePhases).length > 0)
  );

  // PCO / EOL: milestones at moment root, not inside each release phase.
  // Moment type is the primary signal so this stays true when milestones array is empty.
  const _rawMomentTypeForMilestones =
    (currentData as any)?.momentType || card?.momentType || card?.projectType;
  const hasMomentLevelMilestones = !!(
    hasPhases &&
    (Array.isArray(releasePhases)
      ? releasePhases.every((p: any) => !p.milestones)
      : true) &&
    (usesMomentLevelMilestonesType(_rawMomentTypeForMilestones) ||
      // Fallback: non-empty milestones array at root level (legacy non-PCO/EOL edge cases)
      (Array.isArray((currentData as any)?.milestones) &&
        (currentData as any).milestones.length > 0 &&
        (currentData as any).milestones[0]?.milestones))
  );

  // Normalize phases to array format for consistent handling (memoized to prevent unnecessary re-renders)
  const normalizedPhases = React.useMemo(() => {
    return hasPhases
      ? Array.isArray(releasePhases)
        ? releasePhases.map((phase, index) => ({
            ...phase,
            // Generate unique ID if null or missing (fixes API returning null IDs)
            // Format: phase-{index}-{phase-name} ensures uniqueness across all phases
            id:
              phase.id ||
              `phase-${index}-${
                phase.phaseName?.toLowerCase().replace(/\s+/g, '-') || 'unknown'
              }`
          }))
        : Object.keys(releasePhases).map(key => ({
            id: key,
            phaseName: key,
            ...releasePhases[key]
          }))
      : [];
  }, [hasPhases, releasePhases]);

  // Get phase identifiers for state management (use unique IDs, memoized)
  const phaseIds = React.useMemo(() => {
    return normalizedPhases.map(phase => phase.id);
  }, [normalizedPhases]);

  // Phase display order fed to resolveAlertScrollWinner (see alertScrollPriority.ts) — [] for
  // non-phased moments. Uses the SAME ids as normalizedPhases so a resolved winner's phaseId
  // matches the phaseId prop passed into SlideoutMilestoneMatrix/MilestoneMatrixFromAPI below.
  const phasesForScrollPriority: PhaseOrderEntry[] = React.useMemo(() => {
    return normalizedPhases.map(phase => ({
      id: phase.id,
      phaseName: phase.phaseName || ''
    }));
  }, [normalizedPhases]);

  // Field highlighting for alerts — moved here (rather than near the top of the component) because
  // it needs `phasesForScrollPriority`, which itself depends on `normalizedPhases`/`releasePhases`
  // computed just above. `clearHighlight` (the only other return value) is not consumed elsewhere
  // in this file, so relocating this hook call does not change any other code's behavior.
  const { clearHighlight } = useFieldHighlight(
    card?.issueId || '',
    isOpen,
    phasesForScrollPriority
  );

  // Shared helper to determine if a phase is considered ACTIVE
  const isPhaseActive = (phase: any): boolean => {
    const reportingStatus = phase?.generalInfo?.reportingStatus;
    const status = phase?.status;

    return (
      reportingStatus === 'ACTIVE' ||
      reportingStatus === 'Active' ||
      status === 'ACTIVE' ||
      status === 'Active'
    );
  };

  // Helper function to parse date from various formats, handling N/A and TBD
  const parsePhaseDate = (dateValue: string): Date | null => {
    if (
      !dateValue ||
      dateValue === 'N/A' ||
      dateValue === 'TBD' ||
      dateValue.trim() === ''
    ) {
      return null;
    }

    // Handle different date formats
    try {
      // Try parsing as various formats
      if (dateValue.includes(' ') && dateValue.length <= 10) {
        // Format like "Oct 2025" or "Q1 2025"
        const parts = dateValue.split(' ');
        if (parts.length === 2) {
          const [monthOrQuarter, year] = parts;

          if (monthOrQuarter.startsWith('Q')) {
            // Quarter format: Q1 2025, Q2 2025, etc.
            const quarter = parseInt(monthOrQuarter.replace('Q', ''));
            const month = (quarter - 1) * 3; // Q1=0, Q2=3, Q3=6, Q4=9
            return new Date(parseInt(year), month, 1);
          } else {
            // Month format: Oct 2025, Jan 2025, etc.
            return new Date(`${monthOrQuarter} 1, ${year}`);
          }
        }
      }

      // Try parsing as standard date
      return new Date(dateValue);
    } catch (error) {
      return null;
    }
  };

  // Function to find the most current active phase (closest to today)
  const findMostCurrentPhase = (): (typeof normalizedPhases)[0] | null => {
    if (normalizedPhases.length === 0) {
      return null;
    }

    const today = new Date();
    let mostCurrentPhase: (typeof normalizedPhases)[0] | null = null;
    let smallestDiff = Infinity;

    // First, prioritize ACTIVE phases
    const activePhases = normalizedPhases.filter(phase => {
      const isActive =
        phase.generalInfo?.reportingStatus === 'ACTIVE' ||
        phase.status === 'ACTIVE' ||
        phase.generalInfo?.reportingStatus === 'Active' ||
        phase.status === 'Active';
      return isActive;
    });

    const phasesToCheck =
      activePhases.length > 0 ? activePhases : normalizedPhases;

    phasesToCheck.forEach(phase => {
      const dates = phase.generalInfo?.dates;

      if (!dates) {
        return;
      }

      // Use the same date fields as marketing moment and release date
      // Priority: targetStart > targetMonth > targetQuarter for target dates
      // Priority: releaseDate > releaseMonth > releaseQuarter for release dates
      // PCO (Price Changes and Offers) RTM phases use effectiveDate / customerNotificationDate
      const targetDateStr =
        dates.targetStart ||
        dates.targetMonth ||
        dates.targetQuarter ||
        dates.customerNotificationDate;
      const releaseDateStr =
        dates.releaseDate ||
        dates.releaseMonth ||
        dates.releaseQuarter ||
        dates.effectiveDate;

      const targetDate = parsePhaseDate(targetDateStr);
      const releaseDate = parsePhaseDate(releaseDateStr);

      // Calculate difference from today using release date for comparison
      let phaseDiff = Infinity;

      if (releaseDate) {
        // Use release date as primary comparison point
        phaseDiff = Math.abs(today.getTime() - releaseDate.getTime());

        // If release date is in the future, give it slight priority
        if (releaseDate > today) {
          phaseDiff = phaseDiff * 0.9; // Slight preference for upcoming phases
        }
      } else if (targetDate) {
        // Fallback to target date if no release date
        phaseDiff = Math.abs(today.getTime() - targetDate.getTime());

        if (targetDate > today) {
          phaseDiff = phaseDiff * 0.9;
        }
      }

      if (phaseDiff < smallestDiff) {
        smallestDiff = phaseDiff;
        mostCurrentPhase = phase;
      }
    });

    return mostCurrentPhase;
  };

  // Function to find the "latest" phase (the one with the most future/recent release date)
  // This is useful for expanding the most advanced phase in the timeline
  const findLatestPhase = (): (typeof normalizedPhases)[0] | null => {
    if (normalizedPhases.length === 0) {
      return null;
    }

    // âœ… PRIORITY ORDER: GA > Public Beta > Private Beta > Internal Beta
    // NEW RULE: Prefer ACTIVE phases first, then fall back to any status
    // This ensures we don't auto-open a non-active GA when another phase is ACTIVE
    const phasePriority = [
      'GA',
      'Public Beta',
      'Private Beta',
      'Internal Beta'
    ];

    // 1) Try to find ACTIVE phases by priority name
    for (const phaseName of phasePriority) {
      const matchingActivePhase = normalizedPhases.find(
        phase => phase.phaseName === phaseName && isPhaseActive(phase)
      );
      if (matchingActivePhase) {
        return matchingActivePhase;
      }
    }

    // 2) If no ACTIVE phase matches the priority names, try any ACTIVE phase by date
    const activePhases = normalizedPhases.filter(phase => isPhaseActive(phase));

    const phasesForDateSearch =
      activePhases.length > 0 ? activePhases : normalizedPhases;

    // âœ… FALLBACK: Use date-based logic on the chosen set (ACTIVE first, else all)
    let latestPhase: (typeof normalizedPhases)[0] | null = null;
    let latestDate: Date | null = null;

    phasesForDateSearch.forEach(phase => {
      const dates = phase.generalInfo?.dates;

      if (!dates) {
        return;
      }

      // Priority: releaseDate > targetStart > releaseMonth > targetMonth
      // PCO (Price Changes and Offers) RTM phases use effectiveDate / customerNotificationDate
      const releaseDateStr =
        dates.releaseDate ||
        dates.releaseMonth ||
        dates.releaseQuarter ||
        dates.effectiveDate;
      const targetDateStr =
        dates.targetStart ||
        dates.targetMonth ||
        dates.targetQuarter ||
        dates.customerNotificationDate;

      const releaseDate = parsePhaseDate(releaseDateStr);
      const targetDate = parsePhaseDate(targetDateStr);

      // Use release date if available, otherwise fall back to target date
      const phaseDate = releaseDate || targetDate;

      if (phaseDate) {
        // If this is the first phase with a date, or if this date is later than our current latest
        if (!latestDate || phaseDate > latestDate) {
          latestDate = phaseDate;
          latestPhase = phase;
        }
      }
    });

    return latestPhase;
  };

  /** Moment type from card, active phase, or root generalInfo (for UI and payloads). */
  const resolveMomentType = (): string => {
    // Try card.momentType first (if available)
    if (card?.momentType) {
      return card.momentType;
    }

    // For phased layouts: get from active phase
    if (hasPhases && normalizedPhases.length > 0) {
      // First, try to find an ACTIVE phase
      const activePhase = normalizedPhases.find(phase => {
        const isActive =
          phase.generalInfo?.reportingStatus === 'ACTIVE' ||
          phase.status === 'ACTIVE' ||
          phase.generalInfo?.reportingStatus === 'Active' ||
          phase.status === 'Active';
        return isActive;
      });

      // Return momentType from active phase, or fallback to first phase
      const phaseToUse = activePhase || normalizedPhases[0];

      if (phaseToUse?.generalInfo?.momentType) {
        return phaseToUse.generalInfo.momentType;
      }
    }

    // For non-phased layouts: get from root generalInfo
    const data = getDataWithFallback() as any;

    if (data?.generalInfo?.momentType) {
      return data.generalInfo.momentType;
    }

    // Final fallback
    return 'Unknown';
  };

  // âœ… NEW: Global accordion preference system
  // This is a GLOBAL preference (not per-phase, not per-RSD)
  // Determines if user wants accordions open or closed when navigating
  const initialExpandedPhases = React.useMemo(() => {
    // âœ… PRIORITY 1: Direct User Click Intent (Highest)
    // Timeline phase click always overrides stored preferences
    if (navigationContext.type === 'phase-click' && propReleasePhaseId) {
      const matchingPhase = normalizedPhases.find(
        p => p.id === propReleasePhaseId
      );
      if (matchingPhase) {
        return [propReleasePhaseId];
      }
    }

    // âœ… PRIORITY 2: Global Accordion Preference (High)
    // Check if user has a global preference for accordions being open/closed
    const accordionPreference = localStorage.getItem(
      'slideout_accordion_preference'
    );

    // If user has explicitly closed accordions â†’ Respect that for navigation
    if (accordionPreference === 'closed') {
      return []; // Return empty array = all accordions closed
    }

    // âœ… If user preference is OPEN â†’ Always open latest/active phase
    // This applies to all navigation types (chevron, card click, direct, etc.)
    if (accordionPreference === 'open') {
      const latestPhase = findLatestPhase();
      if (latestPhase?.id) {
        return [latestPhase.id];
      }
    }

    // âœ… PRIORITY 3: Chevron Navigation (Medium)
    // For next/previous navigation, check if user had any accordions open
    if (navigationContext.type === 'chevron') {
      const storedExpandedPhases = localStorage.getItem(
        'slideout_expanded_phases'
      );
      if (storedExpandedPhases) {
        try {
          const parsedPhases = JSON.parse(storedExpandedPhases);

          // If user had collapsed all phases â†’ Keep them collapsed
          if (parsedPhases.length === 0) {
            return [];
          }

          // Otherwise, open the latest/active phase
          const latestPhase = findLatestPhase();
          if (latestPhase?.id) {
            return [latestPhase.id];
          }
        } catch (e) {
          console.warn('Failed to parse stored expanded phases:', e);
        }
      }
    }

    // âœ… PRIORITY 4: Feature Click
    if (navigationContext.type === 'feature-click') {
      // For feature click, open the latest/active phase
      const result = [findLatestPhase()?.id || normalizedPhases[0]?.id].filter(
        Boolean
      );
      return result;
    }

    // âœ… PRIORITY 5: Card Click or Default
    // Open the latest/active phase
    const latestPhase = findLatestPhase();
    if (latestPhase?.id) {
      return [latestPhase.id];
    }

    // Fallback to most current phase (closest to today)
    const mostCurrentPhase = findMostCurrentPhase();
    const mostCurrentPhaseId = mostCurrentPhase?.id;

    const fallbackPhaseIds = phaseIds.filter(id => {
      const phase = normalizedPhases.find(p => p.id === id);
      const isPublicBeta = phase?.phaseName === 'Public Beta';
      return isPublicBeta;
    });

    let expandedPhases = mostCurrentPhaseId
      ? [mostCurrentPhaseId]
      : fallbackPhaseIds.length > 0
      ? fallbackPhaseIds
      : normalizedPhases[0]?.id
      ? [normalizedPhases[0].id]
      : [];

    return expandedPhases;
  }, [normalizedPhases, phaseIds, propReleasePhaseId, navigationContext]); // Add navigationContext to dependencies

  const [customExpandedPhases, setCustomExpandedPhases] = useState<Set<string>>(
    new Set(initialExpandedPhases)
  );

  // Track previous card and navigation type so we don't reset accordions
  // on seamless data refreshes (e.g., General Info edits, milestone updates)
  const previousIssueIdRef = useRef<string | null>(null);
  const previousNavTypeRef = useRef<string | null>(null);

  // Separate refs for non-phased/section reset effect so it can also
  // distinguish real navigation vs. soft refreshes independently
  const previousIssueIdForResetRef = useRef<string | null>(null);
  const previousNavTypeForResetRef = useRef<string | null>(null);

  // Track which sections were auto-expanded (so we don't override manual collapse)
  const autoExpandedSectionsRef = useRef<Set<'overview' | 'general-info'>>(
    new Set()
  );
  const previousIssueIdForAutoExpandRef = useRef<string | null>(null);

  // State for collapsible Info section (General Info + Primary Contacts together)
  // âœ… Initialize based on generalInfoPreference from localStorage
  const [expandedInfoSection, setExpandedInfoSection] = useState<Set<string>>(
    () => {
      const generalInfoPref = localStorage.getItem(
        'slideout_generalInfo_expanded'
      );
      const shouldExpandGeneralInfo = generalInfoPref !== 'false'; // Default to true if not set

      // If user preference is to expand General Info, initialize with all phases
      // Otherwise, initialize with empty set (all collapsed)
      if (shouldExpandGeneralInfo) {
        const initialSet = new Set(initialExpandedPhases);
        return initialSet;
      } else {
        return new Set(); // All General Info sections collapsed by default
      }
    }
  );

  // Initialize expanded phases when the card or navigation context changes
  // This prevents accordions from collapsing on background data refreshes
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const currentIssueId = card?.issueId ?? null;
    const currentNavType = navigationContext.type || null;

    const issueChanged =
      currentIssueId !== null && currentIssueId !== previousIssueIdRef.current;
    const navTypeChanged = currentNavType !== previousNavTypeRef.current;

    // âœ… FIX: Check if initialExpandedPhases changed (e.g., from [] to ["phase-id"] on first load)
    // BUT only for initial population, not for user modifications
    const currentExpandedArray = Array.from(customExpandedPhases).sort();
    const newExpandedArray = [...initialExpandedPhases].sort();

    // âœ… IMPORTANT: Detect two scenarios:
    // 1. First load: coming from EMPTY state
    // 2. Navigation: phase IDs changed (different RSD has different phase IDs)
    const isComingFromEmpty = currentExpandedArray.length === 0;
    const phaseIdsChanged =
      !isComingFromEmpty &&
      newExpandedArray.length > 0 &&
      currentExpandedArray.length > 0 &&
      !currentExpandedArray.some(id => newExpandedArray.includes(id)); // None of the old IDs exist in new

    const expandedPhasesChanged =
      (isComingFromEmpty && newExpandedArray.length > 0) || // First load
      phaseIdsChanged; // Or navigation to new RSD with different phase IDs

    // Update refs for next run
    previousIssueIdRef.current = currentIssueId;
    previousNavTypeRef.current = currentNavType;

    // âœ… FIX: Apply initialExpandedPhases in these scenarios:
    // 1. Issue changed (real navigation to new RSD)
    // 2. Navigation type changed (chevron/timeline click)
    // 3. First load (coming from empty state)
    // 4. Navigation complete (phase IDs changed - new RSD has different phase IDs)
    //
    // This preserves user's manually opened phases during seamless refreshes,
    // but correctly updates when navigating to a completely different RSD
    const shouldApply = issueChanged || navTypeChanged || expandedPhasesChanged;

    // Only reset expanded phases on real navigation events or card changes.
    // For seamless data refreshes (same card + same nav type + same phases), keep user state.
    if (!shouldApply) {
      return;
    }

    // âœ… NEW: Only update if this is NOT a chevron navigation with closed preference
    const accordionPreference = localStorage.getItem(
      'slideout_accordion_preference'
    );
    const isChevronNav = navigationContext.type === 'chevron';

    // If chevron navigation AND user wants accordions closed â†’ Don't reset
    if (isChevronNav && accordionPreference === 'closed') {
      setCustomExpandedPhases(new Set([]));
      localStorage.setItem('slideout_expanded_phases', JSON.stringify([]));
      return;
    }

    // Otherwise, respect initialExpandedPhases (timeline click, etc.)
    if (initialExpandedPhases.length > 0) {
      const newExpandedSet = new Set<string>(initialExpandedPhases);
      setCustomExpandedPhases(newExpandedSet);

      // âœ… Save initial expanded phases to localStorage
      // This ensures the preference persists even if user doesn't manually toggle
      localStorage.setItem(
        'slideout_expanded_phases',
        JSON.stringify(initialExpandedPhases)
      );
    } else {
      // If initialExpandedPhases is empty (user wants closed) â†’ Set to empty
      setCustomExpandedPhases(new Set([]));
      localStorage.setItem('slideout_expanded_phases', JSON.stringify([]));
    }

    // Don't reset expandedInfoSection here - it's managed by toggleInfoSection and localStorage
  }, [
    card?.issueId,
    isOpen,
    propReleasePhaseId,
    initialExpandedPhases,
    navigationContext.type
  ]); // Add dependencies

  // âœ… NEW: Update expandedInfoSection when initialExpandedPhases changes (for General Info sections)
  // This handles the same first-load issue as the accordion phases
  useEffect(() => {
    const generalInfoPref = localStorage.getItem(
      'slideout_generalInfo_expanded'
    );
    const shouldExpandGeneralInfo = generalInfoPref !== 'false'; // Default to true

    if (!isOpen) {
      return;
    }

    // Handle PHASED moments: Use initialExpandedPhases
    if (
      shouldExpandGeneralInfo &&
      hasPhases &&
      initialExpandedPhases.length > 0
    ) {
      const currentInfoArray = Array.from(expandedInfoSection).sort();
      const newInfoArray = [...initialExpandedPhases].sort();

      // âœ… IMPORTANT: Detect two scenarios:
      // 1. First load: coming from EMPTY state
      // 2. Navigation: phase IDs changed (different RSD has different phase IDs)
      const isComingFromEmpty = currentInfoArray.length === 0;
      const phaseIdsChanged =
        !isComingFromEmpty &&
        newInfoArray.length > 0 &&
        currentInfoArray.length > 0 &&
        !currentInfoArray.some(id => newInfoArray.includes(id)); // None of the old IDs exist in new

      const infoSectionChanged =
        (isComingFromEmpty && newInfoArray.length > 0) || // First load
        phaseIdsChanged; // Or navigation to new RSD

      if (infoSectionChanged) {
        const newInfoSet = new Set(initialExpandedPhases);
        setExpandedInfoSection(newInfoSet);
      }
    }
    // Handle NON-PHASED moments: Use card?.issueId as momentId
    else if (!hasPhases && card?.issueId) {
      const momentId = card.issueId;
      const hasGeneralInfo = expandedInfoSection.has(momentId);

      if (shouldExpandGeneralInfo && !hasGeneralInfo) {
        setExpandedInfoSection(prev => {
          const newSet = new Set(prev);
          newSet.add(momentId);
          return newSet;
        });
      } else if (!shouldExpandGeneralInfo && hasGeneralInfo) {
        setExpandedInfoSection(prev => {
          const newSet = new Set(prev);
          newSet.delete(momentId);
          return newSet;
        });
      }
    }
  }, [initialExpandedPhases, isOpen, hasPhases, card?.issueId]); // React to changes

  // âœ… NEW: Sync isNonPhasedAccordionExpanded for non-phased moments
  useEffect(() => {
    const accordionPref = localStorage.getItem('slideout_accordion_preference');
    const shouldExpand = accordionPref !== 'closed'; // Default to open

    if (!isOpen) {
      return;
    }

    // Only apply to non-phased moments
    if (!hasPhases) {
      if (shouldExpand !== isNonPhasedAccordionExpanded) {
        setIsNonPhasedAccordionExpanded(shouldExpand);
      } else {
      }
    }
  }, [isOpen, card?.issueId, hasPhases]); // React to card changes

  // State for add contact popup
  const [isPrimaryContactPopupOpen, setIsPrimaryContactPopupOpen] =
    useState(false);
  const [currentPhaseIdForContact, setCurrentPhaseIdForContact] = useState<
    string | null
  >(null);

  // State for non-phased sections collapse (controls both General Info and Primary Contacts)
  // Initialize from localStorage, default to true
  const [isGeneralInfoExpanded, setIsGeneralInfoExpanded] = useState(() => {
    const stored = localStorage.getItem('slideout_generalInfo_expanded');
    const shouldExpand = stored !== null ? stored === 'true' : true;
    return shouldExpand;
  });

  // âœ… NEW: Separate state for non-phased moment accordion (outer accordion)
  // This is different from isGeneralInfoExpanded (which controls the General Info section inside)
  const [isNonPhasedAccordionExpanded, setIsNonPhasedAccordionExpanded] =
    useState(() => {
      const accordionPref = localStorage.getItem(
        'slideout_accordion_preference'
      );
      const shouldExpand = accordionPref !== 'closed'; // Default to open
      return shouldExpand;
    });

  // State for additional collapsible sections
  // Initialize from localStorage, default to true (expanded)
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(() => {
    const stored = localStorage.getItem('slideout_overview_expanded');
    return stored !== null ? stored === 'true' : true;
  });
  const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(() => {
    // âœ… PRIORITY 1: Feature click should always open Features accordion
    if (navigationContext.type === 'feature-click') {
      return true;
    }
    // Otherwise check localStorage or default to closed
    const stored = localStorage.getItem('slideout_features_expanded');
    return stored !== null ? stored === 'true' : true;
  });
  const [isDecisionsExpanded, setIsDecisionsExpanded] = useState(true);
  const [isDACIExpanded, setIsDACIExpanded] = useState(true);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] =
    useState(true);
  const [isMilestonesExpanded, setIsMilestonesExpanded] = useState(true);
  // Add Phase dropdown state (Price Changes and Offers)
  const [addPhaseDropdownOpen, setAddPhaseDropdownOpen] = useState(false);
  const [addPhaseLoading, setAddPhaseLoading] = useState(false);
  const addPhaseButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isChangeLogExpanded, setIsChangeLogExpanded] = useState(() => {
    const stored = localStorage.getItem(
      SLIDEOUT_STORAGE_KEYS.changeLogExpanded
    );
    return stored !== null ? stored === 'true' : true;
  });
  const [isPrinting, setIsPrinting] = useState(false);

  // Reset all accordions to expanded state when slideout opens with a new card
  // or navigation context changes. Do NOT run this on seamless data refreshes.
  useEffect(() => {
    if (!isOpen || !card?.issueId) {
      return;
    }

    const currentIssueId = card.issueId;
    const currentNavType = navigationContext.type || null;

    const issueChanged = currentIssueId !== previousIssueIdForResetRef.current;
    const navTypeChanged =
      currentNavType !== previousNavTypeForResetRef.current;

    // Update refs for next run
    previousIssueIdForResetRef.current = currentIssueId;
    previousNavTypeForResetRef.current = currentNavType;

    // Only reset accordions when we really changed cards or nav type.
    // This respects user-opened/closed accordions across soft refreshes.
    if (!issueChanged && !navTypeChanged) {
      return;
    }

    // âœ… NEW: Check global accordion preference for chevron navigation
    const accordionPreference = localStorage.getItem(
      'slideout_accordion_preference'
    );
    const isChevronNav = navigationContext.type === 'chevron';

    // Reset non-phased sections
    // For isGeneralInfoExpanded, respect localStorage value
    const storedGeneralInfoExpanded = localStorage.getItem(
      'slideout_generalInfo_expanded'
    );

    // âœ… For chevron nav with closed preference â†’ Force close
    let generalInfoExpanded;
    if (isChevronNav && accordionPreference === 'closed') {
      generalInfoExpanded = false;
    } else {
      generalInfoExpanded =
        storedGeneralInfoExpanded !== null
          ? storedGeneralInfoExpanded === 'true'
          : true;
    }
    setIsGeneralInfoExpanded(generalInfoExpanded);

    // For isOverviewExpanded, respect localStorage value
    // BUT: If there are alerts in overview section, always expand it
    const storedOverviewExpanded = localStorage.getItem(
      'slideout_overview_expanded'
    );
    let overviewExpanded =
      storedOverviewExpanded !== null
        ? storedOverviewExpanded === 'true'
        : true;

    // Check if there are alerts that require overview to be expanded
    // We'll check this in the auto-expand effect, but set initial state here
    setIsOverviewExpanded(overviewExpanded);

    // âœ… For Features: Check navigation context first, then localStorage
    if (navigationContext.type === 'feature-click') {
      // PRIORITY 1: Feature click always opens Features accordion
      setIsFeaturesExpanded(true);
    } else {
      // Otherwise respect localStorage preference
      const storedFeaturesExpanded = localStorage.getItem(
        'slideout_features_expanded'
      );
      const featuresExpanded =
        storedFeaturesExpanded !== null
          ? storedFeaturesExpanded === 'true'
          : true;
      setIsFeaturesExpanded(featuresExpanded);
    }

    setIsDecisionsExpanded(true);
    setIsDACIExpanded(true);
    setIsAdditionalInfoExpanded(true);
    setIsChangeLogExpanded(true);
    localStorage.setItem(SLIDEOUT_STORAGE_KEYS.changeLogExpanded, 'true');

    // Reset phased sections if applicable
    // âœ… NOTE: This is now partially handled by the previous useEffect
    // But we keep this for backwards compatibility
    if (initialExpandedPhases.length > 0) {
      const newExpandedSet = new Set<string>(initialExpandedPhases);
      setCustomExpandedPhases(newExpandedSet);

      // For expandedInfoSection, apply the same global localStorage setting to ALL phases
      const storedGeneralInfoExpandedInner = localStorage.getItem(
        'slideout_generalInfo_expanded'
      );
      const generalInfoExpandedInner =
        storedGeneralInfoExpandedInner !== null
          ? storedGeneralInfoExpandedInner === 'true'
          : true;

      const infoSectionSet = new Set<string>();
      if (generalInfoExpandedInner) {
        // If expanded, add all phases to the set
        initialExpandedPhases.forEach(phaseId => {
          infoSectionSet.add(phaseId);
        });
      }
      // If collapsed, leave the set empty (no phases expanded)
      setExpandedInfoSection(infoSectionSet);
    }

    // For non-phased cards, also ensure the info section is expanded/collapsed based on generalInfoExpanded
    if (!hasPhases) {
      if (generalInfoExpanded) {
        setExpandedInfoSection(new Set([card.issueId]));
      } else {
        setExpandedInfoSection(new Set());
      }
    }
  }, [
    isOpen,
    card?.issueId,
    hasPhases,
    navigationContext,
    initialExpandedPhases
  ]);

  // Auto-expand sections that contain highlighted fields when slideout opens
  useEffect(() => {
    if (!isOpen || !card?.issueId) {
      // Reset tracking when slideout closes
      if (!isOpen) {
        autoExpandedSectionsRef.current.clear();
        previousIssueIdForAutoExpandRef.current = null;
      }
      return;
    }

    const currentIssueId = card.issueId;

    // Reset tracking when issue changes - this ensures we can auto-expand again for new issues
    if (previousIssueIdForAutoExpandRef.current !== currentIssueId) {
      autoExpandedSectionsRef.current.clear();
      previousIssueIdForAutoExpandRef.current = currentIssueId;
    }

    // Function to check and expand sections with alerts
    const checkAndExpandSections = () => {
      // Verify we're still on the same issue
      if (!isOpen || !card?.issueId || card.issueId !== currentIssueId) {
        return false;
      }

      if (!alert) {
        return false; // Alerts not loaded yet
      }

      if (!alert.issues || alert.issues.length === 0) {
        return false;
      }

      // Get active issues (not dismissed)
      const activeIssues = alert.issues.filter(
        issue => !alert.dismissedIssueKeys.includes(issue.issueKey)
      );

      if (activeIssues.length === 0) {
        return true; // No alerts, but alerts are loaded
      }

      // Determine which sections have alerts
      const sectionsWithAlerts = new Set<'overview' | 'general-info'>();
      // For general-info (phased): collect phase ids to expand; (non-phased): use currentIssueId
      const generalInfoExpandKeys = new Set<string>();

      const normalizePhaseNameForMatch = (name: string) =>
        (name || '')
          .replace(/_/g, ' ')
          .replace(/\s+/g, ' ')
          .toLowerCase()
          .trim();

      // Build phase lookup map: fieldRef uses "Private_Beta", phase.phaseName may be "Private Beta"
      const phaseNameToPhase = new Map<string, (typeof normalizedPhases)[0]>();
      normalizedPhases.forEach(phase => {
        const pn = phase.phaseName || '';
        const norm = normalizePhaseNameForMatch(pn);
        phaseNameToPhase.set(norm, phase);
        phaseNameToPhase.set(norm.replace(/\s+/g, '-'), phase);
        phaseNameToPhase.set(norm.replace(/\s+/g, ''), phase);
        phaseNameToPhase.set(pn.toLowerCase().replace(/\s/g, '_'), phase);
      });

      activeIssues.forEach(issue => {
        const mapping = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
        if (mapping && mapping.section) {
          if (
            mapping.section === 'overview' ||
            mapping.section === 'general-info'
          ) {
            sectionsWithAlerts.add(mapping.section);
          }
          // Collect keys for general-info: phased = phase id, non-phased = currentIssueId
          if (mapping.section === 'general-info') {
            if (
              normalizedPhases.length > 0 &&
              mapping.phaseName != null &&
              mapping.phaseName !== ''
            ) {
              const refNorm = normalizePhaseNameForMatch(mapping.phaseName);
              const slug = refNorm.replace(/\s+/g, '-');
              const noSpace = refNorm.replace(/\s+/g, '');
              const withUnderscore = refNorm.replace(/\s+/g, '_');
              const phase =
                phaseNameToPhase.get(refNorm) ??
                phaseNameToPhase.get(slug) ??
                phaseNameToPhase.get(noSpace) ??
                phaseNameToPhase.get(withUnderscore) ??
                normalizedPhases.find(
                  p =>
                    normalizePhaseNameForMatch(p.phaseName || '') === refNorm ||
                    (p.phaseName || '').toLowerCase() ===
                      (mapping.phaseName || '').toLowerCase()
                );
              if (phase?.id) {
                generalInfoExpandKeys.add(phase.id);
              }
            } else {
              generalInfoExpandKeys.add(currentIssueId);
            }
          }
        }
      });

      // If we have general-info alerts but no keys yet (e.g. phased match failed), fallback to currentIssueId for non-phased
      const hadPhasedGeneralInfoAlerts = activeIssues.some(issue => {
        const m = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
        return m?.section === 'general-info' && m?.phaseName;
      });
      if (
        sectionsWithAlerts.has('general-info') &&
        generalInfoExpandKeys.size === 0
      ) {
        generalInfoExpandKeys.add(currentIssueId);
      }

      // For phased general-info: only consider "already expanded" when we expanded with phase ids.
      // If we previously used fallback (currentIssueId) because normalizedPhases was empty, re-run expansion.
      const generalInfoHasPhaseIds =
        normalizedPhases.length > 0 &&
        Array.from(generalInfoExpandKeys).some(id =>
          normalizedPhases.some(p => p.id === id)
        );
      const generalInfoAlreadyExpanded =
        autoExpandedSectionsRef.current.has('general-info') &&
        (!hadPhasedGeneralInfoAlerts || generalInfoHasPhaseIds);

      // Auto-expand sections that have alerts
      sectionsWithAlerts.forEach(section => {
        const alreadyExpanded =
          section === 'general-info'
            ? generalInfoAlreadyExpanded
            : autoExpandedSectionsRef.current.has(section);

        if (section === 'overview') {
          if (!alreadyExpanded) {
            autoExpandedSectionsRef.current.add('overview');
            // Update localStorage FIRST to prevent reset effect from overriding
            localStorage.setItem('slideout_overview_expanded', 'true');
            // Force set to true
            setIsOverviewExpanded(true);
          } else {
            // Already auto-expanded once; do not overwrite state so we don't re-expand if the user has since collapsed
          }
        } else if (section === 'general-info') {
          // For phased alerts: wait for normalizedPhases before expanding (avoid wrong keys)
          const shouldExpandGeneralInfo =
            !alreadyExpanded &&
            (!hadPhasedGeneralInfoAlerts || generalInfoHasPhaseIds);
          if (shouldExpandGeneralInfo) {
            autoExpandedSectionsRef.current.add('general-info');
            localStorage.setItem('slideout_generalInfo_expanded', 'true');
            setExpandedInfoSection(prev => {
              const newSet = new Set(prev);
              generalInfoExpandKeys.forEach(k => newSet.add(k));
              return newSet;
            });
            if (normalizedPhases.length > 0) {
              setCustomExpandedPhases(prev => {
                const next = new Set(prev);
                generalInfoExpandKeys.forEach(k => next.add(k));
                return next;
              });
            }
            if (normalizedPhases.length === 0) {
              localStorage.setItem('slideout_accordion_preference', 'open');
              setIsNonPhasedAccordionExpanded(true);
            }
          } else {
            // Already auto-expanded once; do not overwrite state so we don't re-expand
            // if the user has since collapsed General Info or phase headers
          }
        }
      });

      return true; // Alerts processed
    };

    // Run immediately when alert is already loaded (e.g. same card re-open or alert loaded before effect)
    checkAndExpandSections();

    // Also run after delays so we expand when alert loads asynchronously
    const timer1 = setTimeout(() => {
      checkAndExpandSections();
    }, 100);

    const timer2 = setTimeout(() => {
      checkAndExpandSections();
    }, 800);

    const timer3 = setTimeout(() => {
      checkAndExpandSections();
    }, 2000);

    const timer4 = setTimeout(() => {
      checkAndExpandSections();
    }, 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [isOpen, card?.issueId, alert, normalizedPhases]);

  // Calculate if all sections are collapsed for spacing adjustment
  const areAllSectionsCollapsed = () => {
    const hasPhases = normalizedPhases.length > 0;
    const currentMomentId = card.issueId;

    if (hasPhases) {
      // For phased layout: General Info is keyed by phase id; collapsed when no phase has it expanded
      const generalInfoCollapsed = !phaseIds.some(id =>
        expandedInfoSection.has(id)
      );
      const allAdditionalCollapsed =
        !isOverviewExpanded &&
        !isDecisionsExpanded &&
        !isDACIExpanded &&
        !isAdditionalInfoExpanded &&
        !isChangeLogExpanded;
      return generalInfoCollapsed && allAdditionalCollapsed;
    } else {
      // For non-phased layout: check if General Info, Overview, Decisions, DACI, and Additional Info are all collapsed
      const generalInfoCollapsed = !expandedInfoSection.has(currentMomentId);
      const allAdditionalCollapsed =
        !isOverviewExpanded &&
        !isDecisionsExpanded &&
        !isDACIExpanded &&
        !isAdditionalInfoExpanded &&
        !isChangeLogExpanded;
      return generalInfoCollapsed && allAdditionalCollapsed;
    }
  };

  // Popup state management for DACI
  const [isDACIPopupOpen, setIsDACIPopupOpen] = useState(false);
  // Decision popup state moved to Decisions component

  // State to trigger milestone table re-renders when data changes
  const [milestoneUpdateTrigger, setMilestoneUpdateTrigger] = useState(0);

  const {
    refreshMomentNotifications,
    handleSeamlessDataRefresh,
    seamlessRefreshAndNotifications,
    handleSeamlessDataRefreshWithoutFeatures,
    seamlessRefreshWithoutFeatures
  } = useSlideoutSeamlessRefresh({
    issueId: card?.issueId,
    dispatch,
    alertsActions,
    setApiData,
    setDaciContacts,
    setMilestoneUpdateTrigger,
    featuresContainerRef
  });

  // Features section is rendered via FeaturesContainer

  const toggleGeneralInfo = () => {
    const newState = !isGeneralInfoExpanded;
    setIsGeneralInfoExpanded(newState);
    // Persist to localStorage
    localStorage.setItem('slideout_generalInfo_expanded', String(newState));

    // âœ… REMOVED: Don't modify accordion preference here
    // General Info toggle should only affect the inner section, not the outer accordion
  };

  // âœ… NEW: Toggle function for non-phased moment accordion (outer accordion)
  const toggleNonPhasedAccordion = () => {
    const newState = !isNonPhasedAccordionExpanded;
    setIsNonPhasedAccordionExpanded(newState);

    // Update accordion preference (affects next navigation)
    if (!newState) {
      localStorage.setItem('slideout_accordion_preference', 'closed');
    } else {
      localStorage.setItem('slideout_accordion_preference', 'open');
    }
  };

  const toggleOverview = () => {
    setIsOverviewExpanded(prev => {
      const newValue = !prev;
      // Persist to localStorage
      localStorage.setItem('slideout_overview_expanded', String(newValue));

      // If user manually collapses, remove from auto-expanded tracking
      if (!newValue && autoExpandedSectionsRef.current.has('overview')) {
        autoExpandedSectionsRef.current.delete('overview');
      }

      return newValue;
    });
  };

  const toggleFeatures = () => {
    setIsFeaturesExpanded(prev => {
      const newValue = !prev;
      // âœ… Persist to localStorage
      localStorage.setItem('slideout_features_expanded', String(newValue));

      return newValue;
    });
  };

  const toggleMilestones = () => {
    setIsMilestonesExpanded(prev => !prev);
  };

  // RTM options for Add Phase dropdown
  const RTM_OPTIONS = ['VIP', 'Mobile', 'ETLA', 'A.com Direct'];
  const availableRtmOptions = React.useMemo(() => {
    const usedRtms = new Set(normalizedPhases.map(p => p.phaseName));
    return RTM_OPTIONS.filter(option => !usedRtms.has(option));
  }, [normalizedPhases]);

  const handleAddPhase = async (rtm: string) => {
    setAddPhaseDropdownOpen(false);
    if (!card?.issueId) return;
    try {
      setAddPhaseLoading(true);
      const patchResponse = await addRtmPhase(card.issueId, rtm);

      await seamlessRefreshAndNotifications(patchResponse);
    } catch {
      ToastQueue.negative('Failed to add phase. Please try again.', {
        timeout: 5000
      });
    } finally {
      setAddPhaseLoading(false);
    }
  };

  const toggleDecisions = () => {
    setIsDecisionsExpanded(!isDecisionsExpanded);
  };

  const toggleDACI = () => {
    setIsDACIExpanded(!isDACIExpanded);
  };

  const toggleAdditionalInfo = () => {
    setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded);
  };

  const toggleChangeLog = () => {
    setIsChangeLogExpanded(prev => {
      const next = !prev;
      localStorage.setItem(
        SLIDEOUT_STORAGE_KEYS.changeLogExpanded,
        String(next)
      );
      return next;
    });
  };

  const handlePrintSlideout = async () => {
    if (!apiData || isPrinting) return;

    const previous = {
      activeSection,
      isCommentPanelOpen,
      customExpandedPhases: new Set(customExpandedPhases),
      expandedInfoSection: new Set(expandedInfoSection),
      isGeneralInfoExpanded,
      isNonPhasedAccordionExpanded,
      isOverviewExpanded,
      isFeaturesExpanded,
      isDecisionsExpanded,
      isDACIExpanded,
      isAdditionalInfoExpanded,
      isMilestonesExpanded,
      isChangeLogExpanded
    };
    const previousTitle = document.title;
    let printableAnchors: Array<{
      element: HTMLAnchorElement;
      href: string | null;
    }> = [];
    const generatedPrintAnchors: HTMLAnchorElement[] = [];
    const restoreAncestorChain: Array<() => void> = [];
    const printOnlyMvpLinks: HTMLAnchorElement[] = [];

    try {
      setIsPrinting(true);
      setActiveSection('view');
      dispatch(layoutActions.closeAiAssistant());
      setIsCommentPanelOpen(false);

      const allPhaseIds = normalizedPhases
        .map(phase => phase.id)
        .filter((id): id is string => Boolean(id));
      setCustomExpandedPhases(new Set(allPhaseIds));
      setExpandedInfoSection(
        new Set([...allPhaseIds, ...(card?.issueId ? [card.issueId] : [])])
      );
      setIsGeneralInfoExpanded(true);
      setIsNonPhasedAccordionExpanded(true);
      setIsOverviewExpanded(true);
      setIsFeaturesExpanded(true);
      setIsDecisionsExpanded(true);
      setIsDACIExpanded(true);
      setIsAdditionalInfoExpanded(true);
      setIsMilestonesExpanded(true);
      setIsChangeLogExpanded(true);

      // A field left open for editing (even untouched) would otherwise print its
      // editor chrome instead of read-only content. No-op if nothing is open.
      overviewErrorRef.current?.closeOpenEditors();

      const stamp = new Date().toISOString().slice(0, 10);
      const safeTitle = (displayTitle || card?.issueId || 'RSD')
        .replace(/[\\/:*?"<>|]/g, '_')
        .trim();
      document.title = `RSD_${safeTitle}_${stamp}`;

      // Allow React to mount every expanded section and feature group before printing.
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => window.setTimeout(resolve, 500));
        });
      });

      // PDF viewers do not run the app's click handlers. Ensure every rendered
      // anchor has a concrete href that Chrome can embed as a PDF annotation.
      const panel = document.querySelector<HTMLElement>(
        '[data-testid="slideout-panel"]'
      );

      // Chrome/Firefox silently drop PDF link annotations for any anchor
      // whose ancestor chain contains position:absolute/fixed, even when the
      // href is perfectly valid. This panel is portaled into the app's fixed
      // shell header, so walk up to <body> and: (1) hide every unrelated
      // sibling with display:none (removes them from flow entirely, so no
      // blank space is introduced), and (2) force position:static and clear
      // sizing constraints on each ancestor, so links inside the panel keep
      // real, clickable bounds in the exported PDF.
      if (panel) {
        let node: HTMLElement | null = panel;
        while (node && node !== document.body) {
          // Capture the current element in a block-scoped constant so the
          // restore closures below reference THIS element, not the shared
          // loop variable `node` (which ends up as document.body after the
          // loop). Without this, every restore would write to document.body
          // and leave the real ancestors stuck with print overrides.
          const currentNode: HTMLElement = node;
          const parent: HTMLElement | null = currentNode.parentElement;
          if (parent) {
            Array.from(parent.children).forEach(child => {
              if (child === currentNode) return;
              const sibling = child as HTMLElement;
              const prevCssText = sibling.style.cssText;
              sibling.style.setProperty('display', 'none', 'important');
              restoreAncestorChain.push(() => {
                sibling.style.cssText = prevCssText;
              });
            });
          }

          // The slideout panel itself already gets its sizing from the
          // .printMode class (which sets width: 100%); only strip its
          // position here and leave width/height to that class. Ancestors
          // above it just need to stop constraining/positioning content.
          const prevCssText = currentNode.style.cssText;
          currentNode.style.setProperty('position', 'static', 'important');
          currentNode.style.setProperty('transform', 'none', 'important');
          if (currentNode !== panel) {
            currentNode.style.setProperty('overflow', 'visible', 'important');
            currentNode.style.setProperty('height', 'auto', 'important');
            currentNode.style.setProperty('max-height', 'none', 'important');
            currentNode.style.setProperty('min-height', '0', 'important');
            currentNode.style.setProperty('width', 'auto', 'important');
            currentNode.style.setProperty('clip', 'auto', 'important');
            currentNode.style.setProperty('clip-path', 'none', 'important');
          }
          restoreAncestorChain.push(() => {
            currentNode.style.cssText = prevCssText;
          });

          node = parent;
        }
      }

      // Rich-text fields can contain URLs as plain text rather than anchors.
      // Turn those URLs into temporary anchors so the browser can preserve
      // them as PDF link annotations without changing the live slideout.
      if (panel) {
        const textNodes: Text[] = [];
        const urlPattern = /(?:https?:\/\/|www\.)[^\s<>"']+/i;
        const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT, {
          acceptNode: node => {
            const parent = node.parentElement;
            const text = node.nodeValue || '';
            if (
              !parent ||
              parent.closest(
                'a, script, style, textarea, input, [contenteditable="true"]'
              ) ||
              !urlPattern.test(text)
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        });

        let currentNode = walker.nextNode();
        while (currentNode) {
          textNodes.push(currentNode as Text);
          currentNode = walker.nextNode();
        }

        const globalUrlPattern = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
        textNodes.forEach(textNode => {
          const text = textNode.nodeValue || '';
          const fragment = document.createDocumentFragment();
          let cursor = 0;
          let match: RegExpExecArray | null;

          while ((match = globalUrlPattern.exec(text)) !== null) {
            const rawMatch = match[0];
            const url = rawMatch.replace(/[.,;!?]+$/, '');
            const trailingText = rawMatch.slice(url.length);

            fragment.appendChild(
              document.createTextNode(text.slice(cursor, match.index))
            );

            const anchor = document.createElement('a');
            anchor.href = url.startsWith('www.') ? `https://${url}` : url;
            anchor.textContent = url;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            anchor.setAttribute('data-generated-print-link', 'true');
            generatedPrintAnchors.push(anchor);
            fragment.appendChild(anchor);

            if (trailingText) {
              fragment.appendChild(document.createTextNode(trailingText));
            }
            cursor = match.index + rawMatch.length;
          }

          fragment.appendChild(document.createTextNode(text.slice(cursor)));
          textNode.parentNode?.replaceChild(fragment, textNode);
        });
      }

      // The Milestones MVP link has no href in the live DOM (see the data-mvp-link
      // comment in MilestoneMatrixFromAPI.tsx) — that's what stops the browser's
      // native anchor navigation from opening a duplicate tab alongside the
      // onClick scroll-to-Overview handler. A PDF viewer has no click handler to
      // fall back on, so restore a real href for the print pass only, and only
      // when the Overview MVP section actually exists in this card's already-
      // expanded DOM (it doesn't for every moment type/record — see
      // overviewFieldRenderPolicies.mvpStatus in overviewFieldConfig.ts). This
      // way a valid target becomes clickable in the PDF and a missing one stays
      // plain text, without ever touching the slideout's own href/onClick.
      if (panel?.querySelector('#mvp-section')) {
        panel
          .querySelectorAll<HTMLAnchorElement>('a[data-mvp-link="true"]')
          .forEach(element => {
            element.setAttribute('href', '#mvp-section');
            printOnlyMvpLinks.push(element);
          });
      }

      printableAnchors = Array.from(
        panel?.querySelectorAll<HTMLAnchorElement>('a[href]') || []
      ).map(element => ({
        element,
        href: element.getAttribute('href')
      }));
      printableAnchors.forEach(({ element, href }) => {
        if (!href || /^(#|mailto:|tel:)/i.test(href)) return;
        const normalized =
          !/^[a-z][a-z\d+.-]*:/i.test(href) &&
          !href.startsWith('/') &&
          (href.startsWith('www.') || href.includes('.'))
            ? `https://${href}`
            : new URL(href, window.location.href).href;
        element.setAttribute('href', normalized);
      });

      // Some Chromium versions return from window.print() before the print
      // preview has finished composing the document. Keep the normalized
      // anchors mounted until afterprint so PDF annotations are not lost.
      await new Promise<void>(resolve => {
        let resolved = false;
        const finish = () => {
          if (resolved) return;
          resolved = true;
          window.removeEventListener('afterprint', finish);
          resolve();
        };

        window.addEventListener('afterprint', finish, { once: true });
        window.print();
        window.setTimeout(finish, 1500);
      });
    } finally {
      printableAnchors.forEach(({ element, href }) => {
        if (href === null) element.removeAttribute('href');
        else element.setAttribute('href', href);
      });
      printOnlyMvpLinks.forEach(element => {
        element.removeAttribute('href');
      });
      generatedPrintAnchors.forEach(anchor => {
        anchor.replaceWith(document.createTextNode(anchor.textContent || ''));
      });
      restoreAncestorChain.forEach(restore => restore());
      document.title = previousTitle;
      setCustomExpandedPhases(previous.customExpandedPhases);
      setExpandedInfoSection(previous.expandedInfoSection);
      setIsGeneralInfoExpanded(previous.isGeneralInfoExpanded);
      setIsNonPhasedAccordionExpanded(previous.isNonPhasedAccordionExpanded);
      setIsOverviewExpanded(previous.isOverviewExpanded);
      setIsFeaturesExpanded(previous.isFeaturesExpanded);
      setIsDecisionsExpanded(previous.isDecisionsExpanded);
      setIsDACIExpanded(previous.isDACIExpanded);
      setIsAdditionalInfoExpanded(previous.isAdditionalInfoExpanded);
      setIsMilestonesExpanded(previous.isMilestonesExpanded);
      setIsChangeLogExpanded(previous.isChangeLogExpanded);
      setIsCommentPanelOpen(previous.isCommentPanelOpen);
      setActiveSection(previous.activeSection);
      if (previous.activeSection === 'ai') {
        dispatch(layoutActions.openAiAssistant());
      }
      setIsPrinting(false);
    }
  };

  // Same unsaved-changes guard as Clone: "Yes, Close" discards via
  // discardUnsavedDrafts() and proceeds, exactly like Clone/Escape/Close.
  const handleExportPdfClick = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      pendingCloseAction.current = () => {
        discardUnsavedDrafts();
        handlePrintSlideout();
      };
      return;
    }
    handlePrintSlideout();
  }, [
    discardUnsavedDrafts,
    hasUnsavedChanges,
    setShowUnsavedWarning,
    pendingCloseAction,
    handlePrintSlideout
  ]);

  // Handlers for opening popups from section headers
  const handleOpenDACIPopup = () => {
    setIsDACIPopupOpen(true);
  };

  const handleCloseDACIPopup = () => {
    setIsDACIPopupOpen(false);
  };

  // Handlers for primary contact popup
  const handleOpenPrimaryContactPopup = (phaseId?: string) => {
    setCurrentPhaseIdForContact(phaseId || null);
    setIsPrimaryContactPopupOpen(true);
  };

  const handleClosePrimaryContactPopup = () => {
    setIsPrimaryContactPopupOpen(false);
    setCurrentPhaseIdForContact(null);
  };

  // Handler for share link copy success
  const handleCopySuccess = () => {
    ToastQueue.positive('Link copied to clipboard!', { timeout: 3000 });
  };

  // Ref to access AdditionalInfo component's add functionality
  const additionalInfoRef = useRef<AdditionalInfoRef | null>(null);

  const handleOpenAdditionalInfoPopup = () => {
    if (additionalInfoRef.current) {
      additionalInfoRef.current.openAddPopup();
    }
  };

  // Features fetching/refresh logic handled within FeaturesContainer

  const addDecisionRef = useRef<(() => void) | null>(null);

  const handleOpenDecisionPopup = () => {
    // Use the new Decisions component's add function
    if (addDecisionRef.current) {
      addDecisionRef.current();
    }
  };

  // Decision popup close handler moved to Decisions component

  const togglePhase = (phaseId: string) => {
    setCustomExpandedPhases(prev => {
      const newSet = new Set(prev);
      const isCurrentlyExpanded = newSet.has(phaseId);

      // âœ… REMOVED ENFORCEMENT: Users can now close all phases if they want
      // No longer preventing closing the last open phase

      if (isCurrentlyExpanded) {
        newSet.delete(phaseId);
      } else {
        newSet.add(phaseId);

        // âœ… Auto-expand General Info inside this accordion based on user preference
        const generalInfoPref = localStorage.getItem(
          'slideout_generalInfo_expanded'
        );
        const shouldExpandGeneralInfo = generalInfoPref !== 'false'; // Default to true

        if (shouldExpandGeneralInfo) {
          // Expand General Info section inside this newly opened phase
          setExpandedInfoSection(prevInfo => {
            const newInfoSet = new Set(prevInfo);
            newInfoSet.add(phaseId);
            return newInfoSet;
          });
        }
      }

      // âœ… Save user's accordion preference to localStorage
      // This persists which accordions the user has expanded when navigating between RSDs
      const expandedArray = Array.from(newSet);
      localStorage.setItem(
        'slideout_expanded_phases',
        JSON.stringify(expandedArray)
      );

      // âœ… NEW: Save global accordion preference
      // If user closes all accordions â†’ Set preference to 'closed'
      // If user opens any accordion â†’ Set preference to 'open'
      if (expandedArray.length === 0) {
        localStorage.setItem('slideout_accordion_preference', 'closed');
      } else {
        localStorage.setItem('slideout_accordion_preference', 'open');
      }

      return newSet;
    });
  };

  const toggleInfoSection = (phaseId: string) => {
    setExpandedInfoSection(prev => {
      const newSet = new Set(prev);
      const wasCollapsed = !newSet.has(phaseId);

      if (newSet.has(phaseId)) {
        newSet.delete(phaseId);
        // Persist collapsed state to localStorage (global setting for all phases)
        localStorage.setItem('slideout_generalInfo_expanded', 'false');

        // If user manually collapses, remove from auto-expanded tracking
        if (autoExpandedSectionsRef.current.has('general-info')) {
          autoExpandedSectionsRef.current.delete('general-info');
        }
      } else {
        newSet.add(phaseId);
        // Persist expanded state to localStorage (global setting for all phases)
        localStorage.setItem('slideout_generalInfo_expanded', 'true');
        // If we're expanding the section, trigger milestone refresh
        if (wasCollapsed) {
          // console.log('ðŸ”„ Phase activated, refreshing milestones...');
          // Use setTimeout to ensure the accordion is expanded first
          setTimeout(() => {
            handleSeamlessDataRefresh();
          }, 100);
        }
      }
      return newSet;
    });
  };

  /** Expands collapsed sections so a field (by fieldRef) becomes visible before scrolling */
  const expandSectionForFieldRef = useCallback(
    (fieldRef: string, ruleId: string, issueId: string) => {
      const mapping = mapFieldRefToSelector(fieldRef, ruleId);
      if (!mapping) return;

      const section = mapping.section;

      // Ensure we're on the main view (Overview, Features, Milestones, General Info are there)
      setActiveSection('view');

      if (section === 'overview') {
        setIsOverviewExpanded(true);
      } else if (section === 'milestones') {
        if (normalizedPhases.length > 0) {
          // Phased: expand only the phase that matches fieldRef (e.g. GA.milestone.MVP.date)
          if (mapping.phaseName && mapping._phaseNameVariants) {
            const phaseNameVariants = mapping._phaseNameVariants;
            const phase = normalizedPhases.find(p => {
              const pName = (p.phaseName || '')
                .toLowerCase()
                .replace(/[\s_-]/g, '');
              return phaseNameVariants.some(
                v => pName === v.toLowerCase().replace(/[\s_-]/g, '')
              );
            });
            if (phase?.id) {
              setCustomExpandedPhases(prev => new Set(prev).add(phase.id));
              setExpandedInfoSection(prev => new Set(prev).add(phase.id));
            } else {
              // Fallback: expand all phases if phase name did not match
              setCustomExpandedPhases(prev => {
                const next = new Set(prev);
                phaseIds.forEach(id => next.add(id));
                return next;
              });
              setExpandedInfoSection(prev => {
                const next = new Set(prev);
                phaseIds.forEach(id => next.add(id));
                return next;
              });
            }
          } else {
            // Legacy fieldRef without phase prefix: expand all phases
            setCustomExpandedPhases(prev => {
              const next = new Set(prev);
              phaseIds.forEach(id => next.add(id));
              return next;
            });
            setExpandedInfoSection(prev => {
              const next = new Set(prev);
              phaseIds.forEach(id => next.add(id));
              return next;
            });
          }
        } else {
          // Non-phased: expand outer accordion so milestones content is visible
          setIsNonPhasedAccordionExpanded(true);
          setExpandedInfoSection(prev => new Set(prev).add(issueId));
        }
      } else if (section === 'general-info') {
        if (mapping.phaseName && mapping._phaseNameVariants) {
          // Phased: find phase id by matching phase name, expand phase row and General Info
          const phaseNameVariants = mapping._phaseNameVariants;
          const phase = normalizedPhases.find(p => {
            const pName = (p.phaseName || '')
              .toLowerCase()
              .replace(/[\s_-]/g, '');
            return phaseNameVariants.some(
              v => pName === v.toLowerCase().replace(/[\s_-]/g, '')
            );
          });
          if (phase?.id) {
            setCustomExpandedPhases(prev => new Set(prev).add(phase.id));
            setExpandedInfoSection(prev => new Set(prev).add(phase.id));
          }
        } else {
          // Non-phased: expand outer accordion and General Info
          setIsNonPhasedAccordionExpanded(true);
          setExpandedInfoSection(prev => new Set(prev).add(issueId));
        }
      }
    },
    [normalizedPhases, phaseIds]
  );

  /**
   * Focus a highlighted alert field and bring it into centered view.
   * Works for vertical slideout scroll + horizontal containers (e.g. milestone matrix).
   */
  const focusAlertFieldElement = useCallback(
    (issueId: string, fieldRef: string | null, ruleId: string): boolean => {
      const fieldRefKey = fieldRef ?? 'null';
      const candidates = Array.from(
        document.querySelectorAll(
          `[data-alert-issue-id="${issueId}"].alert-field-highlight`
        )
      ) as HTMLElement[];

      const target = candidates.find(
        el =>
          el.getAttribute('data-alert-field-ref') === fieldRefKey &&
          el.getAttribute('data-alert-rule-id') === ruleId
      );
      if (!target) return false;

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });

      // Restart animation for repeated clicks on the same alert.
      target.classList.remove('alert-focus-pulse');
      void target.offsetWidth;
      target.classList.add('alert-focus-pulse');
      // Duration must match the `alert-focus-pulse` animation in alert-field-highlight.scss (1.8s).
      window.setTimeout(() => {
        target.classList.remove('alert-focus-pulse');
      }, 1800);

      // Do NOT auto-clear the border highlight here. This click path only scrolls/pulses the
      // field the user clicked from the notification banner — the persistent border itself is
      // governed elsewhere (useFieldHighlight's reconciliation effect for general-info/overview,
      // MilestoneMatrixFromAPI/Features.tsx's declarative highlight for milestones/features) and
      // should stay visible until the underlying issue is actually resolved/dismissed, not
      // disappear a few seconds after the click. (Confirmed 2026-07-21: an earlier version of
      // this function force-cleared the border ~3s after the click for every section except
      // Features — that was wrong; it made the just-clicked-to field's highlight vanish while the
      // alert was still active.)

      return true;
    },
    []
  );

  /** Retry focus because some sections render asynchronously after expansion. */
  const focusAlertFieldElementWithRetry = useCallback(
    (issueId: string, fieldRef: string | null, ruleId: string) => {
      let attempt = 0;
      const maxAttempts = 8;

      const run = () => {
        if (focusAlertFieldElement(issueId, fieldRef, ruleId)) return;
        if (attempt >= maxAttempts) return;
        attempt += 1;
        window.setTimeout(run, 120 * attempt);
      };

      run();
    },
    [focusAlertFieldElement]
  );

  // Helper function to convert revenue impact boolean to "Yes"/"No" display value
  const getRevenueImpactDisplayValue = (
    revenueImpact: any,
    isPhased: boolean = true // Default to phased behavior (Yes) for backward compatibility
  ): string => {
    return revenueImpact !== undefined && revenueImpact !== null
      ? revenueImpact === true || revenueImpact === 'Yes'
        ? 'Yes'
        : 'No'
      : isPhased
      ? 'Yes' // Phased moments default to 'Yes' (GA/Public Beta have revenue impact)
      : 'No'; // Non-phased moments default to 'No' (most moments don't impact revenue)
  };

  // Helper functions to get data from API only
  // Centralized date formatting utility
  // Extracts and formats statusUpdatedAt without timezone conversion
  const formatStatusUpdatedDate = (
    statusUpdatedAt: string | null | undefined
  ): string => {
    if (!statusUpdatedAt) {
      return 'Unknown Date';
    }

    try {
      // Extract date without timezone conversion
      // If statusUpdatedAt is "2025-12-12" or "2025-12-12T00:00:00Z"
      // Just use the date part as-is
      const dateStr = statusUpdatedAt.split('T')[0]; // Get YYYY-MM-DD
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting status updated date:', error);
      return 'Unknown Date';
    }
  };

  const getGeneralInfo = (phaseId?: string) => {
    const data = getDataWithFallback() as any;

    if (!data) return {};

    if (!hasPhases || !phaseId) {
      // For non-phased layouts, include root-level fields like mirrorRelease, showOnRoadmap and momentType
      return {
        ...data.generalInfo,
        // mirrorRelease: data.mirrorRelease, // Include mirrorRelease from root level
        showOnRoadmap: data.showOnRoadmap, // Include showOnRoadmap from root level
        secondaryDateStatus: data.secondaryDateStatus, // Include secondaryDateStatus from root level
        momentType: data.momentType || data.generalInfo?.momentType // Include momentType for date field config
      };
    }

    const phase = normalizedPhases.find(p => p.id === phaseId);
    const generalInfo = phase?.generalInfo || data.generalInfo || {};

    // For phased layouts, include the phase ID in the generalInfo data
    // so the GeneralInfo component can use it for API calls
    // Also include momentType from root level for date field configuration
    // Include phase-level fields like showOnRoadmap, secondaryDateStatus, inheritMilestones
    return {
      ...generalInfo,
      id: phaseId,
      momentType: data.momentType || generalInfo.momentType, // Include momentType for date field config
      // Include phase-level fields (at phase root, not in generalInfo)
      showOnRoadmap: phase?.showOnRoadmap,
      secondaryDateStatus: phase?.secondaryDateStatus,
      inheritMilestones: phase?.inheritMilestones,
      status: phase?.status,
      statusUpdatedAt: phase?.statusUpdatedAt
    };
  };

  getCurrentTierRef.current = () =>
    getGeneralInfo()?.tier ||
    (card as any)?.generalInfo?.tier ||
    (card as any)?.tier ||
    '';

  const getPrimaryContacts = (phaseId?: string) => {
    const data = getDataWithFallback() as any;

    if (!data) return {};

    if (!hasPhases || !phaseId) {
      return data.primaryContacts || {};
    }

    const phase = normalizedPhases.find(p => p.id === phaseId);
    return phase?.primaryContacts || data.primaryContacts || {};
  };

  const getMilestones = (phaseId?: string) => {
    const data = getDataWithFallback() as any;

    if (!data) return {};

    let result;
    if (!hasPhases || !phaseId) {
      result = data.milestones || {};
    } else {
      const phase = normalizedPhases.find(p => p.id === phaseId);
      // For moment-level milestones (e.g. Price Changes and Offers), fall back to root milestones
      result = phase?.milestones || data.milestones || {};
    }

    // console.log('ðŸ” getMilestones Debug:', {

    return result;
  };

  // Helper function to get milestoneId from the new structure
  const getMilestoneId = (phaseId?: string) => {
    const data = getDataWithFallback() as any;

    // Try root level first
    if (data?.milestoneId) {
      return data.milestoneId;
    }

    // Try to extract from milestone data structure
    const milestoneData = getMilestones(phaseId);

    // For new array structure, get milestoneId from first array item
    if (
      Array.isArray(milestoneData) &&
      milestoneData.length > 0 &&
      milestoneData[0]?.milestoneId
    ) {
      return milestoneData[0].milestoneId;
    }

    // For nested array structure, get milestoneId from milestone data
    if (
      Array.isArray(milestoneData?.milestones) &&
      milestoneData.milestones.length > 0 &&
      milestoneData.milestones[0]?.milestoneId
    ) {
      return milestoneData.milestones[0].milestoneId;
    }

    // Fallback to milestoneData.milestoneId
    return milestoneData?.milestoneId;
  };

  // Helper function to get milestone array from new structure
  const getMilestoneArray = (phaseId?: string) => {
    const milestoneData = getMilestones(phaseId);

    // When milestones live at the root level (e.g. Price Changes and Offers):
    // releasePhases exist but each phase.milestones is null; the real data is
    // in data.milestones (top-level array identical to the non-phased structure).
    if (hasMomentLevelMilestones && !phaseId) {
      const rootMilestones = (getDataWithFallback() as any)?.milestones;
      if (Array.isArray(rootMilestones)) {
        // Return the inner milestones array, or [] if empty (all milestones deleted)
        return Array.isArray(rootMilestones[0]?.milestones)
          ? rootMilestones[0].milestones
          : [];
      }
    }

    // console.log('ðŸ” getMilestoneArray debug:', {

    // Special case for non-phased releases: Check if milestoneData itself is the array structure
    // This handles cases where getMilestones() returns the root milestones array directly
    if (
      !hasPhases &&
      Array.isArray(milestoneData) &&
      milestoneData.length > 0 &&
      milestoneData[0]?.milestones
    ) {
      const extractedMilestones = Array.isArray(milestoneData[0].milestones)
        ? milestoneData[0].milestones
        : [];

      // console.log(

      return extractedMilestones;
    }

    // Handle new array structure for phased releases
    if (Array.isArray(milestoneData?.milestones)) {
      // For non-phased releases, extract milestones from the first array item
      if (
        !hasPhases &&
        milestoneData.milestones.length > 0 &&
        milestoneData.milestones[0]?.milestones
      ) {
        const extractedMilestones = Array.isArray(
          milestoneData.milestones[0].milestones
        )
          ? milestoneData.milestones[0].milestones
          : [];

        // console.log(
        // 'âœ… Non-phased (nested): Extracted milestones from first array item:',

        return extractedMilestones;
      }
      // For phased releases, return the array structure as-is
      // console.log(

      return milestoneData.milestones;
    }

    // Handle legacy object structure - convert to array
    if (
      milestoneData?.milestones &&
      typeof milestoneData.milestones === 'object'
    ) {
      const objectValues = Object.values(milestoneData.milestones);
      // console.log('âœ… Legacy: Converted object to array:', objectValues.length);
      return objectValues;
    }

    // Fallback for direct array
    if (Array.isArray(milestoneData)) {
      // console.log('âœ… Direct array fallback:', milestoneData.length);
      return milestoneData;
    }

    // console.log('âŒ No milestones found, returning empty array');
    return [];
  };

  // Helper function to check if milestones should show Add button
  const shouldShowAddMilestoneButton = (phaseId?: string) => {
    // For moment-level milestones (e.g. Price Changes and Offers): milestoneAdded and
    // milestones array live at the root of the payload, not inside generalInfo or phases.
    if (hasMomentLevelMilestones && !phaseId) {
      const data = getDataWithFallback() as any;
      if (data?.milestoneAdded === true) return false;
      const milestoneArray = getMilestoneArray();
      if (milestoneArray && milestoneArray.length > 0) return false;
      return true;
    }

    // For non-phased releases
    if (!hasPhases) {
      const milestoneData = getMilestones();
      const milestoneArray = getMilestoneArray();

      // Don't show if milestoneAdded flag is true
      if (milestoneData?.milestoneAdded === true) {
        return false;
      }

      // Don't show if milestones exist (array has items)
      if (milestoneArray && milestoneArray.length > 0) {
        return false;
      }

      // Show button if milestones is null OR empty array
      return true;
    }

    // For phased releases, check at the phase level
    if (phaseId) {
      const phase = normalizedPhases.find(p => p.id === phaseId);
      const phaseMilestones = getReleasePhaseMilestones(phaseId);

      // Don't show if milestoneAdded flag is true at phase level
      if (phase?.milestoneAdded === true) {
        return false;
      }

      // Don't show if phase has milestones (array has items)
      if (phaseMilestones && phaseMilestones.length > 0) {
        return false;
      }

      // Show button if no milestones and no milestoneAdded flag
      return true;
    }

    // Fallback: check milestone data structure (legacy support)
    const milestoneData = getMilestones(phaseId);
    if (milestoneData?.milestoneAdded === true) {
      return false;
    }

    return true;
  };

  // Helper function to check if milestones are inherited
  const areMilestonesInherited = (phaseId?: string) => {
    // IMPORTANT: For non-phased releases, inheritance doesn't apply
    // Non-phased releases don't have inheritMilestones flag
    if (!hasPhases) {
      return false; // Non-phased releases don't have inheritance
    }

    // For phased releases, check inheritMilestones flag at the phase level
    if (phaseId) {
      const phase = normalizedPhases.find(p => p.id === phaseId);

      // Check inheritMilestones flag directly from phase data
      if (phase?.inheritMilestones !== undefined) {
        return phase.inheritMilestones;
      }
    }

    // Fallback: check milestone data structure (legacy support)
    const milestoneData = getMilestones(phaseId);
    if (milestoneData?.inheritMilestones !== undefined) {
      return milestoneData.inheritMilestones;
    }

    // Fallback to general info (for phased releases)
    const generalInfo = getGeneralInfo(phaseId);
    return generalInfo?.inheritMilestones || false;
  };

  // Helper function to get parent milestones for inheritance display
  const getParentMilestones = (phaseId?: string) => {
    // Parent milestones only exist for phased releases with inheritance
    if (!hasPhases || !areMilestonesInherited(phaseId)) {
      return [];
    }

    const milestoneArray = getMilestoneArray(phaseId);

    // Find parent moment milestones from the array (only for phased releases)
    const parentMilestones = milestoneArray.find(
      item => item.name === 'Parent Moment Milestones'
    );

    if (parentMilestones?.milestones) {
      return Array.isArray(parentMilestones.milestones)
        ? parentMilestones.milestones
        : [];
    }

    return [];
  };

  // Helper function to get release phase milestones
  const getReleasePhaseMilestones = (phaseId?: string) => {
    const milestoneArray = getMilestoneArray(phaseId);

    // console.log('ðŸ” getReleasePhaseMilestones debug:', {

    // Find release phase milestones from the array
    const releaseMilestones = milestoneArray.find(
      item => item.name === 'Release Phase Milestones'
    );

    if (releaseMilestones?.milestones) {
      return Array.isArray(releaseMilestones.milestones)
        ? releaseMilestones.milestones
        : [];
    }

    // Enhanced fallback logic:
    // 1. First try to find any milestone container with milestones (even if name is null)
    // 2. Then filter out parent milestones
    const fallbackMilestones = milestoneArray
      .filter(item => item.name !== 'Parent Moment Milestones')
      .flatMap(item => item.milestones || []);

    // console.log('ðŸ” Using fallback milestones:', {

    return fallbackMilestones;
  };

  // Local state to track whether user added a second milestone table
  const [userMilestoneAdded, setUserMilestoneAdded] = useState(false);

  /** Primary contact row from API (may use `mail` or `email`). */
  type PrimaryContactApiRow = ContactEmailSource & {
    id?: string;
    uid?: string;
    name?: string;
    status?: string;
  };

  // Flatten contacts for PrimaryContacts
  const flattenPrimaryContacts = (phaseId?: string) => {
    const contacts: any[] = [];
    const pc = getPrimaryContacts(phaseId);

    // console.log('ðŸ” flattenPrimaryContacts Debug:', {

    if (!pc) return contacts;
    const pushPrimary = (c: PrimaryContactApiRow, role: string) => {
      const { mail, email, ...rest } = c;
      contacts.push({
        ...rest,
        role,
        email: resolveContactEmail({ email, mail })
      });
    };
    if (pc.productMarketingManagers) {
      pc.productMarketingManagers.forEach((c: PrimaryContactApiRow) =>
        pushPrimary(c, 'PROD MRKT MANAGER')
      );
    }
    if (pc.productManagers) {
      pc.productManagers.forEach((c: PrimaryContactApiRow) =>
        pushPrimary(c, 'PRODUCT MANAGER')
      );
    }
    if (pc.programManagers) {
      pc.programManagers.forEach((c: PrimaryContactApiRow) =>
        pushPrimary(c, 'PROGRAM MANAGER')
      );
    }

    // console.log('âœ… flattenPrimaryContacts Result:', {

    return contacts;
  };

  // Get current primary contacts data from API only
  const getCurrentPrimaryContactsData = () => {
    const data = getDataWithFallback() as any;
    if (!data) {
      return {
        productMarketingManagers: [],
        productManagers: [],
        programManagers: []
      };
    }
    return (
      data.primaryContacts || {
        productMarketingManagers: [],
        productManagers: [],
        programManagers: []
      }
    );
  };

  // Convert flat contacts to API format while preserving existing data
  // NOTE: This function is now mainly used by legacy code and the unified manager
  const convertContactsToApiFormat = (
    flatContacts: any[],
    updatedRole?: string
  ) => {
    // Start with current API data to preserve existing contacts
    const currentData = getCurrentPrimaryContactsData();

    const apiFormat = {
      productMarketingManagers: [
        ...(currentData.productMarketingManagers || [])
      ],
      productManagers: [...(currentData.productManagers || [])],
      programManagers: [...(currentData.programManagers || [])]
    };

    // If we're updating a specific role, replace only that section
    if (updatedRole) {
      // Clear the specific role section we're updating
      switch (updatedRole) {
        case 'PROD MRKT MANAGER':
          apiFormat.productMarketingManagers = [];
          break;
        case 'PRODUCT MANAGER':
          apiFormat.productManagers = [];
          break;
        case 'PROGRAM MANAGER':
          apiFormat.programManagers = [];
          break;
      }

      // Add only contacts with the updated role
      let contactsAdded = 0;
      flatContacts.forEach(contact => {
        if (contact.role === updatedRole) {
          const { role, ...contactData } = contact;
          switch (role) {
            case 'PROD MRKT MANAGER':
              apiFormat.productMarketingManagers.push(contactData);
              contactsAdded++;
              break;
            case 'PRODUCT MANAGER':
              apiFormat.productManagers.push(contactData);
              contactsAdded++;
              break;
            case 'PROGRAM MANAGER':
              apiFormat.programManagers.push(contactData);
              contactsAdded++;
              break;
          }
        }
      });
    } else {
      // Full update - replace all with current flat contacts
      apiFormat.productMarketingManagers = [];
      apiFormat.productManagers = [];
      apiFormat.programManagers = [];

      flatContacts.forEach(contact => {
        const { role, ...contactData } = contact;
        switch (role) {
          case 'PROD MRKT MANAGER':
            apiFormat.productMarketingManagers.push(contactData);
            break;
          case 'PRODUCT MANAGER':
            apiFormat.productManagers.push(contactData);
            break;
          case 'PROGRAM MANAGER':
            apiFormat.programManagers.push(contactData);
            break;
        }
      });
    }

    return apiFormat;
  };

  // Update primary contacts via API with incremental updates
  const updatePrimaryContactsApi = async (
    updatedContacts: any[],
    updatedRole?: string
  ) => {
    if (!card?.issueId) {
      return;
    }

    try {
      const apiFormat = convertContactsToApiFormat(
        updatedContacts,
        updatedRole
      );
      const response = await updateMomentPrimaryContacts(
        card.issueId,
        apiFormat
      );
    } catch (error) {}
  };

  // Update primary contacts for a specific phase in phased releases
  const updatePhasedPrimaryContactsApi = async (
    phaseId: string,
    updatedContacts: any[],
    updatedRole?: string
  ) => {
    if (!card?.issueId) {
      return;
    }

    try {
      // Find the phase to get its name
      const phase = normalizedPhases.find(p => p.id === phaseId);
      if (!phase) {
        return;
      }

      // Convert contacts to API format
      const apiFormat = convertContactsToApiFormat(
        updatedContacts,
        updatedRole
      );

      // Use the phased utility to send only the specific phase data
      await updatePhasedPrimaryContacts(
        card.issueId,
        phaseId,
        apiFormat,
        response => {},
        error => {}
      );
    } catch (error) {}
  };

  // âœ… CENTRALIZED: Get moment name and type from apiData (single source of truth)
  // These values are at the ROOT level of ALL moment type payloads
  const momentName = apiData?.name || card?.name || card?.title || 'Unknown';
  const momentType =
    apiData?.momentType || card?.momentType || resolveMomentType() || 'Unknown';

  // â”€â”€ Accordion date chip helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const ACCORDION_MONTH_ABBREVS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  // Month-only display: "Apr 2025" â†’ "Apr"
  const formatDateChipMonth = (dateStr: string): string => {
    if (!dateStr) return '--';
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})/);
    if (isoMatch)
      return ACCORDION_MONTH_ABBREVS[parseInt(isoMatch[2], 10) - 1] || '--';
    const nameMatch = dateStr.match(/^([A-Za-z]{3})/);
    if (nameMatch) return nameMatch[1];
    return '--';
  };

  // Full specific-date display: "2025-04-01" â†’ "Apr 1"
  const formatDateChipFull = (dateStr: string): string => {
    if (!dateStr) return '--';
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const month = ACCORDION_MONTH_ABBREVS[parseInt(isoMatch[2], 10) - 1];
      const day = parseInt(isoMatch[3], 10);
      return `${month} ${day}`;
    }
    return formatDateChipMonth(dateStr);
  };

  // Derives all CalendarRange default/restriction props from the current raw dates object + field name.
  const getAccordionCalendarProps = (
    dates: any,
    fieldName: string
  ): {
    defaultSpecialValue: 'TBD' | 'N/A' | null;
    defaultDate: CalendarDate | undefined;
    defaultMonth: { month: string; year: number } | undefined;
    defaultQuarter: { quarter: number; year: number } | undefined;
    disableNA: boolean;
    disableTBD: boolean;
  } => {
    const fieldMap: Record<
      string,
      { date: string; month: string; quarter: string; status: string }
    > = {
      marketingMoment: {
        date: 'targetStart',
        month: 'targetMonth',
        quarter: 'targetQuarter',
        status: 'targetDateStatus'
      },
      releaseDate: {
        date: 'releaseDate',
        month: 'releaseMonth',
        quarter: 'releaseQuarter',
        status: 'releaseDateStatus'
      },
      customerNotificationDate: {
        date: 'customerNotificationDate',
        month: 'customerNotificationMonth',
        quarter: 'customerNotificationQuarter',
        status: 'customerNotificationDateStatus'
      },
      effectiveDate: {
        date: 'effectiveDate',
        month: 'effectiveMonth',
        quarter: 'effectiveQuarter',
        status: 'effectiveDateStatus'
      }
    };
    // Matches GeneralInfo: N/A disabled for marketingMoment and customerNotificationDate
    const disableNA =
      fieldName === 'marketingMoment' ||
      fieldName === 'customerNotificationDate';
    const disableTBD = false;
    const empty = {
      defaultSpecialValue: null as null,
      defaultDate: undefined,
      defaultMonth: undefined,
      defaultQuarter: undefined,
      disableNA,
      disableTBD
    };
    const keys = fieldMap[fieldName];
    if (!keys || !dates) return empty;

    // Real date values take priority over status â€” same order as GeneralInfo.getTargetDate().
    const monthStr = dates[keys.month];
    if (monthStr) {
      const m = String(monthStr).match(/^([A-Za-z]{3})\s+(\d{4})$/);
      if (m)
        return {
          ...empty,
          defaultMonth: { month: m[1], year: parseInt(m[2], 10) }
        };
    }

    const quarterStr = dates[keys.quarter];
    if (quarterStr) {
      const q = String(quarterStr).match(/^Q(\d)\s+(\d{4})$/);
      if (q)
        return {
          ...empty,
          defaultQuarter: {
            quarter: parseInt(q[1], 10),
            year: parseInt(q[2], 10)
          }
        };
    }

    const dateStr = dates[keys.date];
    if (dateStr) {
      try {
        return { ...empty, defaultDate: parseDate(String(dateStr)) };
      } catch {
        // invalid date string â€” fall through
      }
    }

    // Only use status (TBD/N/A) when no real date value is present
    const status = dates[keys.status];
    if (status === 'TBD' || status === 'N/A') {
      return { ...empty, defaultSpecialValue: status as 'TBD' | 'N/A' };
    }

    return empty;
  };

  const getAccordionDateDisplay = (
    mt: string,
    dates: any
  ): { fieldName: string; displayText: string } => {
    const getFromKeys = (
      dateKey: string,
      monthKey: string,
      quarterKey: string,
      statusKey: string
    ): string | null => {
      if (!dates) return null;
      // Real date values take priority â€” matches GeneralInfo.getTargetDate() order.
      // Status (TBD/N/A) is only used when no actual date is present.
      if (dates[monthKey]) return formatDateChipMonth(dates[monthKey]);
      if (dates[quarterKey]) return String(dates[quarterKey]).split(' ')[0];
      if (dates[dateKey]) return formatDateChipFull(dates[dateKey]);
      const st = dates[statusKey];
      if (st === 'TBD' || st === 'N/A') return st;
      return null;
    };

    if (mt === MOMENT_TYPE_PRODUCT_RELEASE_WITHOUT_MARKETING) {
      return {
        fieldName: 'releaseDate',
        displayText:
          getFromKeys(
            'releaseDate',
            'releaseMonth',
            'releaseQuarter',
            'releaseDateStatus'
          ) || '--'
      };
    }
    if (usesPcoStylePhasedDates(mt)) {
      return {
        fieldName: 'customerNotificationDate',
        displayText:
          getFromKeys(
            'customerNotificationDate',
            'customerNotificationMonth',
            'customerNotificationQuarter',
            'customerNotificationDateStatus'
          ) || '--'
      };
    }
    // Default: targetStart (marketing date), fallback to customerNotificationDate
    const targetText = getFromKeys(
      'targetStart',
      'targetMonth',
      'targetQuarter',
      'targetDateStatus'
    );
    if (targetText)
      return { fieldName: 'marketingMoment', displayText: targetText };
    const notifText = getFromKeys(
      'customerNotificationDate',
      'customerNotificationMonth',
      'customerNotificationQuarter',
      'customerNotificationDateStatus'
    );
    return { fieldName: 'marketingMoment', displayText: notifText || '--' };
  };

  // Returns the dates patch object AND the specific API field + value set (for audit).
  const buildDatesPatchObjectForAccordion = (
    fieldName: string,
    calendarValue: any,
    existingDates: any
  ): { datesObject: any; newApiField: string; newDateStr: string } => {
    const fieldMap: Record<
      string,
      { date: string; month: string; quarter: string; status: string }
    > = {
      marketingMoment: {
        date: 'targetStart',
        month: 'targetMonth',
        quarter: 'targetQuarter',
        status: 'targetDateStatus'
      },
      releaseDate: {
        date: 'releaseDate',
        month: 'releaseMonth',
        quarter: 'releaseQuarter',
        status: 'releaseDateStatus'
      },
      customerNotificationDate: {
        date: 'customerNotificationDate',
        month: 'customerNotificationMonth',
        quarter: 'customerNotificationQuarter',
        status: 'customerNotificationDateStatus'
      },
      effectiveDate: {
        date: 'effectiveDate',
        month: 'effectiveMonth',
        quarter: 'effectiveQuarter',
        status: 'effectiveDateStatus'
      }
    };
    const keys = fieldMap[fieldName];
    if (!keys)
      return {
        datesObject: existingDates || {},
        newApiField: fieldName,
        newDateStr: ''
      };

    const MN = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const QM: Record<string, string> = {
      '12': 'Q1',
      '01': 'Q1',
      '02': 'Q1',
      '03': 'Q2',
      '04': 'Q2',
      '05': 'Q2',
      '06': 'Q3',
      '07': 'Q3',
      '08': 'Q3',
      '09': 'Q4',
      '10': 'Q4',
      '11': 'Q4'
    };

    let newField = '';
    let dateStr = '';

    if (calendarValue.type === 'months' && calendarValue.months?.length > 0) {
      let mv = calendarValue.months[0];
      if (mv.match(/^\d{4}-\d{2}$/)) {
        const yr = mv.substring(0, 4);
        const mn = parseInt(mv.substring(5, 7), 10) - 1;
        mv = `${MN[mn]} ${yr}`;
      }
      newField = keys.month;
      dateStr = mv;
    } else if (
      calendarValue.type === 'quarters' &&
      calendarValue.quarters?.length > 0
    ) {
      let qv = calendarValue.quarters[0];
      if (qv.match(/^\d{4}-\d{2}-01$/)) {
        const yr = qv.substring(0, 4);
        const mn = qv.substring(5, 7);
        const qYr = mn === '12' ? (parseInt(yr) + 1).toString() : yr;
        qv = `${QM[mn]} ${qYr}`;
      }
      newField = keys.quarter;
      dateStr = qv;
    } else if (calendarValue.type === 'date' && calendarValue.date) {
      newField = keys.date;
      dateStr = calendarValue.date;
    } else if (calendarValue.type === 'special') {
      newField = keys.status;
      dateStr = calendarValue.value;
    }

    if (!newField)
      return {
        datesObject: existingDates || {},
        newApiField: fieldName,
        newDateStr: ''
      };

    return {
      datesObject: {
        ...(existingDates || {}),
        [newField]: dateStr,
        ...(newField !== keys.month && { [keys.month]: null }),
        ...(newField !== keys.quarter && { [keys.quarter]: null }),
        ...(newField !== keys.date && { [keys.date]: null }),
        ...(newField !== keys.status && { [keys.status]: null })
      },
      newApiField: newField,
      newDateStr: dateStr
    };
  };

  const handleAccordionDateChange = async (
    phaseId: string | null,
    phaseName: string | undefined,
    fieldName: string,
    // Raw CalendarRange onCalendarDateChange params (matches its callback signature)
    calendarDate: import('@internationalized/date').CalendarDate | null,
    isQuarterSelection: boolean | undefined,
    selectedQuarter: { quarter: number; year: number } | undefined,
    isMonthSelection: boolean | undefined,
    selectedMonth: { month: string; year: number } | undefined,
    specialValue: 'TBD' | 'N/A' | null | undefined,
    currentDates: any,
    oldDisplayText: string
  ) => {
    if (!card?.issueId) return;
    if (
      !calendarDate &&
      !isQuarterSelection &&
      !isMonthSelection &&
      specialValue === undefined
    )
      return;
    // TBD/N/A unchecked â€” keep calendar open so user can select a real date (mirrors SimpleDateField)
    if (specialValue === null) return;

    setOpenDateChipPhaseId(null);
    setNonPhasedDateChipOpen(false);

    // Transform raw CalendarRange params to the typed format (mirrors SimpleDateField.handleDateChange)
    let calendarValue: any;
    if (specialValue !== undefined && specialValue !== null) {
      calendarValue = { type: 'special', value: specialValue };
    } else if (isQuarterSelection && selectedQuarter) {
      calendarValue = {
        type: 'quarters',
        quarters: [`Q${selectedQuarter.quarter} ${selectedQuarter.year}`]
      };
    } else if (isMonthSelection && selectedMonth) {
      calendarValue = {
        type: 'months',
        months: [`${selectedMonth.month} ${selectedMonth.year}`]
      };
    } else if (calendarDate) {
      calendarValue = { type: 'date', date: calendarDate.toString() }; // CalendarDate.toString() â†’ "YYYY-MM-DD"
    } else {
      return;
    }

    const { datesObject, newApiField, newDateStr } =
      buildDatesPatchObjectForAccordion(fieldName, calendarValue, currentDates);

    // Optimistic update so the chip and expanded GeneralInfo reflect the change immediately
    setApiData(prev => {
      if (!prev) return prev;
      if (phaseId) {
        const updatedPhases = Array.isArray((prev as any).releasePhases)
          ? (prev as any).releasePhases.map((p: any) =>
              p.id === phaseId
                ? {
                    ...p,
                    generalInfo: {
                      ...(p.generalInfo || {}),
                      dates: datesObject
                    }
                  }
                : p
            )
          : (prev as any).releasePhases;
        return {
          ...(prev as any),
          releasePhases: updatedPhases
        } as typeof prev;
      }
      return {
        ...(prev as any),
        generalInfo: {
          ...((prev as any).generalInfo || {}),
          dates: datesObject
        }
      } as typeof prev;
    });

    try {
      let response: unknown;
      if (phaseId) {
        response = await updateMoment(card.issueId, {
          releasePhases: [
            {
              id: phaseId,
              ...(phaseName && { phaseName }),
              generalInfo: { dates: datesObject }
            }
          ]
        });
      } else {
        response = await updateMoment(card.issueId, {
          generalInfo: { dates: datesObject }
        });
      }
      seamlessRefreshWithoutFeatures(response);

      // Side-effect: auto-disable showOnRoadmap when date is TBD/N/A or matches paired date
      // Mirrors applyToggleSideEffectsForSavedDate in GeneralInfo.tsx
      const currentShowOnRoadmap = phaseId
        ? (apiData as any)?.releasePhases?.find((p: any) => p.id === phaseId)
            ?.showOnRoadmap === true
        : (apiData as any)?.showOnRoadmap === true;

      if (currentShowOnRoadmap) {
        // Helper: extract first available comparable string from a date field group
        const getComp = (dk: string, mk: string, qk: string): string =>
          (datesObject[mk] || datesObject[qk] || datesObject[dk] || '').trim();

        let shouldDisableToggle = false;

        if (newDateStr === 'TBD' || newDateStr === 'N/A') {
          shouldDisableToggle = true;
        } else {
          const newComp = newDateStr.trim();
          if (newComp) {
            if (fieldName === 'marketingMoment') {
              const releaseComp = getComp(
                'releaseDate',
                'releaseMonth',
                'releaseQuarter'
              );
              const effectiveComp = getComp(
                'effectiveDate',
                'effectiveMonth',
                'effectiveQuarter'
              );
              shouldDisableToggle = phaseId
                ? !!(releaseComp && newComp === releaseComp)
                : !!(
                    (releaseComp && newComp === releaseComp) ||
                    (effectiveComp && newComp === effectiveComp)
                  );
            } else if (fieldName === 'releaseDate') {
              const targetComp = getComp(
                'targetStart',
                'targetMonth',
                'targetQuarter'
              );
              const effectiveComp = getComp(
                'effectiveDate',
                'effectiveMonth',
                'effectiveQuarter'
              );
              shouldDisableToggle = phaseId
                ? !!(targetComp && newComp === targetComp)
                : !!(
                    (targetComp && newComp === targetComp) ||
                    (effectiveComp && newComp === effectiveComp)
                  );
            } else if (fieldName === 'customerNotificationDate') {
              const effectiveComp = getComp(
                'effectiveDate',
                'effectiveMonth',
                'effectiveQuarter'
              );
              shouldDisableToggle = !!(
                effectiveComp && newComp === effectiveComp
              );
            }
          }
        }

        if (shouldDisableToggle) {
          // Optimistic update for showOnRoadmap
          setApiData(prev => {
            if (!prev) return prev;
            if (phaseId) {
              const updatedPhases = Array.isArray((prev as any).releasePhases)
                ? (prev as any).releasePhases.map((p: any) =>
                    p.id === phaseId ? { ...p, showOnRoadmap: false } : p
                  )
                : (prev as any).releasePhases;
              return {
                ...(prev as any),
                releasePhases: updatedPhases
              } as typeof prev;
            }
            return { ...(prev as any), showOnRoadmap: false } as typeof prev;
          });

          try {
            if (phaseId) {
              if (usesPcoStylePhasedDates(momentType)) {
                await patchRtmPhase(card.issueId, phaseId, {
                  showOnRoadmap: false
                });
              } else {
                const { updatePhasedField } = await import(
                  './utils/phasedFieldUpdateUtils'
                );
                await updatePhasedField(
                  card.issueId,
                  phaseId,
                  'showOnRoadmap',
                  false,
                  () => {},
                  err => console.error('showOnRoadmap update failed:', err),
                  undefined,
                  phaseName
                );
              }
            } else {
              const { updateNonPhasedField } = await import(
                './utils/fieldUpdateUtils'
              );
              await updateNonPhasedField(
                card.issueId,
                'showOnRoadmap',
                false,
                () => {},
                err => console.error('showOnRoadmap update failed:', err)
              );
            }
          } catch (err) {
            console.error('Failed to auto-disable showOnRoadmap:', err);
          }
        }
      }
    } catch (err) {
      // Rollback: re-fetch to restore correct state
      seamlessRefreshWithoutFeatures();
      showOptimisticUpdateErrorToast(
        'Failed to update date. Please try again.',
        err
      );
    }
  };

  // Extracts the raw API date value for a field â€” mirrors GeneralInfo.handleDateChange's
  // normalizedOldValue logic so audit "from" values are consistent with the changelog.
  const getRawOldDateValue = (fieldName: string, dates: any): string => {
    if (!dates) return 'N/A';
    let oldValue: string | null | undefined;
    if (fieldName === 'marketingMoment') {
      const v =
        dates.targetStart || dates.targetMonth || dates.targetQuarter || null;
      oldValue = !v && dates.targetDateStatus ? dates.targetDateStatus : v;
    } else if (fieldName === 'releaseDate') {
      const v =
        dates.releaseDate || dates.releaseMonth || dates.releaseQuarter || null;
      oldValue = !v && dates.releaseDateStatus ? dates.releaseDateStatus : v;
    } else if (fieldName === 'effectiveDate') {
      const v =
        dates.effectiveDate ||
        dates.effectiveMonth ||
        dates.effectiveQuarter ||
        null;
      oldValue =
        !v && dates.effectiveDateStatus ? dates.effectiveDateStatus : v;
    } else if (fieldName === 'customerNotificationDate') {
      const status = dates.customerNotificationDateStatus;
      if (status === 'TBD' || status === 'N/A') {
        oldValue = status;
      } else {
        const v =
          dates.customerNotificationDate ||
          dates.customerNotificationMonth ||
          dates.customerNotificationQuarter ||
          null;
        oldValue = !v && status ? status : v;
      }
    }
    return oldValue === null || oldValue === undefined
      ? 'N/A'
      : String(oldValue);
  };

  const getDateChipTooltip = (fieldName: string): string => {
    switch (fieldName) {
      case 'releaseDate':
        return 'Release Date';
      case 'customerNotificationDate':
        return 'Customer Notification Date';
      case 'effectiveDate':
        return 'Effective Date';
      default:
        return 'Marketing Date';
    }
  };

  // â”€â”€ End accordion date chip helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  // console.log('ðŸŽ¯ Centralized Moment Metadata:', {
  //   momentName,
  //   momentType,
  //   issueId: card?.issueId,
  //   source: apiData?.name ? 'apiData' : card?.name ? 'card' : 'unknown'
  // });

  // Initialize moment type update hook
  const { updateMomentType, isUpdating: isMomentTypeUpdating } =
    useUpdateMomentType({
      issueId: card?.issueId,
      currentMomentType:
        card?.momentType || card?.projectType || (card as any)?.type,
      momentName,
      onRefresh: seamlessRefreshAndNotifications
    });

  // Initialize delete milestones hook
  const { deleteMilestonesHandler, isDeleting: isDeletingMilestones } =
    useDeleteMilestones({
      issueId: card?.issueId,
      onRefresh: seamlessRefreshAndNotifications
    });

  // Initialize the API data updater for seamless updates
  const apiDataUpdater = useApiDataUpdater({
    setApiData,
    apiUpdateFunction: async (updateData: any) => {
      if (!card?.issueId) {
        throw new Error('No issueId available for API update');
      }
      return await updateMoment(card.issueId, updateData);
    },
    onSuccess: result => {},
    onError: error => {},
    issueId: card?.issueId,
    momentName, // âœ… CENTRALIZED: Use centralized values
    momentType // âœ… CENTRALIZED: Use centralized values
  });

  // Prevent duplicate role updates firing back-to-back
  const roleUpdateInFlightRef = React.useRef<Record<string, boolean>>({});

  // Create unified primary contacts managers
  const createPrimaryContactsManagerForPhase = (
    phaseId: string,
    phaseName?: string
  ): PrimaryContactsManager => {
    return createPrimaryContactsManager({
      issueId: card?.issueId || '',
      isPhased: true,
      phaseId,
      phaseName,
      onOptimisticUpdate: setApiData,
      getCurrentContacts: targetPhaseId =>
        flattenPrimaryContacts(targetPhaseId),
      onDataRefresh: seamlessRefreshWithoutFeatures
    });
  };

  const createPrimaryContactsManagerForNonPhased =
    (): PrimaryContactsManager => {
      return createPrimaryContactsManager({
        issueId: card?.issueId || '',
        isPhased: false,
        onOptimisticUpdate: setApiData,
        getCurrentContacts: () => flattenPrimaryContacts(),
        onDataRefresh: seamlessRefreshWithoutFeatures
      });
    };

  useEffect(() => {
    if (!isFullScreen) return;
    const handleEsc = (e: KeyboardEvent) => {
      // Exit fullscreen on Escape, but don't close the slideout
      // (the main keyboard handler will handle closing the slideout if needed)
      if (e.key === 'Escape') {
        e.stopPropagation(); // Prevent the main handler from also processing
        setIsFullScreen(false);
      }
    };
    window.addEventListener('keydown', handleEsc, { capture: true }); // Use capture to handle first
    return () =>
      window.removeEventListener('keydown', handleEsc, { capture: true });
  }, [isFullScreen]);

  // Old useEffect blocks removed - now handled by the updated click outside handler above

  const getRiskLevelClass = () => {
    switch (riskLevel?.toLowerCase()) {
      case 'low':
        return styles.low;
      case 'moderate':
        return styles.moderate;
      case 'high':
        return styles.high;
      default:
        return '';
    }
  };

  const renderField = (field: any) => {
    return <Field config={field} />;
  };

  const renderColumnGeneralInfo = (column: any) => (
    <div>
      <div className={styles.generalInfoHeader}>
        <Text UNSAFE_className={styles.generalInfoHeaderText}>
          {column.title}
        </Text>
        <div className={styles.genralInfoIconSection}>
          <div className={styles.genralInfoIcon}>
            <GeneralInfoIcon />
          </div>
        </div>
      </div>

      {column.fields.map((field: any, index: number) => (
        <React.Fragment key={index}>{renderField(field)}</React.Fragment>
      ))}
    </div>
  );

  const renderColumn = (column: any) => (
    <div>
      <div className={styles.generalInfoHeader}>
        <Text UNSAFE_className={styles.generalInfoHeaderText}>
          {column.title}
        </Text>
        {column.fields.map((field: any, index: number) => (
          <React.Fragment key={index}>{renderField(field)}</React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderInfoSection = () => {
    const generalInfo = getGeneralInfo();
    const contacts = flattenPrimaryContacts();
    return (
      <SlideoutGeneralInfoSection
        generalInfo={generalInfo}
        reportingStatus={apiData?.status || card?.status}
        eventsData={eventsData}
        isLoadingEvents={isLoadingEvents}
        eventsError={eventsError}
        parentMomentsData={parentMomentsData}
        isLoadingParentMoments={isLoadingParentMoments}
        parentMomentsError={parentMomentsError}
        issueId={card?.issueId}
        onMilestoneRefresh={handleSeamlessDataRefreshWithoutFeatures}
        onSeamlessRefresh={handleSeamlessDataRefreshWithoutFeatures}
        onRefreshNotifications={() =>
          dispatch(alertsActions.loadAlertsAndNotifications())
        }
        onGeneralInfoChange={updatedInfo => {
          // Update non-phased data in the API data structure
          // Merge with existing data to preserve fields like event
          setApiData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              generalInfo: {
                ...prev.generalInfo, // Keep existing fields (event, etc.)
                ...updatedInfo, // Merge with updated fields
                // Merge nested dates object if it exists
                ...(updatedInfo.dates && {
                  dates: {
                    ...prev.generalInfo?.dates,
                    ...updatedInfo.dates
                  }
                })
              }
            };
          });
        }}
        contacts={contacts}
        onRoleContactsReplace={async (role, roleContacts) => {
          const manager = createPrimaryContactsManagerForNonPhased();
          await manager.replaceRoleContacts(role, roleContacts as PCContact[]);
        }}
        onContactUpdate={async (updatedContact, index) => {
          const manager = createPrimaryContactsManagerForNonPhased();
          await manager.updateContact(updatedContact as PCContact, index);
        }}
        onContactAdd={async newContact => {
          // Wire non-phased adds through PrimaryContactsManager for consistent save behavior
          const manager = createPrimaryContactsManagerForNonPhased();
          await manager.addContacts(newContact as PCContact | PCContact[]);
        }}
      />
    );
  };

  const renderOverviewSection = () => {
    return (
      <SlideoutOverviewSection
        overviewRef={overviewErrorRef}
        overviewResetTrigger={overviewResetTrigger}
        adaptedOverviewData={adaptedOverviewData}
        card={card}
        formApiConfig={formApiConfig}
        formApiLoading={formApiLoading}
        onDataRefresh={handleSeamlessDataRefreshWithoutFeatures}
        onNotificationRefresh={refreshMomentNotifications}
        onUnsavedChanges={hasChanges => {
          overviewHasUnsavedChanges.current = hasChanges;
        }}
        onStrategicAlignmentError={(error, payload, intendedFieldValues) => {
          handleStrategicAlignmentErrorGeneric(
            error,
            'overview',
            payload,
            undefined,
            undefined,
            intendedFieldValues
          );
        }}
        onStrategicAlignmentUpdated={() => {
          // First retry tier update if pending
          if (pendingTierUpdate) {
            retryPendingTierUpdate();
          } else {
            // If no tier update, retry other pending updates
            schedulePendingUpdateRetry();
          }
        }}
      />
    );
  };

  const renderFeaturesSection = () => (
    <SlideoutLegacyFeaturesTab features={card.features} />
  );

  const handleMaximize = () => {
    setIsFullScreen(true);
  };

  const handleMinimize = () => {
    setIsFullScreen(false);
  };

  // Hierarchy products are now loaded from Redux store at app startup
  // No need for API call function anymore

  // Handler for product selection changes
  const handleProductSelectionChange = async (newProducts: string[]) => {
    // 1. âœ… Capture OLD value FIRST (before any changes)
    const oldProducts = selectedProducts || [];

    // âœ… CLEAR DRAFT CHANGES: Identify removed products and clear their draft features
    const removedProducts = oldProducts.filter(
      product => !newProducts.includes(product)
    );

    if (removedProducts.length > 0 && card?.issueId) {
      try {
        // Clear sessionStorage for each removed product
        removedProducts.forEach(productName => {
          // Pattern: features:new-rows:{issueId}:{productName}
          // Pattern: features:changes:{issueId}:{productName}
          const newRowsKey = `features:new-rows:${card.issueId}:${productName}`;
          const changesKey = `features:changes:${card.issueId}:${productName}`;

          sessionStorage.removeItem(newRowsKey);
          sessionStorage.removeItem(changesKey);

          console.log(
            `ðŸ§¹ Cleared draft features for removed product: ${productName}`
          );
        });
      } catch (e) {
        console.error(
          'Failed to clear draft features for removed products:',
          e
        );
      }
    }

    // CRITICAL FIX: Record user selection timestamp and products for race condition protection
    lastUserProductUpdateTimestamp.current = Date.now();
    lastUserSelectedProducts.current = newProducts;

    // Set flag to prevent useEffect from overwriting products during update
    isProductUpdateInProgress.current = true;

    setSelectedProducts(newProducts);

    // Update the backend with new product selection
    if (card?.issueId) {
      try {
        const patchResponse = await updateMoment(card.issueId, {
          products: newProducts
        });

        await handleSeamlessDataRefresh(patchResponse);

        // Clear the flag after refresh completes
        // INCREASED DELAY: Use 1000ms (1 second) to ensure all async operations complete
        // This works with the 3-second protection window to preserve user's product order
        setTimeout(() => {
          isProductUpdateInProgress.current = false;
          // console.log('âœ… Product update completed, re-enabling product sync');
        }, 1000);
      } catch (error) {
        console.error('Failed to update products:', error);

        // Check if this is a strategic alignment error
        const isStrategicAlignmentError = detectStrategicAlignmentError(error);

        if (isStrategicAlignmentError) {
          // Handle strategic alignment error
          handleStrategicAlignmentErrorGeneric(
            error,
            'products',
            { products: newProducts },
            oldProducts,
            () => {
              setSelectedProducts(oldProducts);
              isProductUpdateInProgress.current = false;
              lastUserProductUpdateTimestamp.current = 0;
            }
          );
          // Revert the local state immediately on strategic alignment error
          setSelectedProducts(oldProducts);
          isProductUpdateInProgress.current = false;
          lastUserProductUpdateTimestamp.current = 0;
        } else {
          // Regular error - show toast and revert
          ToastQueue.negative('Failed to update products. Please try again.', {
            timeout: 5000
          });

          // Revert the local state if API call fails
          setSelectedProducts(oldProducts);

          // Clear the flag on error and reset timestamp
          isProductUpdateInProgress.current = false;
          lastUserProductUpdateTimestamp.current = 0;
        }
      }
    }
  };

  // Handler for title updates
  const handleTitleUpdate = async (newTitle: string) => {
    if (!card?.issueId) return;

    // Extract only the title part (remove product groups prefix if present)
    const data = getDataWithFallback() as any;
    const productGroups = data?.productGroups || [];

    let actualTitle = newTitle;

    // If there are product groups, remove the prefix from the edited title
    if (productGroups && productGroups.length > 0) {
      const productGroupNames = productGroups.map((group: any) => {
        return typeof group === 'string'
          ? group
          : group.name || group.label || group;
      });
      const productGroupsPrefix = productGroupNames.join(', ') + ' - ';

      // If the newTitle starts with the product groups prefix, remove it
      if (newTitle.startsWith(productGroupsPrefix)) {
        actualTitle = newTitle.substring(productGroupsPrefix.length);
      }
    }

    const phasedTitles = getRtmPhasedTitleParts({
      momentType: card?.momentType || card?.projectType,
      rawTitle: actualTitle,
      releasePhase: card?.releasePhase,
      releasePhaseId: card?.releasePhaseId,
      releasePhases: Array.isArray(data?.releasePhases)
        ? data.releasePhases
        : null
    });
    // Fall back to the pre-phased title if storageTitle is unexpectedly empty
    actualTitle = phasedTitles.storageTitle || actualTitle;

    // CAPTURE OLD TITLE BEFORE ANY UPDATES (for revert on error)
    const oldTitle = card?.name || data?.name || '';
    const oldDisplayTitle = displayTitle;

    // CRITICAL FIX: Set protection flags to prevent product state from being reset
    // When we update apiData below, it will trigger the useEffect that initializes selectedProducts
    // We need to protect the current selectedProducts state during title updates
    lastUserProductUpdateTimestamp.current = Date.now();
    lastUserSelectedProducts.current = selectedProducts;

    // IMMEDIATE UI UPDATE (before API call for zero lag)
    // Update the local state immediately
    setApiData(prev => {
      if (!prev) return prev;
      return { ...prev, name: actualTitle };
    });

    // Update the card's title immediately
    card.title = actualTitle;

    if (!productGroups || productGroups.length === 0) {
      setDisplayTitle(phasedTitles.displayTitle);
    } else {
      const productGroupNames = productGroups.map((group: any) => {
        return typeof group === 'string'
          ? group
          : group.name || group.label || group;
      });
      const productGroupsPrefix = productGroupNames.join(', ') + ' - ';
      setDisplayTitle(`${productGroupsPrefix}${phasedTitles.displayTitle}`);
    }

    // API call in background (non-blocking)
    try {
      await updateMoment(card.issueId, { name: actualTitle });
    } catch (error: any) {
      console.error('Failed to update title:', error);

      // Extract error message from API response
      let errorMessage = 'Failed to update title. Please try again.';

      if (error?.response?.data?.message) {
        // Use the message from the API response
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        // Fallback to error.message if available
        errorMessage = error.message;
      }

      // Show error toast with the API message
      ToastQueue.negative(errorMessage, {
        timeout: 5000
      });

      // Revert the title changes since the API call failed
      setApiData(prev => {
        if (!prev) return prev;
        return { ...prev, name: oldTitle };
      });

      // Revert card title
      if (card) {
        card.title = oldTitle;
      }

      // Revert display title to the original value
      setDisplayTitle(oldDisplayTitle);
    }
  };

  const {
    handleNonPhasedStatusAction,
    handleStatusAction,
    handleSecondaryStatusAction
  } = useSlideoutStatusHandlers({
    card,
    apiData,
    setApiData,
    normalizedPhases,
    seamlessRefreshAndNotifications,
    setIsOverviewExpanded,
    setIsFeaturesExpanded,
    setIsDecisionsExpanded,
    setIsDACIExpanded,
    setIsAdditionalInfoExpanded,
    setIsGeneralInfoExpanded,
    setCustomExpandedPhases,
    setExpandedInfoSection
  });

  // Handler for non-phased risk level dropdown actions
  const handleNonPhasedRiskLevelAction = async (action: React.Key) => {
    if (!card?.issueId) {
      return;
    }

    try {
      const riskLevelValue = action as string;

      // Map action values to proper risk level values (same as phased)
      const riskLevelMap: Record<string, string> = {
        low: RISK_LEVELS.LOW, // 'low' -> 'Green'
        moderate: RISK_LEVELS.MODERATE, // 'moderate' -> 'Yellow'
        high: RISK_LEVELS.HIGH, // 'high' -> 'Red'
        in_planning: RISK_LEVELS.IN_PLANNING // 'in_planning' -> 'Planning'
      };

      const mappedRiskLevel = riskLevelMap[riskLevelValue] || riskLevelValue;
      const oldRiskLevel = apiData?.generalInfo?.riskLevel || '';

      // Optimistic update for non-phased
      setApiData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          generalInfo: {
            ...prev.generalInfo,
            riskLevel: mappedRiskLevel
          }
        } as MomentData;
      });

      // Use non-phased field update utility
      const { updateNonPhasedField } = await import('./utils/fieldUpdateUtils');
      await updateNonPhasedField(
        card.issueId,
        'riskLevel',
        mappedRiskLevel,
        () => {},
        error => {
          showOptimisticUpdateErrorToast(
            'Failed to update risk level. Please try again.',
            error
          );
          // Revert optimistic update on error
          setApiData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              generalInfo: {
                ...prev.generalInfo,
                riskLevel: prev.generalInfo?.riskLevel || 'Low' // Revert to original
              }
            } as MomentData;
          });
        }
      );
    } catch (error) {}
  };

  // Handler for risk level dropdown actions (phase-specific and non-phased)
  const handleRiskLevelAction = async (action: React.Key, phaseId: string) => {
    if (!card?.issueId) {
      return;
    }

    // Check if this is a non-phased moment (momentId = issueId or 'moment-section')
    const isNonPhased = phaseId === (card?.issueId || 'moment-section');

    if (isNonPhased) {
      return handleNonPhasedRiskLevelAction(action);
    }

    try {
      const riskLevelValue = action as string;

      // Get the phase for validation
      const phase = normalizedPhases.find(p => p.id === phaseId);

      if (!phase) {
        return;
      }

      // Map action values to proper risk level values
      const riskLevelMap: Record<string, string> = {
        low: 'Green',
        moderate: 'Yellow',
        high: 'Red',
        in_planning: RISK_LEVELS.IN_PLANNING // 'In Planning'
      };

      const mappedRiskLevel = riskLevelMap[riskLevelValue] || riskLevelValue;
      const oldPhasedRiskLevel = getGeneralInfo(phaseId)?.riskLevel || '';

      // Update local state immediately for instant UI feedback
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;

          // For phased layouts, update the specific phase data
          if (prevData.releasePhases && Array.isArray(prevData.releasePhases)) {
            const updatedPhases = prevData.releasePhases.map((p: any) => {
              if (p.id === phaseId) {
                return {
                  ...p,
                  generalInfo: {
                    ...p.generalInfo,
                    riskLevel: mappedRiskLevel
                  }
                };
              }
              return p;
            });

            return {
              ...prevData,
              releasePhases: updatedPhases
            };
          }

          // Fallback for non-phased or if phase not found
          return {
            ...prevData,
            riskLevel: mappedRiskLevel
          };
        });
      }

      // Find the phase to get its name for the API payload
      const targetPhase = normalizedPhases.find(p => p.id === phaseId);
      const phaseName = targetPhase?.phaseName;

      // Use the new phased utility for proper API payload structure
      await updatePhasedRiskLevelField(
        card.issueId,
        phaseId, // Use the phase ID instead of phase name
        mappedRiskLevel,
        () => {},
        error => {
          showOptimisticUpdateErrorToast(
            'Failed to update risk level. Please try again.',
            error
          );
          // Revert state on error
          if (setApiData) {
            setApiData(prevData => {
              if (!prevData) return prevData;

              // Get original risk level value - try phase-specific first, then global
              const originalPhase = normalizedPhases.find(
                p => p.id === phaseId
              );
              const originalRiskLevel =
                originalPhase?.generalInfo?.riskLevel ||
                card?.generalInfo?.riskLevel ||
                card?.riskLevel ||
                'Green';

              // For phased layouts, revert the specific phase data
              if (
                prevData.releasePhases &&
                Array.isArray(prevData.releasePhases)
              ) {
                const revertedPhases = prevData.releasePhases.map((p: any) => {
                  if (p.id === phaseId) {
                    return {
                      ...p,
                      generalInfo: {
                        ...p.generalInfo,
                        riskLevel: originalRiskLevel
                      }
                    };
                  }
                  return p;
                });

                return {
                  ...prevData,
                  releasePhases: revertedPhases
                };
              }

              // Fallback for non-phased
              return {
                ...prevData,
                riskLevel: originalRiskLevel
              };
            });
          }
        },
        phaseName
      );
    } catch (error) {
      showOptimisticUpdateErrorToast(
        'Failed to update risk level. Please try again.',
        error
      );
      // Revert state on error (fallback)
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;
          // Get original risk level value from card data
          const originalRiskLevel =
            card?.generalInfo?.riskLevel || card?.riskLevel || 'Green';
          return {
            ...prevData,
            riskLevel: originalRiskLevel
          };
        });
      }
    }
  };

  // Get risk level icon based on phase-specific risk level
  const getRiskLevelIcon = (phaseRiskLevel?: string) => {
    const riskLevelToUse = resolveRiskLevel(phaseRiskLevel, riskLevel);
    switch (riskLevelToUse.toLowerCase()) {
      case 'green':
      case RISK_LEVELS.LOW.toLowerCase():
      case 'low':
        return RiskLevelLowIcon;
      case 'yellow':
      case RISK_LEVELS.MODERATE.toLowerCase():
      case 'moderate':
        return RiskLevelModerateIcon;
      case 'red':
      case RISK_LEVELS.HIGH.toLowerCase():
      case 'high':
        return RiskLevelHighIcon;
      case 'planning':
      case RISK_LEVELS.IN_PLANNING.toLowerCase():
      case 'in_planning':
        return RiskLevelPlanningIcon;
      case 'na':
      case RISK_LEVELS.NA.toLowerCase():
        return RiskLevelLowIcon; // Use low icon for NA
      default:
        return RiskLevelLowIcon; // Default to low if no risk level
    }
  };

  // Get risk level display text based on phase-specific risk level
  const getRiskLevelText = (phaseRiskLevel?: string) => {
    const riskLevelToUse = phaseRiskLevel || riskLevel; // Use phase-specific or fallback to global
    return riskLevelToUse || 'Low'; // Default to 'Low' if no risk level
  };

  // Helper function to get status display text
  const getStatusDisplayText = (status: string | undefined): string => {
    if (!status) return 'Status';

    // Map status values to display text
    const statusMap: Record<string, string> = {
      draft: 'Draft',
      active: 'Active',
      completed: 'Completed',
      cancelled: 'Cancelled',
      on_hold: 'On Hold',
      in_progress: 'In Progress'
    };

    return statusMap[status] || status;
  };

  // Handler for non-phased tier dropdown actions
  const handleNonPhasedTierAction = async (action: React.Key) => {
    if (!card?.issueId) {
      return;
    }

    const tierValue = action as string;
    const oldTier = apiData?.generalInfo?.tier || '';

    try {
      // Optimistic update for non-phased
      setApiData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          generalInfo: {
            ...prev.generalInfo,
            tier: tierValue
          }
        } as MomentData;
      });

      // Use non-phased field update utility
      const { updateNonPhasedField } = await import('./utils/fieldUpdateUtils');
      await updateNonPhasedField(
        card.issueId,
        'tier',
        tierValue,
        response => {
          seamlessRefreshWithoutFeatures(response);
          // Tier gates alert applicability (e.g. overdue/upcoming rules) — refresh the
          // top-level alert banner too, or it keeps showing a now-stale alert until the
          // slideout closes. Mirrors the onRefreshNotifications wiring used elsewhere
          // (e.g. GeneralInfo's other field-change handlers).
          dispatch(alertsActions.loadAlertsAndNotifications());
        },
        error => {
          const isStrategicAlignmentError =
            detectStrategicAlignmentErrorForTier(error, oldTier, tierValue);

          if (isStrategicAlignmentError) {
            handleStrategicAlignmentError(
              error,
              card?.issueId || 'moment-section',
              tierValue,
              oldTier,
              true
            );
          } else {
            showOptimisticUpdateErrorToast(
              'Failed to update tier. Please try again.',
              error
            );
          }

          // Revert optimistic update on error
          setApiData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              generalInfo: {
                ...prev.generalInfo,
                tier: prev.generalInfo?.tier || 'NULL' // Revert to original
              }
            } as MomentData;
          });
        }
      );
    } catch (error) {
      const isStrategicAlignmentError = detectStrategicAlignmentErrorForTier(
        error,
        oldTier,
        tierValue
      );
      if (isStrategicAlignmentError) {
        handleStrategicAlignmentError(
          error,
          card?.issueId || 'moment-section',
          tierValue,
          oldTier,
          true
        );
      } else {
        showOptimisticUpdateErrorToast(
          'Failed to update tier. Please try again.',
          error
        );
      }
    }
  };

  // Handler for non-phased revenue impact dropdown actions
  const handleNonPhasedRevenueImpactAction = async (action: React.Key) => {
    if (!card?.issueId) {
      return;
    }

    try {
      const revenueImpactValue = action as string;

      // Capture old revenue impact before update (for optimistic UI / rollback)
      const booleanValue = apiData?.generalInfo?.revenueImpact;
      const oldRevenueImpact =
        booleanValue !== undefined && booleanValue !== null
          ? booleanValue
            ? 'Yes'
            : 'No'
          : '';

      // Convert "Yes"/"No" string to boolean for API
      const booleanRevenueImpact = revenueImpactValue === 'Yes';

      // Optimistic update for non-phased
      setApiData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          generalInfo: {
            ...prev.generalInfo,
            revenueImpact: booleanRevenueImpact
          }
        } as MomentData;
      });

      // Use non-phased field update utility
      const { updateNonPhasedField } = await import('./utils/fieldUpdateUtils');
      await updateNonPhasedField(
        card.issueId,
        'revenueImpact',
        booleanRevenueImpact, // Send boolean to API
        response => {
          handleSeamlessDataRefreshWithoutFeatures(response);
        },
        error => {
          showOptimisticUpdateErrorToast(
            'Failed to update revenue impact. Please try again.',
            error
          );
          // Revert optimistic update on error
          setApiData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              generalInfo: {
                ...prev.generalInfo,
                revenueImpact: prev.generalInfo?.revenueImpact
              }
            } as MomentData;
          });
        }
      );
    } catch (error) {
      showOptimisticUpdateErrorToast(
        'Failed to update revenue impact. Please try again.',
        error
      );
    }
  };

  // Helper function to detect strategic alignment error for tier updates
  // (keeps the tier-specific logic for backward compatibility)
  const detectStrategicAlignmentErrorForTier = (
    error: any,
    oldTier: string,
    newTier: string
  ): boolean => {
    if (isEolMoment(momentType)) {
      return false;
    }

    // 1. Check if we're upgrading TO L1/L2 (not from L1/L2)
    const isUpgradingToL1OrL2 =
      (newTier === 'L1' || newTier === 'L2') &&
      oldTier !== 'L1' &&
      oldTier !== 'L2';

    if (!isUpgradingToL1OrL2) {
      return false; // Not upgrading to L1/L2, so this error doesn't apply
    }

    // Use the generic detection function
    return detectStrategicAlignmentError(error);
  };

  // Helper function to handle strategic alignment error for tier updates
  const handleStrategicAlignmentError = (
    error: any,
    phaseId: string,
    tierValue: string,
    oldTier: string,
    isNonPhased: boolean
  ) => {
    // Store pending tier update
    setPendingTierUpdate({
      phaseId,
      tierValue,
      oldTier,
      isNonPhased
    });

    // Show dialog for strategic alignment (replaces toast for better user attention)
    const errorMessage = getStrategicAlignmentErrorMessage(tierValue);
    setStrategicAlignmentDialog({ isOpen: true, message: errorMessage });

    // Highlight the strategic alignment field
    if (overviewErrorRef.current) {
      overviewErrorRef.current.highlightStrategicAlignment();
    }
  };

  // Generic handler for strategic alignment error (for overview, products, etc.)
  const handleStrategicAlignmentErrorGeneric = (
    error: any,
    updateType: 'overview' | 'products' | 'generalInfo' | 'other',
    payload: Record<string, any>,
    oldValue?: any,
    revertFn?: () => void,
    intendedFieldValues?: Record<string, any>
  ) => {
    // Store pending update
    setPendingUpdate({
      type: updateType,
      payload,
      oldValue,
      revertFn,
      intendedFieldValues
    });

    // Show dialog for strategic alignment (dynamic message based on tier context)
    const tierContext = payload?.generalInfo?.tier
      ? [payload.generalInfo.tier]
      : getGeneralInfo()?.tier
      ? [getGeneralInfo()!.tier!]
      : undefined;
    const errorMessage = getStrategicAlignmentErrorMessage(tierContext, error);
    setStrategicAlignmentDialog({ isOpen: true, message: errorMessage });

    // Highlight the strategic alignment field
    if (overviewErrorRef.current) {
      overviewErrorRef.current.highlightStrategicAlignment();
    }
  };

  // Function to retry pending update after strategic alignment is filled
  // Helper function to schedule retry of pending updates
  const schedulePendingUpdateRetry = () => {
    if (pendingUpdate) {
      setTimeout(() => {
        retryPendingUpdate();
      }, 500);
    }
  };

  const retryPendingUpdate = async () => {
    if (!pendingUpdate || !card?.issueId) {
      return;
    }

    const { type, payload, oldValue, revertFn, intendedFieldValues } =
      pendingUpdate;

    try {
      let retryPayload = payload;

      // IMPORTANT: For overview retries, rebuild payload from Overview's LIVE state
      // so we include the latest Must Win selections (audienceStrategyMappings),
      // then overlay the intended failed field value(s).
      if (type === 'overview' && intendedFieldValues) {
        const currentFieldValues =
          overviewErrorRef.current?.getCurrentFieldValues() ||
          adaptedOverviewData;

        const mergedFieldValues = {
          ...currentFieldValues,
          ...intendedFieldValues
        };

        const rebuiltOverviewPayload =
          mapExistingToNewOverview(mergedFieldValues);

        // Preserve headlineUpdatedAt if it was part of the original payload
        const headlineUpdatedAt = (payload as any)?.overview?.headlineUpdatedAt;
        if (headlineUpdatedAt) {
          (rebuiltOverviewPayload as any).headlineUpdatedAt = headlineUpdatedAt;
        }

        retryPayload = { overview: rebuiltOverviewPayload };
      }

      // Retry the update
      const patchResponse = await updateMoment(card.issueId, retryPayload);

      if (patchResponse) {
        // Success - clear pending update and error state
        setPendingUpdate(null);
        if (overviewErrorRef.current) {
          overviewErrorRef.current.clearStrategicAlignmentError();
        }

        if (type === 'overview') {
          await seamlessRefreshWithoutFeatures(patchResponse);
        } else {
          await handleSeamlessDataRefresh(patchResponse);
        }
      }
    } catch (error) {
      console.error(`Error retrying ${type} update:`, error);

      // Check if it's still a strategic alignment error
      const isStillStrategicAlignmentError =
        detectStrategicAlignmentError(error);

      if (isStillStrategicAlignmentError) {
        // Keep the pending update and show dialog again (dynamic message)
        const errorMessage = getStrategicAlignmentErrorMessage(
          undefined,
          error
        );
        setStrategicAlignmentDialog({ isOpen: true, message: errorMessage });
      } else {
        // Different error - clear pending update and show error toast
        setPendingUpdate(null);
        ToastQueue.negative(`Failed to update ${type}. Please try again.`, {
          timeout: 5000
        });

        // Revert state if revert function is provided
        if (revertFn) {
          revertFn();
        } else if (oldValue && type === 'products') {
          setSelectedProducts(oldValue);
        }
      }
    }
  };

  // Handler for tier dropdown actions (phase-specific and non-phased)
  const handleTierAction = async (action: React.Key, phaseId: string) => {
    if (!card?.issueId) {
      return;
    }

    // Check if this is a non-phased moment (momentId = issueId or 'moment-section')
    const isNonPhased = phaseId === (card?.issueId || 'moment-section');

    if (isNonPhased) {
      return handleNonPhasedTierAction(action);
    }

    const tierValue = action as string;
    const oldTier = getGeneralInfo(phaseId)?.tier || '';

    try {
      // Get the phase for validation
      const phase = normalizedPhases.find(p => p.id === phaseId);

      if (!phase) {
        return;
      }

      // Update local state immediately for instant UI feedback
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;

          // For phased layouts, update the specific phase data
          if (prevData.releasePhases && Array.isArray(prevData.releasePhases)) {
            const updatedPhases = prevData.releasePhases.map((p: any) => {
              if (p.id === phaseId) {
                return {
                  ...p,
                  generalInfo: {
                    ...p.generalInfo,
                    tier: tierValue
                  }
                };
              }
              return p;
            });

            return {
              ...prevData,
              releasePhases: updatedPhases
            };
          }

          // Fallback for non-phased or if phase not found
          return {
            ...prevData,
            tier: tierValue
          };
        });
      }

      // Find the phase to get its name for the API payload
      const targetPhase = normalizedPhases.find(p => p.id === phaseId);
      const phaseName = targetPhase?.phaseName;

      // Use the new phased utility for proper API payload structure
      await updatePhasedTierField(
        card.issueId,
        phaseId, // Use the phase ID instead of phase name
        tierValue,
        response => {
          seamlessRefreshWithoutFeatures(response);
          // See handleNonPhasedTierAction — tier gates alert applicability, so the
          // top-level alert banner needs its own refresh too, not just the in-panel
          // field highlight.
          dispatch(alertsActions.loadAlertsAndNotifications());
        },
        error => {
          const isStrategicAlignmentError =
            detectStrategicAlignmentErrorForTier(error, oldTier, tierValue);

          if (isStrategicAlignmentError) {
            handleStrategicAlignmentError(
              error,
              phaseId,
              tierValue,
              oldTier,
              false
            );
          } else {
            showOptimisticUpdateErrorToast(
              'Failed to update tier. Please try again.',
              error
            );
          }

          // Revert state on error
          if (setApiData) {
            setApiData(prevData => {
              if (!prevData) return prevData;

              // Get original tier value - try phase-specific first, then global
              const originalPhase = normalizedPhases.find(
                p => p.id === phaseId
              );
              const originalTier =
                originalPhase?.generalInfo?.tier ||
                card?.generalInfo?.tier ||
                card?.tier ||
                'L1';

              // For phased layouts, revert the specific phase data
              if (
                prevData.releasePhases &&
                Array.isArray(prevData.releasePhases)
              ) {
                const revertedPhases = prevData.releasePhases.map((p: any) => {
                  if (p.id === phaseId) {
                    return {
                      ...p,
                      generalInfo: {
                        ...p.generalInfo,
                        tier: originalTier
                      }
                    };
                  }
                  return p;
                });

                return {
                  ...prevData,
                  releasePhases: revertedPhases
                };
              }

              // Fallback for non-phased
              return {
                ...prevData,
                tier: originalTier
              };
            });
          }
        },
        phaseName
      );
    } catch (error) {
      const isStrategicAlignmentError = detectStrategicAlignmentErrorForTier(
        error,
        oldTier,
        tierValue
      );
      if (isStrategicAlignmentError) {
        handleStrategicAlignmentError(
          error,
          phaseId,
          tierValue,
          oldTier,
          false
        );
      } else {
        showOptimisticUpdateErrorToast(
          'Failed to update tier. Please try again.',
          error
        );
      }
      // Revert state on error (fallback)
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;
          // Get original tier value from card data
          const originalTier = card?.generalInfo?.tier || card?.tier || 'L1';
          return {
            ...prevData,
            tier: originalTier
          };
        });
      }
    }
  };

  // Function to retry pending tier update after strategic alignment is filled
  const retryPendingTierUpdate = async () => {
    if (!pendingTierUpdate || !card?.issueId) {
      // If no tier update pending, check for other pending updates
      schedulePendingUpdateRetry();
      return;
    }

    const { phaseId, tierValue, oldTier, isNonPhased } = pendingTierUpdate;

    try {
      if (isNonPhased) {
        // Handle non-phased retry
        setApiData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            generalInfo: {
              ...prev.generalInfo,
              tier: tierValue
            }
          } as MomentData;
        });

        const { updateNonPhasedField } = await import(
          './utils/fieldUpdateUtils'
        );
        await updateNonPhasedField(
          card.issueId,
          'tier',
          tierValue,
          response => {
            setPendingTierUpdate(null);
            if (overviewErrorRef.current) {
              overviewErrorRef.current.clearStrategicAlignmentError();
            }

            handleSeamlessDataRefreshWithoutFeatures(response);
            refreshMomentNotifications(response);

            // After tier update succeeds, check for other pending updates
            schedulePendingUpdateRetry();
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update tier. Please try again.',
              error
            );
            // Revert on error
            setApiData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                generalInfo: {
                  ...prev.generalInfo,
                  tier: oldTier
                }
              } as MomentData;
            });
          }
        );
      } else {
        // Handle phased retry
        const targetPhase = normalizedPhases.find(p => p.id === phaseId);
        const phaseName = targetPhase?.phaseName;

        // Optimistic update
        if (setApiData) {
          setApiData(prevData => {
            if (!prevData) return prevData;
            if (
              prevData.releasePhases &&
              Array.isArray(prevData.releasePhases)
            ) {
              const updatedPhases = prevData.releasePhases.map((p: any) => {
                if (p.id === phaseId) {
                  return {
                    ...p,
                    generalInfo: {
                      ...p.generalInfo,
                      tier: tierValue
                    }
                  };
                }
                return p;
              });
              return {
                ...prevData,
                releasePhases: updatedPhases
              };
            }
            return {
              ...prevData,
              tier: tierValue
            };
          });
        }

        await updatePhasedTierField(
          card.issueId,
          phaseId,
          tierValue,
          response => {
            setPendingTierUpdate(null);
            if (overviewErrorRef.current) {
              overviewErrorRef.current.clearStrategicAlignmentError();
            }

            handleSeamlessDataRefreshWithoutFeatures(response);
            refreshMomentNotifications(response);

            // After tier update succeeds, check for other pending updates
            schedulePendingUpdateRetry();
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update tier. Please try again.',
              error
            );
            // Revert on error
            if (setApiData) {
              setApiData(prevData => {
                if (!prevData) return prevData;
                const originalPhase = normalizedPhases.find(
                  p => p.id === phaseId
                );
                const originalTier =
                  originalPhase?.generalInfo?.tier ||
                  card?.generalInfo?.tier ||
                  card?.tier ||
                  'L1';

                if (
                  prevData.releasePhases &&
                  Array.isArray(prevData.releasePhases)
                ) {
                  const revertedPhases = prevData.releasePhases.map(
                    (p: any) => {
                      if (p.id === phaseId) {
                        return {
                          ...p,
                          generalInfo: {
                            ...p.generalInfo,
                            tier: originalTier
                          }
                        };
                      }
                      return p;
                    }
                  );
                  return {
                    ...prevData,
                    releasePhases: revertedPhases
                  };
                }
                return {
                  ...prevData,
                  tier: originalTier
                };
              });
            }
          },
          phaseName
        );
      }
    } catch (error) {
      console.error('Error retrying tier update:', error);
    }
  };

  // Handler for RTM (phaseName) change â€” Price Changes and Offers only
  const handleRtmAction = async (newRtm: string, phaseId: string) => {
    if (!card?.issueId) return;

    const oldRtm =
      normalizedPhases.find(p => p.id === phaseId)?.phaseName || '';
    if (oldRtm === newRtm) {
      setRtmDropdownOpen(null);
      return;
    }

    // Optimistic UI update
    if (setApiData) {
      setApiData(prevData => {
        if (!prevData) return prevData;
        if (prevData.releasePhases && Array.isArray(prevData.releasePhases)) {
          const updatedPhases = prevData.releasePhases.map((p: any) =>
            p.id === phaseId ? { ...p, phaseName: newRtm } : p
          );
          return { ...prevData, releasePhases: updatedPhases };
        }
        return prevData;
      });
    }

    setRtmDropdownOpen(null);

    try {
      const patchResponse = await updateRtmPhase(card.issueId, phaseId, newRtm);

      await seamlessRefreshAndNotifications(patchResponse);
    } catch (error) {
      // Revert on error
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData || !Array.isArray(prevData.releasePhases))
            return prevData;
          const revertedPhases = prevData.releasePhases.map((p: any) =>
            p.id === phaseId ? { ...p, phaseName: oldRtm } : p
          );
          return { ...prevData, releasePhases: revertedPhases };
        });
      }
      ToastQueue.negative('Failed to update RTM. Please try again.', {
        timeout: 5000
      });
    }
  };

  // Handler for revenue driver dropdown actions
  const handleRevenueImpactAction = async (
    action: React.Key,
    phaseId: string
  ) => {
    if (!card?.issueId) {
      return;
    }

    // Check if this is a non-phased moment (momentId = issueId)
    const isNonPhased = phaseId === card?.issueId;

    if (isNonPhased) {
      return handleNonPhasedRevenueImpactAction(action);
    }

    try {
      const revenueImpactValue = action as string;

      // Get the phase for validation
      const phase = normalizedPhases.find(p => p.id === phaseId);

      if (!phase) {
        return;
      }

      // Update local state immediately for instant UI feedback
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;

          // For phased layouts, update the specific phase data
          if (prevData.releasePhases && Array.isArray(prevData.releasePhases)) {
            const updatedPhases = prevData.releasePhases.map((p: any) => {
              if (p.id === phaseId) {
                return {
                  ...p,
                  generalInfo: {
                    ...p.generalInfo,
                    revenueImpact: revenueImpactValue
                  }
                };
              }
              return p;
            });

            return {
              ...prevData,
              releasePhases: updatedPhases
            };
          }

          // Fallback for non-phased or if phase not found
          return {
            ...prevData,
            revenueImpact: revenueImpactValue
          };
        });
      }

      // Find the phase to get its name for the API payload
      const targetPhase = normalizedPhases.find(p => p.id === phaseId);
      const phaseName = targetPhase?.phaseName;

      // Use similar API update pattern as tier
      await updatePhasedRevenueImpactField(
        card.issueId,
        phaseId,
        revenueImpactValue, // "Yes" or "No" string - will be converted to boolean in the function
        response => {
          handleSeamlessDataRefreshWithoutFeatures(response);
        },
        error => {
          showOptimisticUpdateErrorToast(
            'Failed to update revenue impact. Please try again.',
            error
          );
          // Revert state on error
          if (setApiData) {
            setApiData(prevData => {
              if (!prevData) return prevData;

              // Get original revenue driver value - try phase-specific first, then global
              // Convert boolean to "Yes"/"No" for display
              const originalPhase = normalizedPhases.find(
                p => p.id === phaseId
              );
              const booleanValue =
                originalPhase?.generalInfo?.revenueImpact ??
                card?.generalInfo?.revenueImpact;
              const originalRevenueDriver =
                booleanValue !== undefined && booleanValue !== null
                  ? booleanValue === true || booleanValue === 'Yes'
                    ? 'Yes'
                    : 'No'
                  : 'Yes'; // Default to 'Yes' if undefined

              // For phased layouts, revert the specific phase data
              if (
                prevData.releasePhases &&
                Array.isArray(prevData.releasePhases)
              ) {
                const revertedPhases = prevData.releasePhases.map((p: any) => {
                  if (p.id === phaseId) {
                    return {
                      ...p,
                      generalInfo: {
                        ...p.generalInfo,
                        revenueImpact: originalRevenueDriver
                      }
                    };
                  }
                  return p;
                });

                return {
                  ...prevData,
                  releasePhases: revertedPhases
                };
              }

              // Fallback for non-phased
              return {
                ...prevData,
                revenueImpact: originalRevenueDriver
              };
            });
          }
        },
        phaseName
      );
    } catch (error) {
      showOptimisticUpdateErrorToast(
        'Failed to update revenue impact. Please try again.',
        error
      );
      // Revert state on error (fallback)
      if (setApiData) {
        setApiData(prevData => {
          if (!prevData) return prevData;
          // Get original revenue driver value from card data
          // Convert boolean to "Yes"/"No" for display
          const booleanValue = card?.generalInfo?.revenueImpact;
          const originalRevenueDriver =
            booleanValue !== undefined && booleanValue !== null
              ? booleanValue === true || booleanValue === 'Yes'
                ? 'Yes'
                : 'No'
              : 'Yes'; // Default to 'Yes' if undefined
          return {
            ...prevData,
            revenueImpact: originalRevenueDriver
          };
        });
      }
    }
  };

  const {
    StatusDropdownPortal,
    RiskLevelDropdownPortal,
    MomentStatusDropdownPortal,
    MomentRiskLevelDropdownPortal,
    MomentTierDropdownPortal,
    TierDropdownPortal,
    RtmDropdownPortal,
    RevenueImpactDropdownPortal
  } = useSlideoutFieldDropdownPortals({
    statusButtonRefs,
    statusDropdownOpen,
    setStatusDropdownOpen,
    riskButtonRefs,
    riskLevelDropdownOpen,
    setRiskLevelDropdownOpen,
    tierButtonRefs,
    tierDropdownOpen,
    setTierDropdownOpen,
    revenueImpactButtonRefs,
    revenueImpactDropdownOpen,
    setRevenueImpactDropdownOpen,
    rtmButtonRefs,
    rtmDropdownOpen,
    normalizedPhases,
    apiData,
    card,
    getGeneralInfo,
    tierOptions,
    revenueImpactOptions,
    handleStatusAction,
    handleSecondaryStatusAction,
    momentType,
    handleRiskLevelAction,
    handleTierAction,
    handleRevenueImpactAction,
    handleRtmAction
  });

  // Helper function to get all existing DACI contacts for duplicate prevention
  const getAllExistingDACIContacts = React.useMemo(() => {
    if (!daciContacts || !daciContacts.contacts) {
      return [];
    }
    const existingContacts = daciContacts.contacts.map(contact => ({
      ...contact,
      uid: contact.uid || contact.name.toLowerCase().replace(/\s+/g, '.') // Generate uid if not present
    }));
    return existingContacts;
  }, [daciContacts]);

  const getDaciContactsFromResponse = (response: any): any[] => {
    const moment = response?.data?.moment || response?.moment || response;
    const contacts =
      moment?.daciContacts?.contacts || response?.data?.daciContacts?.contacts;
    return Array.isArray(contacts) ? contacts : [];
  };

  const findPersistedDaciContact = (response: any, contactToFind: any) => {
    const contacts = getDaciContactsFromResponse(response);
    return contacts.find(
      contact =>
        (contactToFind.id && contact.id === contactToFind.id) ||
        (!!contactToFind.uid &&
          contact.uid === contactToFind.uid &&
          contact.name === contactToFind.name) ||
        (contact.name === contactToFind.name &&
          contact.daciRole === contactToFind.daciRole &&
          contact.role === contactToFind.role)
    );
  };

  // Handler for adding new DACI contacts
  const handleAddDACIContact = (contact: any) => {
    const contactAddr = contact as ContactEmailSource;
    // Format the new contact
    const newContact = {
      daciRole: contact.daciRole || contact.daciTitle || 'Driver',
      role: contact.role,
      name: contact.name,
      uid: contact.uid,
      email: resolveContactEmail(contactAddr),
      imageUrl: contact.imageUrl
    };

    // Optimistically update local state first
    setDaciContacts(prevData => {
      if (!prevData || !prevData.contacts) {
        // If no previous data, create initial structure
        return {
          contacts: [newContact],
          roles: {
            Driver: {
              title: 'Driver',
              description: 'Responsible for driving the project forward',
              displayOrder: 1
            },
            Contributor: {
              title: 'Contributor',
              description: 'Contributes to the project',
              displayOrder: 3
            },
            Approver: {
              title: 'Approver',
              description: 'Has authority to approve decisions',
              displayOrder: 2
            },
            Informed: {
              title: 'Informed',
              description: 'Kept informed of progress',
              displayOrder: 4
            }
          }
        };
      }

      // Add new contact to existing structure
      return {
        ...prevData,
        contacts: [...prevData.contacts, newContact]
      };
    });

    // 2. Make API call with only the new contact (without existing contacts)
    if (card.issueId) {
      updateDACIContactsWithIds(
        card.issueId,
        {
          contacts: [newContact] // Only send the new contact, not existing ones
        },
        response => {
          // Sync local state with server response - merge instead of replace
          if (response && response.daciContacts) {
            setDaciContacts(prevData => {
              if (!prevData) {
                // If no previous data, use server response
                return response.daciContacts;
              }

              // Merge server response with existing data
              // Server response should contain the complete updated daciContacts
              // including both existing and new contacts with proper IDs
              return response.daciContacts;
            });
          }

          // Trigger soft refresh to get the newly added contact with ID
          handleSeamlessDataRefresh(response, { refreshFeatures: false });
        },
        error => {
          // Rollback optimistic update on error
          setDaciContacts(prevData => {
            if (!prevData || !prevData.contacts) return prevData;
            return {
              ...prevData,
              contacts: prevData.contacts.filter(
                c => !(c.uid === newContact.uid && c.name === newContact.name)
              )
            };
          });
        }
      );
    }
  };

  // Handler for editing existing DACI contacts
  const handleEditDACIContact = (updatedContact: any, originalContact: any) => {
    const previousContactEmail = resolveContactEmail(originalContact);
    const updatedContactEmail = resolveContactEmail({
      ...originalContact,
      ...updatedContact,
      ...mergeContactEmailFields(
        originalContact as ContactEmailSource,
        updatedContact as ContactEmailSource
      )
    });
    const daciFieldChanges = [
      {
        field: 'daciRole',
        oldValue: originalContact.daciRole || '',
        newValue: updatedContact.daciRole || ''
      },
      {
        field: 'role',
        oldValue: originalContact.role || '',
        newValue: updatedContact.role || ''
      },
      {
        field: 'name',
        oldValue: originalContact.name || '',
        newValue: updatedContact.name || ''
      },
      {
        field: 'uid',
        oldValue: originalContact.uid || '',
        newValue: updatedContact.uid || ''
      },
      {
        field: 'email',
        oldValue: previousContactEmail || '',
        newValue: updatedContactEmail || ''
      }
    ].filter(change => change.oldValue !== change.newValue);

    setDaciContacts(prevData => {
      if (!prevData || !prevData.contacts) return prevData;

      const updatedContacts = prevData.contacts.map(contact => {
        if (
          contact.uid === originalContact.uid &&
          contact.name === originalContact.name
        ) {
          return {
            ...contact,
            daciRole: updatedContact.daciRole,
            role: updatedContact.role,
            name: updatedContact.name,
            uid: updatedContact.uid,
            imageUrl: updatedContact.imageUrl,
            email: resolveContactEmail({
              ...contact,
              ...updatedContact,
              ...mergeContactEmailFields(
                contact as ContactEmailSource,
                updatedContact as ContactEmailSource
              )
            })
          };
        }
        return contact;
      });

      return {
        ...prevData,
        contacts: updatedContacts
      };
    });

    // 2. Update backend
    if (card.issueId) {
      updateDACIContactsWithIds(
        card.issueId,
        {
          contacts: [
            {
              id: originalContact.id,
              daciRole: updatedContact.daciRole,
              role: updatedContact.role,
              name: updatedContact.name,
              uid: updatedContact.uid,
              imageUrl: updatedContact.imageUrl
            }
          ]
        },
        response => {
          // DACI contact updated successfully - trigger soft refresh
          handleSeamlessDataRefresh(response, { refreshFeatures: false });
        },
        error => {
          // Failed to update DACI contact
        }
      );
    }
  };

  // Handler for deleting DACI contacts
  const handleDeleteDACIContact = (contactToDelete: any) => {
    // âœ… Prevent duplicate delete operations
    const contactKey = `${contactToDelete.id || contactToDelete.uid}-${
      contactToDelete.name
    }`;
    if (deletingContactsRef.current.has(contactKey)) {
      return; // Exit early if delete is already in progress
    }

    // Mark contact as being deleted
    deletingContactsRef.current.add(contactKey);

    // Store original data for potential rollback
    const originalData = daciContacts;

    // Optimistic update: immediately remove from UI
    setDaciContacts(prevData => {
      if (!prevData || !prevData.contacts) return prevData;

      const updatedContacts = prevData.contacts.filter(contact => {
        // More robust contact matching - use id first, fallback to uid + name
        if (contactToDelete.id && contact.id) {
          return contact.id !== contactToDelete.id;
        }

        // Fallback to uid + name matching
        return !(
          contact.uid === contactToDelete.uid &&
          contact.name === contactToDelete.name
        );
      });

      const newData = {
        ...prevData,
        contacts: updatedContacts
      };

      return newData;
    });

    // 2. Update backend
    if (card.issueId && contactToDelete.id) {
      updateDACIContactsWithIds(
        card.issueId,
        {
          contacts: [
            {
              id: contactToDelete.id,
              daciRole: contactToDelete.daciRole,
              name: contactToDelete.name,
              imageUrl:
                contactToDelete.imageUrl || 'https://via.placeholder.com/32',
              _delete: true
            }
          ]
        },
        response => {
          // Refresh data from response to ensure UI matches backend state
          if (response && response.daciContacts) {
            setDaciContacts(response.daciContacts);
          }

          // âœ… Remove from tracking set after successful delete
          deletingContactsRef.current.delete(contactKey);

          // Trigger soft refresh to ensure data is up to date
          handleSeamlessDataRefresh(response, { refreshFeatures: false });
        },
        error => {
          // Rollback optimistic update on error
          if (originalData) {
            setDaciContacts(originalData);
          }

          // âœ… Remove from tracking set on error
          deletingContactsRef.current.delete(contactKey);
        }
      );
    } else {
      // Rollback optimistic update if we can't make the backend call
      if (originalData) {
        setDaciContacts(originalData);
      }

      // âœ… Remove from tracking set if can't make API call
      deletingContactsRef.current.delete(contactKey);
    }
  };

  // Product group codes for this moment â€” passed to MilestoneMatrixFromAPI so the
  // edit modal can fetch default approvers from the template timeline config.
  const getMomentProductGroupCodes = (): string[] => {
    const raw = (getDataWithFallback() as any)?.productGroups || [];
    return raw.map((g: any) =>
      typeof g === 'string' ? g : g.name || g.label || String(g)
    );
  };

  // For non-phased milestone matrix
  /**
   * Moment-level context for the Send-for-Approval subject, sourced from the
   * `generalInfo` of the phase the milestones belong to (or the moment root for
   * non-phased milestones). Release-date resolution by moment type is handled
   * downstream in {@link buildSlcApprovalSubject}.
   */
  const buildSendForApprovalContext = (
    phaseId?: string
  ): SlcApprovalSubjectContext => {
    const generalInfo = getGeneralInfo(phaseId) as any;
    let dates = generalInfo?.dates as SlcSubjectMomentDates | undefined;

    // "Price Changes and Offers" and "End of Life (EOL)" moments keep their
    // milestones at the moment root (outside the release phases — VIP, Mobile, …),
    // but the effective date used for the SLC subject lives on each phase. Pull
    // that phase's dates: the phase named in the URL (?phaseId=) when present,
    // otherwise the first active phase.
    if (
      momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS ||
      isEolMoment(momentType)
    ) {
      const targetPhaseId = phaseId || propReleasePhaseId;
      const targetPhase =
        (targetPhaseId && normalizedPhases.find(p => p.id === targetPhaseId)) ||
        normalizedPhases.find(isPhaseActive) ||
        normalizedPhases[0];
      const phaseDates = targetPhase?.generalInfo?.dates as
        | SlcSubjectMomentDates
        | undefined;
      if (phaseDates) {
        dates = { ...dates, ...phaseDates };
      }
    }

    return {
      momentName,
      momentType,
      pco: (card as any)?.pco,
      dates
    };
  };

  const renderMilestoneMatrix = () => {
    const milestones = getMilestones();
    const milestoneArray = getMilestoneArray();

    const dataForDebug = getDataWithFallback() as any;
    // console.log('ðŸ” renderMilestoneMatrix Debug:', {

    // Don't render if no issueId available
    if (!milestones?.issueId && !card?.issueId) {
      return null;
    }

    const phaseKey = `nonphased-${card?.issueId}`;
    return (
      <SlideoutMilestoneMatrix
        matrixKey={`milestones-nonphased-${card?.issueId}`}
        refreshToken={milestoneUpdateTrigger}
        milestones={milestoneArray}
        onMilestoneUpdate={handleMilestoneUpdate}
        sortProps={getMilestoneMatrixSortProps(phaseKey)}
        onDataRefresh={handleSeamlessDataRefresh}
        onNotificationRefresh={refreshMomentNotifications}
        isCommentPanelOpen={isCommentPanelOpen}
        isEditable={true}
        issueId={milestones?.issueId || card?.issueId!}
        milestoneId={getMilestoneId()}
        isFullScreen={isFullScreen}
        tier={(() => {
          const data = getDataWithFallback() as any;
          if (hasMomentLevelMilestones) {
            return resolveSharedRootMilestoneTierOrDefault(data);
          }
          return getGeneralInfo()?.tier || 'L1';
        })()}
        momentType={momentType}
        pco={(card as any)?.pco}
        currentOverviewMvpStatusContent={
          (adaptedOverviewData as any)?.mvpStatus?.content ?? ''
        }
        productGroups={getMomentProductGroupCodes()}
        isOpen={isOpen}
        phases={phasesForScrollPriority}
        sendForApprovalContext={buildSendForApprovalContext()}
        onOpenFeedback={openMilestoneFeedback}
      />
    );
  };

  // Render inherited parent milestones with special styling
  const renderInheritedMilestones = (phaseId?: string) => {
    const parentMilestones = getParentMilestones(phaseId);
    const milestones = getMilestones(phaseId);
    const isInherited = areMilestonesInherited(phaseId);

    // If inheritance is enabled but no parent milestones, show placeholder
    if (isInherited && parentMilestones.length === 0) {
      return (
        <SlideoutInheritedMilestonesPlaceholder
          wrapperClassName={styles.inheritedWrapper}
          placeholderClassName={styles.noMilestones}
        />
      );
    }

    if (parentMilestones.length === 0) {
      return null;
    }

    // Don't render if no issueId available
    if (!milestones?.issueId && !card?.issueId) {
      return null;
    }

    // Get parent moment ID from general info for MVP navigation
    const generalInfo = getGeneralInfo(phaseId);
    const parentMomentId = generalInfo?.parentMoment?.id || null;

    // Get phase name from normalizedPhases for PATCH payloads
    const phase = normalizedPhases.find(p => p.id === phaseId);
    const phaseName = phase?.phaseName || undefined;

    const phaseKey = phaseId
      ? `${phaseId}-${card?.issueId}`
      : `inherited-${card?.issueId}`;
    return (
      <SlideoutMilestoneMatrix
        matrixKey={`inherited-milestones-${phaseId || 'root'}-${card?.issueId}`}
        refreshToken={milestoneUpdateTrigger}
        sortProps={getMilestoneMatrixSortProps(phaseKey)}
        milestones={parentMilestones}
        isCommentPanelOpen={isCommentPanelOpen}
        onMilestoneUpdate={handleMilestoneUpdate}
        onDataRefresh={handleSeamlessDataRefresh}
        onNotificationRefresh={refreshMomentNotifications}
        isEditable={false}
        issueId={milestones?.issueId || card?.issueId!}
        milestoneId={getMilestoneId(phaseId)}
        isFullScreen={isFullScreen}
        tier={getGeneralInfo(phaseId)?.tier || 'L1'}
        alertHighlightScope="inherited"
        parentMomentId={parentMomentId}
        phaseName={phaseName}
        phaseId={phaseId}
        momentType={momentType}
        pco={(card as any)?.pco}
        currentOverviewMvpStatusContent={
          (adaptedOverviewData as any)?.mvpStatus?.content ?? ''
        }
        productGroups={getMomentProductGroupCodes()}
        sendForApprovalContext={buildSendForApprovalContext(phaseId)}
        wrapperClassName={styles.inheritedWrapper}
        isOpen={isOpen}
        phases={phasesForScrollPriority}
        // `onOpenFeedback` deliberately omitted here (unlike the other two
        // matrix instances) - these milestones are inherited from a
        // *different*, parent moment, not `card` itself. `openMilestoneFeedback`
        // builds its `releaseMoment`/`issueId` from `card`, which would be
        // wrong for a feedback thread that actually belongs to the parent
        // moment. MilestoneMatrixFromAPI.tsx only renders the Feedback icon
        // when `onOpenFeedback` is provided, so it simply doesn't show here.
      />
    );
  };

  // Render release phase milestones
  const renderReleasePhaseMilestones = (phaseId?: string) => {
    const releaseMilestones = getReleasePhaseMilestones(phaseId);
    const milestones = getMilestones(phaseId);

    if (releaseMilestones.length === 0) {
      return null;
    }

    // Get phase name from normalizedPhases for PATCH payloads
    const phase = normalizedPhases.find(p => p.id === phaseId);
    const phaseName = phase?.phaseName || undefined;

    const phaseKey = phaseId
      ? `${phaseId}-${card?.issueId}`
      : `release-${card?.issueId}`;
    return (
      <SlideoutMilestoneMatrix
        matrixKey={`release-milestones-${phaseId || 'root'}-${card?.issueId}`}
        refreshToken={milestoneUpdateTrigger}
        sortProps={getMilestoneMatrixSortProps(phaseKey)}
        milestones={releaseMilestones}
        isCommentPanelOpen={isCommentPanelOpen}
        onMilestoneUpdate={handleMilestoneUpdate}
        onDataRefresh={handleSeamlessDataRefresh}
        onNotificationRefresh={refreshMomentNotifications}
        isEditable={true}
        issueId={milestones?.issueId || card?.issueId!}
        milestoneId={getMilestoneId(phaseId)}
        isFullScreen={isFullScreen}
        phaseName={phaseName}
        phaseId={phaseId}
        tier={getGeneralInfo(phaseId)?.tier || 'L1'}
        momentType={momentType}
        pco={(card as any)?.pco}
        currentOverviewMvpStatusContent={
          (adaptedOverviewData as any)?.mvpStatus?.content ?? ''
        }
        productGroups={getMomentProductGroupCodes()}
        isOpen={isOpen}
        phases={phasesForScrollPriority}
        sendForApprovalContext={buildSendForApprovalContext(phaseId)}
        onOpenFeedback={openMilestoneFeedback}
      />
    );
  };

  const handleDeleteMilestones = async (
    phaseId?: string,
    phaseName?: string
  ) => {
    const milestoneData = getMilestones(phaseId) as any;
    const milestoneSnapshot = Array.isArray(milestoneData?.milestones)
      ? milestoneData.milestones[0]
      : Array.isArray(milestoneData)
      ? milestoneData[0]
      : milestoneData?.milestones?.[0] ?? milestoneData;

    const deleted = await deleteMilestonesHandler({ phaseId });
    if (!deleted || !milestoneSnapshot || !card?.issueId) return;
  };

  const handleMilestoneUpdate = (
    milestoneIndex: number,
    updatedMilestone: any
  ) => {
    // console.log('ðŸ”„ Parent: Updating milestone data in apiData state');
    // console.log('ðŸ“Š Parent: Updated milestone:', updatedMilestone);
    // console.log('ðŸ“ Parent: Milestone index:', milestoneIndex);

    // Update the milestone data in the apiData state
    setApiData(prevApiData => {
      if (!prevApiData) return prevApiData;

      const updatedApiData = { ...prevApiData };

      // Handle phased data structure
      if (hasPhases && updatedApiData.releasePhases) {
        updatedApiData.releasePhases = updatedApiData.releasePhases.map(
          phase => {
            if (phase.milestones?.milestones) {
              // Handle new array structure with milestone groups
              if (
                Array.isArray(phase.milestones.milestones) &&
                phase.milestones.milestones.some(item => item.milestones)
              ) {
                const updatedMilestoneGroups = [...phase.milestones.milestones];
                let updated = false;
                let currentIndex = milestoneIndex;

                for (
                  let groupIndex = 0;
                  groupIndex < updatedMilestoneGroups.length;
                  groupIndex++
                ) {
                  const group = updatedMilestoneGroups[groupIndex];
                  if (Array.isArray(group.milestones)) {
                    const groupMilestones = [...group.milestones];
                    if (currentIndex < groupMilestones.length) {
                      groupMilestones[currentIndex] = updatedMilestone;
                      updatedMilestoneGroups[groupIndex] = {
                        ...group,
                        milestones: groupMilestones
                      };
                      updated = true;
                      break;
                    }
                    currentIndex -= groupMilestones.length;
                  }
                }

                return {
                  ...phase,
                  milestones: {
                    ...phase.milestones,
                    milestones: updatedMilestoneGroups
                  }
                };
              }
              // Handle legacy array structure
              else if (Array.isArray(phase.milestones.milestones)) {
                const updatedMilestones = [...phase.milestones.milestones];
                if (
                  milestoneIndex >= 0 &&
                  milestoneIndex < updatedMilestones.length
                ) {
                  updatedMilestones[milestoneIndex] = updatedMilestone;
                  // console.log(
                }
                return {
                  ...phase,
                  milestones: {
                    ...phase.milestones,
                    milestones: updatedMilestones
                  }
                };
              }
            }
            return phase;
          }
        );
      }
      // Handle non-phased data structure
      else if (updatedApiData.milestones?.milestones) {
        // Handle new array structure with milestone groups
        if (
          Array.isArray(updatedApiData.milestones.milestones) &&
          updatedApiData.milestones.milestones.some(item => item.milestones)
        ) {
          const updatedMilestoneGroups = [
            ...updatedApiData.milestones.milestones
          ];
          let updated = false;
          let currentIndex = milestoneIndex;

          for (
            let groupIndex = 0;
            groupIndex < updatedMilestoneGroups.length;
            groupIndex++
          ) {
            const group = updatedMilestoneGroups[groupIndex];
            if (Array.isArray(group.milestones)) {
              const groupMilestones = [...group.milestones];
              if (currentIndex < groupMilestones.length) {
                groupMilestones[currentIndex] = updatedMilestone;
                updatedMilestoneGroups[groupIndex] = {
                  ...group,
                  milestones: groupMilestones
                };
                updated = true;
                break;
              }
              currentIndex -= groupMilestones.length;
            }
          }

          if (updated) {
            // console.log(

            updatedApiData.milestones = {
              ...updatedApiData.milestones,
              milestones: updatedMilestoneGroups
            };
          }
        }
        // Handle legacy array structure
        else if (Array.isArray(updatedApiData.milestones.milestones)) {
          const updatedMilestones = [...updatedApiData.milestones.milestones];
          if (
            milestoneIndex >= 0 &&
            milestoneIndex < updatedMilestones.length
          ) {
            updatedMilestones[milestoneIndex] = updatedMilestone;
            // console.log(

            updatedApiData.milestones = {
              ...updatedApiData.milestones,
              milestones: updatedMilestones
            };
          }
        }
      }

      return updatedApiData;
    });

    // Trigger a re-render to show the updated data in the UI
    setMilestoneUpdateTrigger(prev => prev + 1);
    // console.log(

    // Optional: You could add success notifications here
    // showSuccessToast('Milestone updated successfully');
  };

  const getTiersArray = () => {
    // For now, return a default set of tiers
    // In a real implementation, this would come from your data
    const generalInfo = getGeneralInfo();
    const tier = generalInfo?.tier || 'L1';
    return [tier];
  };

  const content = (
    <MomentProvider
      momentName={momentName}
      momentType={momentType}
      issueId={card?.issueId || ''}
    >
      <Provider theme={defaultTheme} colorScheme="light">
        <RBACProvider permissionData={permissionData}>
          {/* Overlay: only render when not in full screen */}
          {!isFullScreen && (
            <div
              data-testid="slideout-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)', // Darker overlay
                backdropFilter: 'blur(4px)', // Blur effect for scrim
                WebkitBackdropFilter: 'blur(4px)', // Safari support
                zIndex: 10050, //Since header has 10016 , give more than that
                opacity: isOpen && !isClosing ? 1 : 0,
                visibility: isOpen && !isClosing ? 'visible' : 'hidden',
                transition:
                  'opacity 0.4s ease-in-out, visibility 0.4s ease-in-out, backdrop-filter 0.4s ease-in-out',
                cursor: 'pointer'
              }}
              onClick={handleCloseWithRefresh}
            />
          )}

          {/* Panel */}
          <div
            data-testid="slideout-panel"
            className={`${styles.slideOutPanel} slideout-panel ${
              showSlideAnimation ? styles.open : ''
            } ${isFullScreen ? styles.fullScreen : ''} ${
              isCommentPanelOpen ? styles.shiftedLeft : ''
            } ${isPrinting ? styles.printMode : ''}`}
          >
            {/* Smooth Loading Overlay */}
            {isInitialLoading && (
              <div
                data-testid="slideout-loading-overlay"
                className={styles.loadingOverlay}
                style={{
                  opacity: loadingProgress < 100 ? 1 : 0,
                  transition: 'opacity 0.15s ease-out'
                }}
              >
                <div className={styles.loadingContent}>
                  <div className={styles.loadingSpinner}></div>
                  <div className={styles.loadingText}>Loading...</div>
                  <div className={styles.loadingProgress}>
                    <div
                      className={styles.loadingProgressBar}
                      style={{ width: `${loadingProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            {/* Main Content Container */}
            <div
              data-testid="slideout-main-content"
              className={styles.mainContent}
              style={{
                opacity: isInitialLoading ? 0.5 : 1,
                transition: 'opacity 0.15s ease-out'
              }}
            >
              <SlideoutHeaderShell
                headerProps={{
                  title: displayTitle,
                  rtmPhaseTitleLabel: getPhasedOfferPhaseStripLabel({
                    momentType: card?.momentType || card?.projectType,
                    releasePhase: card?.releasePhase,
                    releasePhaseId: card?.releasePhaseId,
                    releasePhases: Array.isArray(
                      getDataWithFallback()?.releasePhases
                    )
                      ? getDataWithFallback()!.releasePhases
                      : null
                  }),
                  eolReleasePhase: card?.releasePhase,
                  categories,
                  onClose: handleCloseWithRefresh,
                  isFullScreen,
                  onMaximize: isFullScreen ? handleMinimize : handleMaximize,
                  MaximizeIcon: isFullScreen ? MinimizeIcon : MaximizeIcon,
                  issueId: card?.issueId,
                  onCopySuccess: handleCopySuccess,
                  onTitleUpdate: handleTitleUpdate,
                  onNavigatePrevious: getPreviousMomentId(card?.issueId || '')
                    ? handleNavigatePrevious
                    : undefined,
                  onNavigateNext: getNextMomentId(card?.issueId || '')
                    ? handleNavigateNext
                    : undefined,
                  productIcon: (
                    <Image
                      data-testid="slideout-header-product-icon"
                      src={getCategoryIcon(categories, (card as any).products)}
                      alt={
                        Array.isArray(categories) ? categories.join(', ') : ''
                      }
                      width="32px"
                      height="32px"
                    />
                  ),
                  products: (card as any).products || [],
                  selectedProducts,
                  onProductSelectionChange: handleProductSelectionChange,
                  availableProducts,
                  enableProductDropdown,
                  productGroups: (() => {
                    const data = getDataWithFallback();
                    const pg = data?.productGroups;
                    return Array.isArray(pg) ? pg : [];
                  })(),
                  isCommentPanelOpen,
                  momentType:
                    card?.momentType ||
                    card?.projectType ||
                    (card as any)?.type,
                  surfaces: (() => {
                    const data = getDataWithFallback();
                    const s = data?.surfaces;
                    return Array.isArray(s) ? s : [];
                  })(),
                  genai: (() => {
                    const mt =
                      card?.momentType ||
                      card?.projectType ||
                      (card as any)?.type;
                    const pco = (card as any)?.pco;
                    if (!momentTypeSupportsGenAi(mt, { pco })) return undefined;
                    const val = getDataWithFallback()?.genai;
                    return val == null ? undefined : val;
                  })(),
                  consumesCredit: (() => {
                    const mt =
                      card?.momentType ||
                      card?.projectType ||
                      (card as any)?.type;
                    const pco = (card as any)?.pco;
                    if (!momentTypeSupportsGenAi(mt, { pco })) return undefined;
                    const val = getDataWithFallback()?.consumesCredit;
                    return val == null ? undefined : val;
                  })(),
                  parId: (() => {
                    const data = getDataWithFallback();
                    return (data as any)?.parId;
                  })(),
                  onParIdChange: async value => {
                    if (!card?.issueId) return;
                    try {
                      const patchResponse = await updateMoment(card.issueId, {
                        parId: value.slice(0, PAR_ID_MAX_LENGTH)
                      });
                      await handleSeamlessDataRefresh(patchResponse);
                    } catch (error) {
                      console.error('Failed to update PAR ID:', error);
                      ToastQueue.negative(
                        'Failed to update PAR ID. Please try again.',
                        { timeout: 5000 }
                      );
                    }
                  },
                  onGenAiChange: async value => {
                    if (!card?.issueId) return;

                    try {
                      const patchResponse = await updateMoment(card.issueId, {
                        genai: value
                      });

                      await handleSeamlessDataRefresh(patchResponse);
                    } catch (error) {
                      console.error('Failed to update GenAI:', error);
                      ToastQueue.negative(
                        'Failed to update GenAI. Please try again.',
                        { timeout: 5000 }
                      );
                    }
                  },
                  onMomentTypeChange: async newType => {
                    await updateMomentType({ newMomentType: newType });
                  },
                  onConsumesCreditChange: async value => {
                    if (!card?.issueId) return;

                    try {
                      const patchResponse = await updateMoment(card.issueId, {
                        consumesCredit: value
                      });

                      await handleSeamlessDataRefresh(patchResponse);
                    } catch (error) {
                      console.error('Failed to update Consumes Credit:', error);
                      ToastQueue.negative(
                        'Failed to update Consumes Credit. Please try again.',
                        { timeout: 5000 }
                      );
                    }
                  },
                  isCloneModalOpen,
                  onCloneModalOpenChange: setIsCloneModalOpen,
                  onCloneClick: handleCloneClick,
                  onExportPdf: handleExportPdfClick,
                  showBottomBorder: activeSection === 'ai'
                }}
                showNewItemBanner={showNewItemBanner}
                onDismissNewItemBanner={onDismissNewItemBanner}
                showErrorBanner={showErrorBanner}
                errorMessage={errorMessage}
                onDismissErrorBanner={() => {
                  setShowErrorBanner(false);
                  setErrorMessage('');
                }}
              />

              {/* Content */}
              <SlideoutMainContentSection
                activeSection={activeSection}
                canAccessAiAssistant={canAccessAiAssistant}
                hasPhases={hasPhases}
                phasedAccordion={
                  <SlideoutCustomAccordionSection
                    normalizedPhases={normalizedPhases}
                    customExpandedPhases={customExpandedPhases}
                    momentType={momentType}
                    momentName={momentName}
                    card={card}
                    apiData={apiData}
                    hasMomentLevelMilestones={hasMomentLevelMilestones}
                    rawMomentTypeForMilestones={_rawMomentTypeForMilestones}
                    areAllSectionsCollapsed={areAllSectionsCollapsed}
                    getGeneralInfo={getGeneralInfo}
                    flattenPrimaryContacts={flattenPrimaryContacts}
                    getMilestones={getMilestones}
                    togglePhase={togglePhase}
                    canEditGeneralInfo={canEditGeneralInfo}
                    canEditMilestones={canEditMilestones}
                    rtmButtonRefs={rtmButtonRefs}
                    handleRtmToggle={handleRtmToggle}
                    renderRtmDropdownPortal={phaseId => (
                      <RtmDropdownPortal phaseId={phaseId} />
                    )}
                    statusButtonRefs={statusButtonRefs}
                    handleStatusToggle={handleStatusToggle}
                    formatStatusUpdatedDate={formatStatusUpdatedDate}
                    renderStatusDropdownPortal={phaseId => (
                      <StatusDropdownPortal phaseId={phaseId} />
                    )}
                    openDateChipPhaseId={openDateChipPhaseId}
                    setOpenDateChipPhaseId={setOpenDateChipPhaseId}
                    getAccordionDateDisplay={getAccordionDateDisplay}
                    getAccordionCalendarProps={getAccordionCalendarProps}
                    getDateChipTooltip={getDateChipTooltip}
                    handleAccordionDateChange={handleAccordionDateChange}
                    tierButtonRefs={tierButtonRefs}
                    handleTierToggle={handleTierToggle}
                    renderTierDropdownPortal={phaseId => (
                      <TierDropdownPortal phaseId={phaseId} />
                    )}
                    revenueImpactButtonRefs={revenueImpactButtonRefs}
                    handleRevenueImpactToggle={handleRevenueImpactToggle}
                    getRevenueImpactDisplayValue={getRevenueImpactDisplayValue}
                    renderRevenueImpactDropdownPortal={phaseId => (
                      <RevenueImpactDropdownPortal phaseId={phaseId} />
                    )}
                    riskButtonRefs={riskButtonRefs}
                    handleRiskLevelToggle={handleRiskLevelToggle}
                    getRiskLevelIcon={getRiskLevelIcon}
                    renderRiskLevelDropdownPortal={phaseId => (
                      <RiskLevelDropdownPortal phaseId={phaseId} />
                    )}
                    expandedInfoSection={expandedInfoSection}
                    toggleInfoSection={toggleInfoSection}
                    handleGeneralInfoCommentIconClick={
                      handleGeneralInfoCommentIconClick
                    }
                    handleMilestonesCommentIconClick={
                      handleMilestonesCommentIconClick
                    }
                    eventsData={eventsData}
                    isLoadingEvents={isLoadingEvents}
                    eventsError={eventsError}
                    parentMomentsData={parentMomentsData}
                    isLoadingParentMoments={isLoadingParentMoments}
                    parentMomentsError={parentMomentsError}
                    resolveMomentType={resolveMomentType}
                    handleSeamlessDataRefreshWithoutFeatures={
                      handleSeamlessDataRefreshWithoutFeatures
                    }
                    handleSeamlessDataRefresh={handleSeamlessDataRefresh}
                    onLoadAlertsAndNotifications={() =>
                      dispatch(alertsActions.loadAlertsAndNotifications())
                    }
                    onPhasedGeneralInfoChange={(id, updatedInfo) => {
                      setApiData(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          releasePhases:
                            prev.releasePhases?.map(p =>
                              p.id === id
                                ? {
                                    ...p,
                                    generalInfo: {
                                      ...p.generalInfo,
                                      ...updatedInfo,
                                      ...(updatedInfo.dates && {
                                        dates: {
                                          ...p.generalInfo?.dates,
                                          ...updatedInfo.dates
                                        }
                                      })
                                    }
                                  }
                                : p
                            ) || []
                        };
                      });
                    }}
                    refreshMomentNotifications={refreshMomentNotifications}
                    handleOpenPrimaryContactPopup={
                      handleOpenPrimaryContactPopup
                    }
                    onRoleContactsReplace={async (
                      phaseId,
                      phaseName,
                      role,
                      roleContacts
                    ) => {
                      const manager = createPrimaryContactsManagerForPhase(
                        phaseId,
                        phaseName
                      );
                      await manager.replaceRoleContacts(role, roleContacts);
                    }}
                    onContactUpdate={async (
                      phaseId,
                      phaseName,
                      updatedContact,
                      index
                    ) => {
                      const manager = createPrimaryContactsManagerForPhase(
                        phaseId,
                        phaseName
                      );
                      await manager.updateContact(updatedContact, index);
                    }}
                    areMilestonesInherited={areMilestonesInherited}
                    shouldShowAddMilestoneButton={shouldShowAddMilestoneButton}
                    getReleasePhaseMilestones={getReleasePhaseMilestones}
                    renderInheritedMilestones={renderInheritedMilestones}
                    renderReleasePhaseMilestones={renderReleasePhaseMilestones}
                    handleSortToggle={handleSortToggle}
                    getSortDescriptiveText={getSortDescriptiveText}
                    sortDropdownOpen={sortDropdownOpen}
                    getEffectiveMilestoneSort={getEffectiveMilestoneSort}
                    handleSortSelect={handleSortSelect}
                    milestoneUpdateTrigger={milestoneUpdateTrigger}
                    milestoneMenuOpen={milestoneMenuOpen}
                    handleMilestoneMenuToggle={handleMilestoneMenuToggle}
                    handleDeleteMilestones={handleDeleteMilestones}
                    seamlessRefreshAndNotifications={
                      seamlessRefreshAndNotifications
                    }
                    availableRtmOptions={availableRtmOptions}
                    addPhaseButtonRef={addPhaseButtonRef}
                    addPhaseLoading={addPhaseLoading}
                    addPhaseDropdownOpen={addPhaseDropdownOpen}
                    setAddPhaseDropdownOpen={setAddPhaseDropdownOpen}
                    handleAddPhase={handleAddPhase}
                  />
                }
                nonPhasedAccordion={
                  <SlideoutNonPhasedLayoutSection
                    card={card}
                    apiData={apiData}
                    generalInfo={getGeneralInfo()}
                    contacts={flattenPrimaryContacts()}
                    momentName={momentName}
                    isNonPhasedAccordionExpanded={isNonPhasedAccordionExpanded}
                    areAllSectionsCollapsed={areAllSectionsCollapsed}
                    toggleNonPhasedAccordion={toggleNonPhasedAccordion}
                    toggleGeneralInfo={toggleGeneralInfo}
                    canEditGeneralInfo={canEditGeneralInfo}
                    canEditMilestones={canEditMilestones}
                    expandedInfoSection={expandedInfoSection}
                    toggleInfoSection={toggleInfoSection}
                    statusButtonRefs={statusButtonRefs}
                    handleStatusToggle={handleStatusToggle}
                    formatStatusUpdatedDate={formatStatusUpdatedDate}
                    nonPhasedDateChipOpen={nonPhasedDateChipOpen}
                    setNonPhasedDateChipOpen={setNonPhasedDateChipOpen}
                    getAccordionDateDisplay={getAccordionDateDisplay}
                    getAccordionCalendarProps={getAccordionCalendarProps}
                    getDateChipTooltip={getDateChipTooltip}
                    handleAccordionDateChange={handleAccordionDateChange}
                    tierButtonRefs={tierButtonRefs}
                    handleTierToggle={handleTierToggle}
                    revenueImpactButtonRefs={revenueImpactButtonRefs}
                    handleRevenueImpactToggle={handleRevenueImpactToggle}
                    getRevenueImpactDisplayValue={getRevenueImpactDisplayValue}
                    riskButtonRefs={riskButtonRefs}
                    handleRiskLevelToggle={handleRiskLevelToggle}
                    getRiskLevelIcon={getRiskLevelIcon}
                    renderStatusDropdownPortal={momentId => (
                      <MomentStatusDropdownPortal momentId={momentId} />
                    )}
                    renderTierDropdownPortal={momentId => (
                      <MomentTierDropdownPortal momentId={momentId} />
                    )}
                    renderRevenueImpactDropdownPortal={phaseId => (
                      <RevenueImpactDropdownPortal phaseId={phaseId} />
                    )}
                    renderRiskLevelDropdownPortal={momentId => (
                      <MomentRiskLevelDropdownPortal momentId={momentId} />
                    )}
                    eventsData={eventsData}
                    isLoadingEvents={isLoadingEvents}
                    eventsError={eventsError}
                    parentMomentsData={parentMomentsData}
                    isLoadingParentMoments={isLoadingParentMoments}
                    parentMomentsError={parentMomentsError}
                    handleGeneralInfoCommentIconClick={
                      handleGeneralInfoCommentIconClick
                    }
                    resolveMomentType={resolveMomentType}
                    handleSeamlessDataRefreshWithoutFeatures={
                      handleSeamlessDataRefreshWithoutFeatures
                    }
                    handleSeamlessDataRefresh={handleSeamlessDataRefresh}
                    onLoadAlertsAndNotifications={() =>
                      dispatch(alertsActions.loadAlertsAndNotifications())
                    }
                    onGeneralInfoChange={updatedInfo => {
                      setApiData(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          generalInfo: {
                            ...prev.generalInfo,
                            ...updatedInfo,
                            ...(updatedInfo.dates && {
                              dates: {
                                ...prev.generalInfo?.dates,
                                ...updatedInfo.dates
                              }
                            })
                          }
                        };
                      });
                    }}
                    refreshMomentNotifications={refreshMomentNotifications}
                    handleOpenPrimaryContactPopup={
                      handleOpenPrimaryContactPopup
                    }
                    onRoleContactsReplace={async (role, roleContacts) => {
                      const manager =
                        createPrimaryContactsManagerForNonPhased();
                      await manager.replaceRoleContacts(role, roleContacts);
                    }}
                    onContactUpdate={async (updatedContact, index) => {
                      const manager =
                        createPrimaryContactsManagerForNonPhased();
                      await manager.updateContact(updatedContact, index);
                    }}
                    isMilestonesExpanded={isMilestonesExpanded}
                    getMilestoneArray={getMilestoneArray}
                    handleSortToggle={handleSortToggle}
                    getSortDescriptiveText={getSortDescriptiveText}
                    sortDropdownOpen={sortDropdownOpen}
                    getEffectiveMilestoneSort={getEffectiveMilestoneSort}
                    handleSortSelect={handleSortSelect}
                    milestoneUpdateTrigger={milestoneUpdateTrigger}
                    milestoneMenuOpen={milestoneMenuOpen}
                    handleMilestoneMenuToggle={handleMilestoneMenuToggle}
                    handleDeleteMilestones={handleDeleteMilestones}
                    hasPhases={hasPhases}
                    hasMomentLevelMilestones={hasMomentLevelMilestones}
                    areMilestonesInherited={areMilestonesInherited}
                    shouldShowAddMilestoneButton={shouldShowAddMilestoneButton}
                    getReleasePhaseMilestones={getReleasePhaseMilestones}
                    userMilestoneAdded={userMilestoneAdded}
                    renderMilestoneMatrix={renderMilestoneMatrix}
                    renderInheritedMilestones={renderInheritedMilestones}
                    renderReleasePhaseMilestones={renderReleasePhaseMilestones}
                    getMilestones={getMilestones}
                    seamlessRefreshAndNotifications={
                      seamlessRefreshAndNotifications
                    }
                  />
                }
                featuresContent={renderFeaturesSection()}
                areAllSectionsCollapsed={areAllSectionsCollapsed}
                hasMomentLevelMilestones={hasMomentLevelMilestones}
                isMilestonesExpanded={isMilestonesExpanded}
                toggleMilestones={toggleMilestones}
                handleGeneralInfoCommentIconClick={
                  handleGeneralInfoCommentIconClick
                }
                handleSortToggle={handleSortToggle}
                getSortDescriptiveText={getSortDescriptiveText}
                sortDropdownOpen={sortDropdownOpen}
                getEffectiveMilestoneSort={getEffectiveMilestoneSort}
                handleSortSelect={handleSortSelect}
                getMilestoneArray={getMilestoneArray}
                milestoneUpdateTrigger={milestoneUpdateTrigger}
                milestoneMenuOpen={milestoneMenuOpen}
                handleMilestoneMenuToggle={handleMilestoneMenuToggle}
                handleDeleteMilestones={handleDeleteMilestones}
                card={card}
                renderMilestoneMatrix={renderMilestoneMatrix}
                shouldShowAddMilestoneButton={shouldShowAddMilestoneButton}
                getMilestones={getMilestones}
                seamlessRefreshAndNotifications={
                  seamlessRefreshAndNotifications
                }
                momentName={momentName}
                momentType={momentType}
                canEditMilestones={canEditMilestones}
                isOverviewExpanded={isOverviewExpanded}
                toggleOverview={toggleOverview}
                renderOverviewSection={renderOverviewSection}
                getDataWithFallback={getDataWithFallback}
                featuresRef={featuresRef}
                featuresContainerRef={featuresContainerRef}
                targetFeatureId={targetFeatureId}
                targetProductId={targetProductId}
                navigationContext={navigationContext}
                printMode={isPrinting}
                handleSeamlessDataRefreshWithoutFeatures={
                  handleSeamlessDataRefreshWithoutFeatures
                }
                refreshMomentNotifications={refreshMomentNotifications}
                isDecisionsExpanded={isDecisionsExpanded}
                toggleDecisions={toggleDecisions}
                handleOpenDecisionPopup={handleOpenDecisionPopup}
                addDecisionRef={addDecisionRef}
                setErrorMessage={setErrorMessage}
                setShowErrorBanner={setShowErrorBanner}
                seamlessRefreshWithoutFeatures={seamlessRefreshWithoutFeatures}
                isDACIExpanded={isDACIExpanded}
                toggleDACI={toggleDACI}
                handleOpenDACIPopup={handleOpenDACIPopup}
                daciContacts={daciContacts}
                handleAddDACIContact={handleAddDACIContact}
                handleEditDACIContact={handleEditDACIContact}
                handleDeleteDACIContact={handleDeleteDACIContact}
                isAdditionalInfoExpanded={isAdditionalInfoExpanded}
                toggleAdditionalInfo={toggleAdditionalInfo}
                handleOpenAdditionalInfoPopup={handleOpenAdditionalInfoPopup}
                additionalInfoRef={additionalInfoRef}
                apiDataUpdater={apiDataUpdater}
                isChangeLogExpanded={isChangeLogExpanded}
                toggleChangeLog={toggleChangeLog}
                isOpen={isOpen}
                phases={phasesForScrollPriority}
              />
            </div>

            {/* Side Navigation */}
            <nav className={styles.sideNavigation}>
              <SlideoutNotificationBell
                notificationPopoverRef={notificationPopoverRef}
                notificationButtonRef={notificationButtonRef}
                isNotificationModalOpen={isNotificationModalOpen}
                onToggle={() =>
                  setIsNotificationModalOpen(!isNotificationModalOpen)
                }
                onClose={() => setIsNotificationModalOpen(false)}
                alertCount={alertCount}
                activeIssues={activeIssues}
                momentName={card?.name || card?.title || 'Unknown'}
                onIssueClick={issue => {
                  if (card?.issueId && issue.fieldRef != null) {
                    expandSectionForFieldRef(
                      issue.fieldRef,
                      issue.ruleId,
                      card.issueId
                    );
                    setTimeout(() => {
                      highlightField({
                        issueId: card.issueId!,
                        fieldRef: issue.fieldRef,
                        ruleId: issue.ruleId,
                        scrollToField: true,
                        skipExpandSection: false
                      });
                      focusAlertFieldElementWithRetry(
                        card.issueId!,
                        issue.fieldRef,
                        issue.ruleId
                      );
                    }, 150);
                  }
                  setIsNotificationModalOpen(false);
                }}
              />

              <SideNavTooltip label="RSD Overview">
                <button
                  type="button"
                  className={activeSection === 'view' ? styles.active : ''}
                  onClick={handleViewSectionClick}
                  aria-label="RSD Overview"
                  aria-pressed={activeSection === 'view'}
                  data-testid="slide-out-side-nav-view-button"
                >
                  <ViewCardContent width="20" height="20" />
                </button>
              </SideNavTooltip>
              {/* <button
              className={activeSection === 'features' ? styles.active : ''}
              onClick={() => setActiveSection('features')}
            >
              <Features width="16" height="16" />
            </button> */}

              <SideNavTooltip label="RSD Comments">
                <button
                  className={`${isCommentPanelOpen ? styles.active : ''} ${
                    styles.commentButton
                  }`}
                  onClick={openCommentsPanelHandler}
                  aria-label="RSD Comments"
                >
                  <CommentIcon width={18} height={18} badgeCount={badgeCount} />
                </button>
              </SideNavTooltip>
              <div className={styles.sideNavBottomActions}>
                {canAccessAiAssistant && (
                  <div className={styles.sideNavIconContainer}>
                    <SideNavTooltip label="Ask Ai">
                      <button
                        type="button"
                        className={activeSection === 'ai' ? styles.active : ''}
                        onClick={handleAskAiSectionClick}
                        onMouseDown={e => {
                          e.stopPropagation();
                        }}
                        aria-label="Ask Ai"
                        aria-pressed={activeSection === 'ai'}
                        data-testid="slide-out-side-nav-ask-ai-button"
                      >
                        <AskAiIcon />
                      </button>
                    </SideNavTooltip>
                  </div>
                )}
                <InfoButton apiData={apiData} />
                <div className={styles.sideNavIconContainer}>
                  <DeleteButton
                    apiData={apiData}
                    onClose={handleCloseWithRefresh}
                  />
                </div>
              </div>
            </nav>
          </div>

          {/* DACI Contact Popup */}
          <DACIContactPopup
            isOpen={isDACIPopupOpen}
            onClose={handleCloseDACIPopup}
            onSave={contactData => {
              // Use the unified add contact handler
              const formattedContact = {
                daciRole:
                  contactData.daciTitle || contactData.daciRole || 'Driver',
                role: contactData.role || '',
                name: contactData.name || '',
                uid: contactData.uid || '',
                imageUrl:
                  contactData.imageUrl || 'https://via.placeholder.com/32'
              };

              // Call the unified handler that manages both local state and API
              handleAddDACIContact(formattedContact);
              handleCloseDACIPopup();
            }}
            onDelete={handleDeleteDACIContact}
            daciRoles={DACI_ROLES}
            driverRoles={DRIVER_ROLES}
            // contacts={[
            //   { id: 'contact1', name: 'John Doe' },
            //   { id: 'contact2', name: 'Jane Smith' }
            // ]}
            existingContacts={getAllExistingDACIContacts}
          />

          {/* Add Primary Contact(s) Popup - Using EditPrimaryContact with Add title */}
          <EditPrimaryContact
            isOpen={isPrimaryContactPopupOpen}
            contact={null} // No specific contact being edited in Add mode
            existingContacts={flattenPrimaryContacts(
              currentPhaseIdForContact || undefined
            )}
            onClose={handleClosePrimaryContactPopup}
            onSave={async (role, updatedContacts) => {
              try {
                // console.log(

                if (currentPhaseIdForContact) {
                  // Phased contact
                  const currentPhaseName = normalizedPhases.find(
                    p => p.id === currentPhaseIdForContact
                  )?.phaseName;
                  const manager = createPrimaryContactsManagerForPhase(
                    currentPhaseIdForContact,
                    currentPhaseName
                  );
                  // Use replaceRoleContacts which computes the diff
                  await manager.replaceRoleContacts(
                    role,
                    updatedContacts as PCContact[]
                  );

                  // Auto-expand the section after successfully saving
                  setExpandedInfoSection(prev => {
                    const newSet = new Set(prev);
                    newSet.add(currentPhaseIdForContact);
                    return newSet;
                  });
                } else {
                  // Non-phased contact
                  const manager = createPrimaryContactsManagerForNonPhased();
                  // Use replaceRoleContacts which computes the diff
                  await manager.replaceRoleContacts(
                    role,
                    updatedContacts as PCContact[]
                  );

                  // Auto-expand the section after successfully saving
                  const momentId = card?.issueId || 'default';
                  setExpandedInfoSection(prev => {
                    const newSet = new Set(prev);
                    newSet.add(momentId);
                    return newSet;
                  });
                }
                // Note: onClose is called automatically by EditPrimaryContact after save
              } catch (error) {}
            }}
            title="Add Primary Contact(s)" // Custom title for Add mode
          />

          {/* Edit Decision Popup - Now handled by Decisions component */}

          {/* Dropdowns are now rendered inline within their containers */}
          {/* Comments side panel */}
          <CommentPanel
            isOpen={isCommentPanelOpen}
            onClose={handleCommentPanelClose}
          />
          {/* Feedback slide-out for a Milestones-matrix Feedback icon (see
              openMilestoneFeedback above) - the exact same component/state/
              navigation shape Review & Approval's own dashboard tables use.
              Portals itself above this whole panel (see
              FeedbackSlideOut.module.scss's own z-index comment), so its
              JSX position here doesn't affect stacking. */}
          <FeedbackSlideOut
            navigation={feedbackNavigation}
            onClose={() => setFeedbackNavigation(null)}
            onFeedbackRead={() => handleSeamlessDataRefreshWithoutFeatures()}
          />
          {/* Dropdowns are now rendered inline within their containers */}

          {/* Unsaved Changes Warning Dialog (shared by Clone, Escape/Close, and Export PDF) */}
          <UnsavedChangesWarning
            isOpen={showUnsavedWarning}
            onConfirm={handleConfirmClose}
            onCancel={handleCancelClose}
          />

          {/* Strategic Alignment Error Dialog */}
          <MessageDialog
            isOpen={strategicAlignmentDialog.isOpen}
            title="Strategic Alignment Required"
            message={strategicAlignmentDialog.message}
            onClose={() =>
              setStrategicAlignmentDialog({ isOpen: false, message: '' })
            }
            dataTestId="strategic-alignment-dialog-overlay"
          />
        </RBACProvider>
      </Provider>
    </MomentProvider>
  );

  return createPortal(
    content,
    document.querySelector('[data-omega-feature="shell header"]')!
  );
  // return createPortal(content, document.body);
}
